import React, { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, Line, Group, Image as KonvaImage, Text, Path } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import { isLoggedIn, getUser, authHeader } from '../auth/auth';
import { LoginCard } from '../auth/LoginGate';

// Types
interface SeatBlock {
  id?: string; gate?: string; name?: string; zone?: string; level?: string;
  capacity?: number; category?: string; is_seat_specific?: boolean; section_rows?: any[];
}

interface Seat {
  id: string; x: number; y: number; radius?: number; color?: string;
  opacity?: number; zIndex?: number; name?: string; groupId?: string;
  seat_block?: SeatBlock;
}

interface Shape {
  id: string;
  type: 'rect' | 'line' | 'trapezoid' | 'custom' | 'circle' | 'group' | 'text';
  points: number[];
  color?: string; seat_block?: SeatBlock; opacity?: number; zIndex?: number;
  name?: string; description?: string; strokeColor?: string; strokeWidth?: number;
  holeX?: number; holeY?: number; holeRadius?: number; holePoints?: number[];
  cx?: number; cy?: number; r?: number; rotation?: number; groupId?: string;
  childIds?: string[]; text?: string; fontSize?: number;
  svgPath?: string; svgPathStroke?: boolean; svgViewBox?: number;
}

interface ImageElement {
  id: string; x: number; y: number; width: number; height: number;
  url: string; zIndex?: number; rotation?: number;
}

// Shared resizable wrapper for image/rect/text elements
interface ResizableGroupProps {
  x: number; y: number; width: number; height: number; rotation?: number;
  selected: boolean; isDraggable: boolean;
  onSelect: () => void; onDragStart: () => void;
  onContextMenu: (e: any) => void; onTouchStart: (e: any) => void;
  onTouchEnd?: (e?: any) => void;
  onDragEnd: (x: number, y: number) => void; onResizeStart: () => void;
  onResizeCorner: (corner: number, newX: number, newY: number, proportional?: boolean) => void;
  onResizeEdge: (edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => void;
  centerPoint: { x: number; y: number }; zoomScale: number;
  proportionalCorners?: boolean;
  onMouseDown?: (e: any) => void; onMouseUp?: () => void; onDblClick?: () => void;
  children: (onMouseMove: ((e: any) => void) | undefined) => React.ReactNode;
}

const ResizableGroup: React.FC<ResizableGroupProps> = ({
  x, y, width, height, rotation, selected, isDraggable,
  onSelect, onDragStart, onContextMenu, onTouchStart, onTouchEnd,
  onDragEnd, onResizeStart, onResizeCorner, onResizeEdge,
  centerPoint, zoomScale, proportionalCorners, onMouseDown, onMouseUp, onDblClick,
  children,
}) => {
  const [resizeEdge, setResizeEdge] = React.useState<'left' | 'right' | 'top' | 'bottom' | null>(null);
  const lastPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const offsetX = width / 2, offsetY = height / 2;
  const corners = [{x:0,y:0},{x:width,y:0},{x:width,y:height},{x:0,y:height}];

  const detectEdge = (lx: number, ly: number, t = 12): 'left'|'right'|'top'|'bottom'|null => {
    if (ly >= -t && ly <= height + t) {
      if (Math.abs(lx) <= t) return 'left';
      if (Math.abs(lx - width) <= t) return 'right';
    }
    if (lx >= -t && lx <= width + t) {
      if (Math.abs(ly) <= t) return 'top';
      if (Math.abs(ly - height) <= t) return 'bottom';
    }
    return null;
  };

  const onMM = selected ? (e: any) => {
    const stage = e.target.getStage(); if (!stage) return;
    const group = e.target.findAncestor('Group'); if (!group) return;
    const lp = group.getRelativePointerPosition(); if (!lp) return;
    const edge = detectEdge(lp.x, lp.y);
    if (edge) {
      stage.container().style.cursor = (edge === 'left' || edge === 'right') ? 'ew-resize' : 'ns-resize';
      setResizeEdge(edge);
    } else { stage.container().style.cursor = 'default'; setResizeEdge(null); }
  } : undefined;

  const onGDM = selected ? (e: any) => {
    if (!resizeEdge) return;
    e.cancelBubble = true;
    e.target.position({ x: x + centerPoint.x + offsetX, y: y + centerPoint.y + offsetY });
    const pointer = e.target.getStage()?.getPointerPosition();
    if (!pointer || !lastPointerRef.current) return;
    const dx = (pointer.x - lastPointerRef.current.x) / zoomScale;
    const dy = (pointer.y - lastPointerRef.current.y) / zoomScale;
    onResizeEdge(resizeEdge, (resizeEdge === 'left' || resizeEdge === 'right') ? dx : dy);
    lastPointerRef.current = { x: pointer.x, y: pointer.y };
  } : undefined;

  return (
    <Group
      x={x + centerPoint.x + offsetX} y={y + centerPoint.y + offsetY}
      offsetX={offsetX} offsetY={offsetY} rotation={rotation ?? 0}
      draggable={isDraggable}
      onDragStart={(e: any) => {
        const p = e.target.getStage()?.getPointerPosition();
        if (p) lastPointerRef.current = { x: p.x, y: p.y };
        if (!resizeEdge) onDragStart();
      }}
      onDragEnd={isDraggable ? (e: any) => {
        lastPointerRef.current = null;
        if (!resizeEdge) onDragEnd(e.target.x() - offsetX, e.target.y() - offsetY);
        setResizeEdge(null);
      } : undefined}
      onClick={onSelect} onMouseDown={onMouseDown} onMouseUp={onMouseUp}
      onMouseMove={onMM} onDragMove={onGDM}
      onMouseLeave={(e: any) => {
        e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'default');
        setResizeEdge(null); onMouseUp?.();
      }}
      onContextMenu={onContextMenu} onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd} onTouchMove={onTouchEnd} onDblClick={onDblClick}
    >
      {children(onMM)}
      {selected && corners.map((c, i) => (
        <Circle key={`corner-${i}`} x={c.x} y={c.y} radius={6}
          fill="#00ff00" stroke="#00aa00" strokeWidth={2} draggable
          onDragStart={(e: any) => { e.cancelBubble = true; onResizeStart(); }}
          onDragMove={(e: any) => { e.cancelBubble = true; onResizeCorner(i, e.target.x(), e.target.y(), proportionalCorners); }}
          onDragEnd={(e: any) => { e.cancelBubble = true; onResizeCorner(i, e.target.x(), e.target.y(), proportionalCorners); }}
        />
      ))}
    </Group>
  );
};

// ImageKonvaRenderer - uses shared ResizableGroup
interface ImageKonvaRendererProps {
  img: ImageElement; selected: boolean; isDraggable: boolean;
  onSelect: () => void; onDragStart: () => void;
  onContextMenu: (e: any) => void; onMouseDown?: (e: any) => void; onMouseUp?: () => void;
  onTouchStart: (e: any) => void; onTouchEnd?: (e?: any) => void;
  onDragEnd: (x: number, y: number) => void; onResizeStart: () => void;
  onResizeCorner: (corner: number, newX: number, newY: number, proportional: boolean) => void;
  onResizeEdge: (edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => void;
  onRotate: (rotation: number) => void;
  centerPoint: { x: number; y: number }; zoomScale: number;
}
const ImageKonvaRenderer: React.FC<ImageKonvaRendererProps> = ({
  img, selected, isDraggable, onSelect, onDragStart, onContextMenu, onMouseDown, onMouseUp,
  onTouchStart, onTouchEnd, onDragEnd, onResizeStart, onResizeCorner, onResizeEdge, centerPoint, zoomScale,
}) => {
  const [imgEl] = useImage(img.url);
  return (
    <ResizableGroup x={img.x} y={img.y} width={img.width} height={img.height} rotation={img.rotation}
      selected={selected} isDraggable={isDraggable} proportionalCorners
      onSelect={onSelect} onDragStart={onDragStart} onContextMenu={onContextMenu}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onDragEnd={onDragEnd}
      onResizeStart={onResizeStart} onResizeCorner={(c, nx, ny, p) => onResizeCorner(c, nx, ny, p ?? true)}
      onResizeEdge={onResizeEdge} onMouseDown={onMouseDown} onMouseUp={onMouseUp}
      centerPoint={centerPoint} zoomScale={zoomScale}>
      {(onMM) => (<>
        {imgEl ? <KonvaImage image={imgEl} width={img.width} height={img.height} x={0} y={0} onMouseMove={onMM} />
               : <Rect x={0} y={0} width={img.width} height={img.height} fill="#cccccc" stroke="#999" strokeWidth={1} onMouseMove={onMM} />}
        {selected && <Rect x={0} y={0} width={img.width} height={img.height} stroke="#00ff00" strokeWidth={2} fill="transparent" onMouseMove={onMM} />}
      </>)}
    </ResizableGroup>
  );
};

// RectKonvaRenderer - uses shared ResizableGroup
interface RectKonvaRendererProps {
  rect: { id: string; x: number; y: number; width: number; height: number; color?: string; strokeColor?: string; strokeWidth?: number; opacity?: number; rotation?: number };
  selected: boolean; inGroup: boolean; isDraggable: boolean;
  onSelect: () => void; onDragStart: () => void;
  onContextMenu: (e: any) => void; onTouchStart: (e: any) => void; onTouchEnd?: (e?: any) => void;
  onDragEnd: (x: number, y: number) => void; onResizeStart: () => void;
  onResizeCorner: (corner: number, newX: number, newY: number) => void;
  onResizeEdge: (edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => void;
  centerPoint: { x: number; y: number }; zoomScale: number;
}
const RectKonvaRenderer: React.FC<RectKonvaRendererProps> = ({
  rect, selected, inGroup, isDraggable, onSelect, onDragStart, onContextMenu,
  onTouchStart, onTouchEnd, onDragEnd, onResizeStart, onResizeCorner, onResizeEdge, centerPoint, zoomScale,
}) => {
  const strokeColor = selected ? '#00ff00' : inGroup ? '#4488ff' : (rect.strokeColor || 'transparent');
  return (
    <ResizableGroup x={rect.x} y={rect.y} width={rect.width} height={rect.height} rotation={rect.rotation}
      selected={selected} isDraggable={isDraggable}
      onSelect={onSelect} onDragStart={onDragStart} onContextMenu={onContextMenu}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onDragEnd={onDragEnd}
      onResizeStart={onResizeStart} onResizeCorner={(c, nx, ny) => onResizeCorner(c, nx, ny)}
      onResizeEdge={onResizeEdge} centerPoint={centerPoint} zoomScale={zoomScale}>
      {(onMM) => (<>
        <Rect x={0} y={0} width={rect.width} height={rect.height} fill={opaqueColor(rect.color, rect.opacity ?? 1)}
          stroke={strokeColor} strokeWidth={rect.strokeWidth || 2} opacity={rect.opacity ?? 1} onMouseMove={onMM} />
        {selected && <Rect x={0} y={0} width={rect.width} height={rect.height} stroke="#00ff00" strokeWidth={2} fill="transparent" onMouseMove={onMM} />}
      </>)}
    </ResizableGroup>
  );
};

// Helper: when shape opacity is 1, strip any alpha from an rgba/hsla color so the fill is fully opaque.
// This ensures that shapes sent to the back are properly occluded by top shapes with opacity 1.
const opaqueColor = (color: string | undefined, opacity: number): string | undefined => {
  if (!color || opacity < 1) return color;
  // rgba(r, g, b, a) → rgb(r, g, b)
  const rgbaMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+)?\s*\)$/i);
  if (rgbaMatch) return `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`;
  // hsla(h, s%, l%, a) → hsl(h, s%, l%)
  const hslaMatch = color.match(/^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+%?)\s*,\s*([\d.]+%?)\s*(?:,\s*[\d.]+)?\s*\)$/i);
  if (hslaMatch) return `hsl(${hslaMatch[1]}, ${hslaMatch[2]}, ${hslaMatch[3]})`;
  return color;
};

// Helper: strip HTML tags to get plain text for Konva canvas rendering
const htmlToPlain = (html: string): string => {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.innerText || el.textContent || '';
};

// Parse HTML to detect formatting (bold, italic, cursive) for Konva Text rendering
const parseHtmlFormat = (html: string): { fontStyle: string; fontFamily: string } => {
  const hasBold = /<b[\s>]|<strong[\s>]|font-weight:\s*bold/i.test(html);
  const hasItalic = /<i[\s>]|<em[\s>]|font-style:\s*italic/i.test(html);
  const hasCursive = /font-family:\s*cursive/i.test(html);
  const parts: string[] = [];
  if (hasBold) parts.push('bold');
  if (hasItalic) parts.push('italic');
  return {
    fontStyle: parts.length > 0 ? parts.join(' ') : 'normal',
    fontFamily: hasCursive ? 'cursive' : 'Arial, sans-serif',
  };
};

// TextKonvaRenderer - single click selects, double click opens inline edit (handled by parent via onRequestEdit)
interface TextKonvaRendererProps {
  textShape: { id: string; x: number; y: number; width: number; height: number; text?: string; fontSize?: number; color?: string; opacity?: number; rotation?: number };
  selected: boolean; inGroup?: boolean; isEditing: boolean; isDraggable: boolean;
  onSelect: () => void; onDragStart: () => void;
  onContextMenu: (e: any) => void; onTouchStart: (e: any) => void; onTouchEnd?: (e?: any) => void;
  onDragEnd: (x: number, y: number) => void; onResizeStart: () => void;
  onResizeCorner: (corner: number, newX: number, newY: number) => void;
  onResizeEdge: (edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => void;
  centerPoint: { x: number; y: number }; zoomScale: number;
  onRequestEdit?: (id: string) => void;
}
const TextKonvaRenderer: React.FC<TextKonvaRendererProps> = ({
  textShape, selected, inGroup = false, isEditing, isDraggable, onSelect, onDragStart, onContextMenu,
  onTouchStart, onTouchEnd, onDragEnd, onResizeStart, onResizeCorner, onResizeEdge,
  centerPoint, zoomScale, onRequestEdit,
}) => {
  const lastTapRef = React.useRef<number|null>(null);
  const handleTouchEnd = (e: any) => {
    const now = Date.now();
    if (lastTapRef.current && (now - lastTapRef.current) < 350) {
      onRequestEdit && onRequestEdit(textShape.id); lastTapRef.current = null;
    } else { lastTapRef.current = now; setTimeout(() => { lastTapRef.current = null; }, 400); }
  };
  const plainText = htmlToPlain(textShape.text || 'new text');
  const { fontStyle, fontFamily } = parseHtmlFormat(textShape.text || '');
  // When editing, hide the Konva text (HTML overlay is shown instead)
  if (isEditing) return null;
  return (
    <ResizableGroup x={textShape.x} y={textShape.y} width={textShape.width} height={textShape.height} rotation={textShape.rotation}
      selected={selected} isDraggable={isDraggable}
      onSelect={onSelect} onDragStart={onDragStart} onContextMenu={onContextMenu}
      onTouchStart={onTouchStart} onTouchEnd={(e: any) => { onTouchEnd && onTouchEnd(e); handleTouchEnd(e); }}
      onDragEnd={onDragEnd} onResizeStart={onResizeStart}
      onResizeCorner={(c, nx, ny) => onResizeCorner(c, nx, ny)}
      onResizeEdge={onResizeEdge}
      onDblClick={() => onRequestEdit && onRequestEdit(textShape.id)}
      centerPoint={centerPoint} zoomScale={zoomScale}>
      {(onMM) => (<>
        <Rect x={0} y={0} width={textShape.width} height={textShape.height}
          fill="transparent" stroke={selected ? '#00ff00' : inGroup ? '#4488ff' : 'transparent'}
          strokeWidth={(selected || inGroup) ? 2 : 0} onMouseMove={onMM} />
        <Text x={4} y={4} width={textShape.width-8} height={textShape.height-8}
          text={plainText} fontSize={textShape.fontSize || 16}
          fontStyle={fontStyle} fontFamily={fontFamily}
          fill={textShape.color || '#ffffff'} opacity={textShape.opacity ?? 1} wrap="word" onMouseMove={onMM} />
      </>)}
    </ResizableGroup>
  );
};
interface Point { x: number; y: number; }
interface ContextMenuState { x: number; y: number; targetId: string; targetType: 'seat' | 'shape' | 'image'; }

interface SeatMapSection {
  id: string;
  gate: string;
  name: string;
  zone: string;
  level: string;
  capacity: number | '';
  category: string;
  svg_path?: string;
  is_seat_specific: boolean;
  section_rows: {
    id: string;
    name?: string;
    row_seats: { seat_id: string; num: string }[];
  }[];
}

interface SeatMapData {
  id: number | null;
  venue_name: string;
  capacity: number | '';
  configuration: string;
  svg_path: string;
  image_preview_path: string;
  anchor_point: { x: number; y: number; description?: string };
  sections: SeatMapSection[];
  location: { street: string; housenumber: string; postal_code: string; city: string; state: string; country: string }[];
}

const rowInputStyle: React.CSSProperties = { width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' };
const RowFormFields: React.FC<{row: {id:string;name?:string;seatsCount:number}; onChange: (r: {id:string;name?:string;seatsCount:number}) => void}> = ({row, onChange}) => (
  <div style={{ padding: '8px', background: '#fff' }}>
    <label style={{ display: 'block', marginBottom: '6px' }}>
      <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Row ID</div>
      <input type="text" value={row.id} onChange={(e) => onChange({ ...row, id: e.target.value })} style={rowInputStyle} />
    </label>
    <label style={{ display: 'block', marginBottom: '6px' }}>
      <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Name</div>
      <input type="text" value={row.name || ''} onChange={(e) => onChange({ ...row, name: e.target.value })} placeholder="N/A" style={rowInputStyle} />
    </label>
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Seats count</div>
      <input type="number" min={0} placeholder="0" value={row.seatsCount === 0 ? '' : row.seatsCount}
        onChange={(e) => { const d = (e.target.value||'').replace(/\D/g, ''); onChange({ ...row, seatsCount: d === '' ? 0 : Math.max(0, parseInt(d, 10)) }); }}
        style={rowInputStyle} />
    </label>
  </div>
);

// ── Prebuilt icon definitions (viewBox-based SVG paths, scalable) ──────────
const PREBUILT_ICONS: { id: string; label: string; emoji: string; viewBox: number; svgPath: string; strokeMode: boolean; defaultColor: string }[] = [
    {
        id: 'wheelchair',
        label: 'Wheelchair',
        emoji: '♿',
        viewBox: 100,
        // ISA accessibility symbol: head + reclined torso + seat/thigh + shin to
        // footplate, sitting inside a large wheel ring (ring = outer arc CW + inner
        // arc CCW so the nonzero fill leaves the wheel hollow).
        svgPath: 'M45 16 a7 7 0 1 0 -14 0 a7 7 0 1 0 14 0 z M73 62 a27 27 0 1 1 -54 0 a27 27 0 1 1 54 0 z M68 62 a22 22 0 1 0 -44 0 a22 22 0 1 0 44 0 z M34 25 L42 25 L39 50 L31 50 Z M31 45 H58 V52 H31 Z M52 47 L59 45 L64 65 L57 67 Z M55 66 H67 V70 H55 Z',
        strokeMode: false,
        defaultColor: '#2196F3',
    },
    {
        id: 'stage',
        label: 'Stage',
        emoji: '🎭',
        viewBox: 100,
        svgPath: 'M15 60L30 20h40l15 40z M8 64h84v10H8z M2 78h96v10H2z',
        strokeMode: false,
        defaultColor: '#9C27B0',
    },
    {
        id: 'exit',
        label: 'Exit',
        emoji: '🚪',
        viewBox: 100,
        // ISO-style emergency exit: running figure + arrow heading into an open
        // doorway (door frame = outer rect CW + inner rect CCW for the opening).
        svgPath: 'M62 8 H92 V92 H62 Z M66 12 V88 H88 V12 Z M40 46 H56 V40 L70 50 L56 60 V54 H40 Z M26 14 a6 6 0 1 0 -12 0 a6 6 0 1 0 12 0 z M19 20 L26 21 L20 46 L13 44 Z M16 42 L22 40 L32 50 L26 54 Z M28 50 H34 V66 H27 Z M14 43 L20 46 L12 66 L6 63 Z M22 22 L27 21 L35 29 L31 33 Z M20 24 L24 27 L15 34 L11 31 Z',
        strokeMode: false,
        defaultColor: '#2E7D32',
    },
    {
        id: 'football-field',
        label: 'Football Field',
        emoji: '⚽',
        viewBox: 100,
        // Soccer pitch: boundary, halfway line, centre circle/spot, both penalty
        // areas + goal areas + penalty arcs, and four corner arcs.
        svgPath: 'M4 4 H96 V96 H4 Z M4 50 H96 M38 50 a12 12 0 1 0 24 0 a12 12 0 1 0 -24 0 M30 4 V20 H70 V4 M42 4 V11 H58 V4 M43 20 a7 7 0 0 0 14 0 M30 96 V76 H70 V96 M42 96 V89 H58 V96 M43 76 a7 7 0 0 1 14 0 M4 7 a3 3 0 0 0 3 -3 M93 4 a3 3 0 0 0 3 3 M7 96 a3 3 0 0 0 -3 -3 M96 93 a3 3 0 0 0 -3 3 M49.2 50 a0.8 0.8 0 1 0 1.6 0 a0.8 0.8 0 1 0 -1.6 0',
        strokeMode: true,
        defaultColor: '#2E7D32',
    },
    {
        id: 'basketball-court',
        label: 'Basketball Court',
        emoji: '🏀',
        viewBox: 100,
        // Full court: centre line + circle, both keys with free-throw circles,
        // three-point arcs, and backboard/hoop ticks at each baseline.
        svgPath: 'M4 4 H96 V96 H4 Z M50 4 V96 M41 50 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0 M4 36 H30 V64 H4 M21 50 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0 M4 14 A 42 36 0 0 1 4 86 M7 44 V56 M7 50 a2.5 2.5 0 1 0 5 0 a2.5 2.5 0 1 0 -5 0 M96 36 H70 V64 H96 M61 50 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0 M96 14 A 42 36 0 0 0 96 86 M93 44 V56 M83 50 a2.5 2.5 0 1 0 5 0 a2.5 2.5 0 1 0 -5 0',
        strokeMode: true,
        defaultColor: '#C8843C',
    },
    {
        id: 'tennis-court',
        label: 'Tennis Court',
        emoji: '🎾',
        viewBox: 100,
        // Doubles boundary + singles sidelines, centre net, service lines, centre
        // service line and the two baseline centre marks.
        svgPath: 'M4 4 H96 V96 H4 Z M50 4 V96 M4 16 H96 M4 84 H96 M26 16 V84 M74 16 V84 M26 50 H74 M4 50 H8 M92 50 H96',
        strokeMode: true,
        defaultColor: '#2E6CA8',
    },
    {
        id: 'american-football-field',
        label: 'American Football',
        emoji: '🏈',
        viewBox: 100,
        // Gridiron: end-zone goal lines, yard lines every ~10 yds, and two rows of
        // hash marks down the middle of the field.
        svgPath: 'M4 4 H96 V96 H4 Z M18 4 V96 M82 4 V96 M26 4 V96 M34 4 V96 M42 4 V96 M50 4 V96 M58 4 V96 M66 4 V96 M74 4 V96 M18 38 V42 M26 38 V42 M34 38 V42 M42 38 V42 M50 38 V42 M58 38 V42 M66 38 V42 M74 38 V42 M82 38 V42 M18 58 V62 M26 58 V62 M34 58 V62 M42 58 V62 M50 58 V62 M58 58 V62 M66 58 V62 M74 58 V62 M82 58 V62',
        strokeMode: true,
        defaultColor: '#357A38',
    },
];

const SeatMapModel: React.FC = () => {

    const appUrl = 'https://maineventbackend.seuytel.com/api/';
    const storageUrl = 'https://maineventbackend.seuytel.com/storage/';

    // ── Auth gate ─────────────────────────────────────────────────────────────
    // Creating, updating and importing seat maps require a logged-in user. The
    // strategy is "model freely, then log in to save": the editor stays open and
    // we only prompt when the user actually tries to persist or import. After a
    // successful login the originally-requested action is resumed automatically.
    const [authGateOpen, setAuthGateOpen] = useState(false);
    const pendingAuthActionRef = useRef<null | (() => void)>(null);
    const requireAuth = (action: () => void) => {
        if (isLoggedIn()) {
            action();
            return;
        }
        pendingAuthActionRef.current = action;
        setAuthGateOpen(true);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const stageRef = useRef<Konva.Stage>(null);
    const [mode, setMode] = useState<'draw'>('draw'); // Only draw mode now
    const [activeTool, setActiveTool] = useState<'seat' | 'rect' | 'line' | 'trapezoid' | 'custom' | 'circle' | 'text' | null>(null);
    const [showPopup, setShowPopup] = useState(false);
    const [seats, setSeats] = useState<Seat[]>([]);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [tempPoints, setTempPoints] = useState<number[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<'seat' | 'shape' | 'image' | null>(null);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [editPropsOpen, setEditPropsOpen] = useState(false);
    const editPropsTargetRef = useRef<{ id: string; type: 'seat' | 'shape' | 'image' }>({ id: '', type: 'shape' });
    const [editingProps, setEditingProps] = useState<{ name: string; description: string; is_seat_block?: boolean; block?: { id?: string; gate?: string; name?: string; zone?: string; level?: string; capacity?: number | ''; category?: string; is_seat_specific?: boolean; section_rows?: any[] } }>({ name: '', description: '', is_seat_block: false, block: { id: '', gate: '', name: '', zone: '', level: '', capacity: '', category: '', is_seat_specific: false, section_rows: [] } });
    const [selectedColor, setSelectedColor] = useState('#ff0000');
    const [selectedOpacity, setSelectedOpacity] = useState(1);  // 0–1
    const stageContainerRef = useRef<HTMLDivElement>(null);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const [centerPoint, setCenterPoint] = useState({ x: 400, y: 300 }); // Fixed origin: always canvas center, never moved by user

    // ── Centralised seat-map state ────────────────────────────────────────────
    const [seatMap, setSeatMap] = useState<SeatMapData>({
        id: null,
        venue_name: '',
        capacity: '',
        configuration: '',
        svg_path: '',
        image_preview_path: '',
        anchor_point: { x: 0, y: 0, description: '' },
        sections: [],
        location: [{ street: '', housenumber: '', postal_code: '', city: '', state: '', country: '' }],
    });

    // Convenience aliases so the rest of the file stays readable
    const anchorPoint = seatMap.anchor_point;
    const setAnchorPoint = (pt: { x: number; y: number; description?: string }) =>
        setSeatMap(prev => ({ ...prev, anchor_point: { ...prev.anchor_point, ...pt } }));
    const [anchorPopupOpen, setAnchorPopupOpen] = useState(false);
    const [anchorForm, setAnchorForm] = useState<{ description: string; x: string; y: string }>({ description: '', x: '0', y: '0' });
    const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null);
    const [zoomScale, setZoomScale] = useState(1);
    const [images, setImages] = useState<ImageElement[]>([]);
    // Multi-select group
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [caretPositions] = useState<Record<string, number>>({});
    const [editingTextId, setEditingTextId] = useState<string | null>(null);
    const editingOverlayRef = useRef<HTMLDivElement | null>(null);
    const [addRowOpen, setAddRowOpen] = useState(false);
    const [addRowForShapeId, setAddRowForShapeId] = useState<string | null>(null);
    const [existingRows, setExistingRows] = useState<{ id: string; name?: string; seatsCount: number }[]>([]);
    const [currentRow, setCurrentRow] = useState<{ id: string; name?: string; seatsCount: number }>({ id: '', name: '', seatsCount: 0 });
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    // Venue details (stored in seatMap)
    const venueName = seatMap.venue_name;
    const venueConfiguration = seatMap.configuration;
    const venueCapacity = seatMap.capacity;
    const [venueDetailsOpen, setVenueDetailsOpen] = useState(false);
    const [venueForm, setVenueForm] = useState<{ name: string; configuration: string; capacity: number | ''; country: string; city: string; street: string; housenumber: string; postal_code: string; state: string }>({ name: '', configuration: '', capacity: '', country: '', city: '', street: '', housenumber: '', postal_code: '', state: '' });
    const hasVenueDetails = !!(venueName || venueConfiguration || (typeof venueCapacity === 'number' && venueCapacity > 0));
    const openVenueDetails = () => {
        const loc = seatMap.location?.[0] || { street: '', housenumber: '', postal_code: '', city: '', state: '', country: '' };
        setVenueForm({ name: venueName, configuration: venueConfiguration, capacity: venueCapacity, country: loc.country || '', city: loc.city || '', street: loc.street || '', housenumber: loc.housenumber || '', postal_code: loc.postal_code || '', state: loc.state || '' });
        setVenueDetailsOpen(true);
    };
    const applyVenueDetails = () => {
        const updatedLocation = { ...(seatMap.location?.[0] || { street: '', housenumber: '', postal_code: '', city: '', state: '', country: '' }), country: venueForm.country, city: venueForm.city, street: venueForm.street, housenumber: venueForm.housenumber, postal_code: venueForm.postal_code, state: venueForm.state };
        setSeatMap(prev => ({
            ...prev,
            venue_name: venueForm.name,
            configuration: venueForm.configuration,
            capacity: venueForm.capacity,
            location: [updatedLocation],
        }));
        setVenueDetailsOpen(false);
    };

    // ── Import popup state ───────────────────────────────────────────────────
    const [importPopupOpen, setImportPopupOpen] = useState(false);
    const [importSearchQuery, setImportSearchQuery] = useState('');
    const [importSuggestions, setImportSuggestions] = useState<{id: number; venue_name: string; svg_path: string; capacity: number; location: any; configuration: string}[]>([]);
    const [importDropdownOpen, setImportDropdownOpen] = useState(false);
    const [importedFromId, setImportedFromId] = useState<number | null>(null);
    const [importLoading, setImportLoading] = useState(false);
    const importSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Save preview popup state ─────────────────────────────────────────────
    const [savePreviewOpen, setSavePreviewOpen] = useState(false);
    const [savePreviewSvg, setSavePreviewSvg] = useState('');
    const [saveValidationError, setSaveValidationError] = useState<string | null>(null);
    const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});
    const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
    const [previewZoom, setPreviewZoom] = useState(1);
    const previewDragRef = useRef<{ dragging: boolean; lastX: number; lastY: number }>({ dragging: false, lastX: 0, lastY: 0 });
    const previewContainerRef = useRef<HTMLDivElement | null>(null);

    // ── Venue interior preview (designer-chosen media) ───────────────────────
    const [venuePreviewFile, setVenuePreviewFile] = useState<File | null>(null);
    const [venuePreviewUrl, setVenuePreviewUrl] = useState<string>('');
    const [venuePreviewModalOpen, setVenuePreviewModalOpen] = useState(false);
    const venuePreviewInputRef = useRef<HTMLInputElement>(null);

    const groupDragInitRef = useRef<{ x: number; y: number } | null>(null);
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mouseLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDraggingRef = useRef(false); // Track if currently dragging to prevent click-deselect
    const shapeClickedRef = useRef(false); // Track if a shape was just clicked to prevent stage deselect
    // Border resize mode: tracks which edge is being dragged for resize
    const borderResizeRef = useRef<{ edge: 'left' | 'right' | 'top' | 'bottom' | null; shapeId: string | null }>({ edge: null, shapeId: null });
    // Row resize: snapshot of initial state at drag start (avoids compound sliding)
    const rowResizeStartRef = useRef<{ posX: number; posY: number; width: number; height: number; ptrX: number; ptrY: number } | null>(null);
    // Track whether the bend handle is being dragged (hides outline during drag)
    const isBendDraggingRef = useRef(false);
    // Snapshot bend-handle initial state to avoid circular reference during drag
    const bendDragStartRef = useRef<{ bend: number; ptrY: number } | null>(null);
    
    // Helper: detect which edge of a rect the mouse is near (within threshold)
    const detectEdge = (mouseX: number, mouseY: number, rectX: number, rectY: number, rectW: number, rectH: number, threshold: number = 10): 'left' | 'right' | 'top' | 'bottom' | null => {
        const left = rectX;
        const right = rectX + rectW;
        const top = rectY;
        const bottom = rectY + rectH;
        
        // Must be within the rect's vertical range for left/right edges
        const inVerticalRange = mouseY >= top - threshold && mouseY <= bottom + threshold;
        // Must be within the rect's horizontal range for top/bottom edges
        const inHorizontalRange = mouseX >= left - threshold && mouseX <= right + threshold;
        
        // Check edges (prefer left/right for horizontal resize)
        if (inVerticalRange) {
            if (Math.abs(mouseX - left) <= threshold) return 'left';
            if (Math.abs(mouseX - right) <= threshold) return 'right';
        }
        if (inHorizontalRange) {
            if (Math.abs(mouseY - top) <= threshold) return 'top';
            if (Math.abs(mouseY - bottom) <= threshold) return 'bottom';
        }
        return null;
    };

    const calcMenuPos = (cx: number, cy: number, menuW = 200, menuH = 450) => {
        let x = cx, y = cy;
        const vw = window.innerWidth, vh = window.innerHeight;
        if (x + menuW > vw) x = vw - menuW - 10;
        if (y + menuH > vh) { y = y > vh - y ? Math.max(10, y - menuH) : Math.max(10, vh - menuH - 10); }
        return { x, y };
    };

    // Undo history - start with initial empty state
    const [history, setHistory] = useState<{ shapes: Shape[]; seats: Seat[]; images: ImageElement[] }[]>([{ shapes: [], seats: [], images: [] }]);
    const [historyIndex, setHistoryIndex] = useState(0);
    // Edit panel mode: 'rotate' or 'scale'
    const [editPanelMode, setEditPanelMode] = useState<'rotate' | 'scale' | 'seatsize'>('rotate');
    // Input value for angle/scale/seat-size
    const [editInputValue, setEditInputValue] = useState('0');
    // Marquee selection state
    const [isMarqueeSelecting, setIsMarqueeSelecting] = useState(false);
    const isMarqueeRef = useRef(false); // synchronous flag — needed because Konva click fires on mouseup target
    const [marqueeStart, setMarqueeStart] = useState<{ x: number; y: number } | null>(null);
    const [marqueeRect, setMarqueeRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
    // Selection menu (shown after marquee selection with multiple shapes)
    const [selectionMenu, setSelectionMenu] = useState<{ x: number; y: number } | null>(null);
    // Responsive toolbar
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [showToolbarOverflow, setShowToolbarOverflow] = useState(false);
    // Stage panning state
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
    // Prebuilt shapes menu
    const [prebuiltMenuOpen, setPrebuiltMenuOpen] = useState(false);
    const [prebuiltMenuPos, setPrebuiltMenuPos] = useState({ top: 0, left: 0 });

    // Save state to history
    const saveToHistory = () => {
        const newState = { shapes: JSON.parse(JSON.stringify(shapes)), seats: JSON.parse(JSON.stringify(seats)), images: JSON.parse(JSON.stringify(images)) };
        setHistory(prev => [...prev.slice(0, historyIndex + 1), newState]);
        setHistoryIndex(prev => prev + 1);
    };

    // ── Build sections array from shapes that have seat_block metadata ────────
    const buildSectionsFromShapes = (currentShapes: Shape[]): SeatMapSection[] => {
        return currentShapes
            .filter(s => s.seat_block)
            .map(s => ({
                id: s.seat_block!.id || s.id,
                gate: s.seat_block!.gate || '',
                name: s.seat_block!.name || '',
                zone: s.seat_block!.zone || '',
                level: s.seat_block!.level || '',
                capacity: typeof s.seat_block!.capacity === 'number' ? s.seat_block!.capacity : '',
                category: s.seat_block!.category || '',
                is_seat_specific: !!s.seat_block!.is_seat_specific,
                section_rows: (s.seat_block!.section_rows || []).map((r: any) => {
                    const rid = r.id || '';
                    const seats = (r.row_seats || []).map((_: any, idx: number) => ({
                        seat_id: `${rid}-s-${idx + 1}`,
                        num: String(idx + 1),
                    }));
                    return { id: rid, name: r.name, row_seats: seats };
                }),
            }));
    };

    // Browser-tab title while the designer is open
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'Main Events SeatMap Designer';
        return () => { document.title = prevTitle; };
    }, []);

    // Keep seatMap.sections in sync whenever shapes change
    useEffect(() => {
        const sections = buildSectionsFromShapes(shapes);
        setSeatMap(prev => {
            // Only update if sections actually changed (avoid infinite loop)
            if (JSON.stringify(prev.sections) === JSON.stringify(sections)) return prev;
            return { ...prev, sections };
        });
    }, [shapes]);

    // ── Generate an interactive SVG string from the current seat map ──────────
    // Shapes with seat_block get class="seat-block" and data attributes.
    // Individual seats (row dots & standalone) get class="seat" and data attrs.
    const generateSeatMapSVG = (): string => {
        const escXml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

        // Compute bounding box of all content to set the viewBox
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const expandBounds = (x: number, y: number) => { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; };
        shapes.forEach(s => {
            if (s.type === 'rect' || s.type === 'text' || s.type === 'group') {
                const [x, y, w, h] = s.points;
                expandBounds(x, y); expandBounds(x + w, y + h);
            }
            else if (s.type === 'circle') { const cx = s.cx || 0, cy = s.cy || 0, r = s.r || 50; expandBounds(cx - r, cy - r); expandBounds(cx + r, cy + r); }
            else { for (let i = 0; i < s.points.length; i += 2) expandBounds(s.points[i], s.points[i + 1]); }
        });
        seats.forEach(s => { const r = s.radius || 12; expandBounds(s.x - r, s.y - r); expandBounds(s.x + r, s.y + r); });
        images.forEach(img => { expandBounds(img.x, img.y); expandBounds(img.x + img.width, img.y + img.height); });

        // Also expand bounds for seat rows (they may extend beyond their parent shape)
        shapes.forEach(shape => {
            if (!shape.seat_block) return;
            const rows = shape.seat_block.section_rows || [];
            const bbox = getShapeBBox(shape);
            const LABEL_W = 24;
            const defaultNatRowH_bb = SEAT_DOT_RADIUS * 2 + ROW_PADDING * 2;
            rows.forEach((row: any, rowIdx: number) => {
                const rowSeats = row.row_seats || [];
                if (rowSeats.length === 0) return;
                const rowSeatR = row.seatRadius ?? SEAT_DOT_RADIUS;
                const seatDiameter = rowSeatR * 2;
                const natRowH = seatDiameter + ROW_PADDING * 2;
                const bend = row.bend ?? 0;
                const bendAbs = Math.abs(bend);
                const effectiveNatH = natRowH + (bendAbs > 0.5 ? bendAbs : 0);
                const natRowW = LABEL_W + rowSeats.length * seatDiameter + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                const groupW = row.width ?? natRowW;
                const groupH = row.height ?? effectiveNatH;
                const baseGroupY = bbox.y + defaultNatRowH_bb * rowIdx;
                const groupX = (row.posX ?? bbox.x) + (row.offsetX ?? 0);
                const groupY = (row.posY ?? baseGroupY) + (row.offsetY ?? 0);
                const groupBendYShift = Math.min(0, bend) * (groupH / effectiveNatH);
                const rowRotation = row.rotation ?? 0;

                // Corners relative to group origin (top-left)
                const corners = [
                    { x: 0, y: 0 },
                    { x: groupW, y: 0 },
                    { x: groupW, y: groupH },
                    { x: 0, y: groupH },
                ];
                // Apply rotation around (0,0) then translate to world position
                const rad = (rowRotation * Math.PI) / 180;
                const cosR = Math.cos(rad);
                const sinR = Math.sin(rad);
                corners.forEach(c => {
                    const rx = c.x * cosR - c.y * sinR + groupX;
                    const ry = c.x * sinR + c.y * cosR + groupY + groupBendYShift;
                    expandBounds(rx, ry);
                });
            });
        });

        if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 800; maxY = 600; }

        const pad = 20;
        const vbX = minX - pad, vbY = minY - pad, vbW = maxX - minX + pad * 2, vbH = maxY - minY + pad * 2;

        const lines: string[] = [];
        lines.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet">`);
        lines.push(`<style>`);
        lines.push(`.seat-block { cursor: pointer; } .seat-block:hover { filter: brightness(1.15); }`);
        lines.push(`.seat { cursor: pointer; } .seat:hover { filter: brightness(1.2); stroke: #00ff00; stroke-width: 2; }`);
        lines.push(`.row-label { font-family: sans-serif; pointer-events: none; }`);
        lines.push(`</style>`);

        // ── Embed full canvas state as JSON metadata for lossless import ──
        const canvasState = {
            shapes: shapes.map(s => {
                const obj: any = { ...s };
                // Strip transient runtime fields if any, keep all visual/structural data
                return obj;
            }),
            seats,
            images: images.map(img => {
                // Exclude the loaded HTMLImageElement (domImg) which can't be serialized
                const { domImg, ...rest } = img as any;
                return rest;
            }),
        };
        const stateJson = JSON.stringify(canvasState);
        // Base64 encode to avoid XML escaping issues with JSON special chars
        const stateB64 = btoa(unescape(encodeURIComponent(stateJson)));
        lines.push(`<metadata><seatmap-state encoding="base64">${stateB64}</seatmap-state></metadata>`);

        // ── Images (sorted by zIndex, lower first = painted first = behind) ──
        [...images].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)).forEach(img => {
            const rot = img.rotation ? ` transform="rotate(${img.rotation}, ${img.x + img.width / 2}, ${img.y + img.height / 2})"` : '';
            lines.push(`<image href="${escXml(img.url)}" x="${img.x}" y="${img.y}" width="${img.width}" height="${img.height}"${rot} />`);
        });

        // ── Shapes (sorted by zIndex, lower first = painted first = behind) ──
        [...shapes].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)).forEach(shape => {
            const isSeatBlock = !!shape.seat_block;
            const sectionId = shape.seat_block?.id || shape.id;
            const sectionName = shape.seat_block?.name || '';
            const clickAttrs = isSeatBlock
                ? ` class="seat-block" data-section-id="${escXml(sectionId)}" data-section-name="${escXml(sectionName)}" data-clickable="true"`
                : '';
            const opacity = shape.opacity ?? 1;
            const fill = opaqueColor(shape.color, opacity) || 'transparent';
            const stroke = shape.strokeColor || 'transparent';
            const sw = shape.strokeWidth || 2;

            if (shape.type === 'rect' && shape.svgPath) {
                // Prebuilt icon shape: render background rect + scaled SVG path
                const [x, y, w, h] = shape.points;
                const vb = shape.svgViewBox || 100;
                const scX = w / vb;
                const scY = h / vb;
                const rot = shape.rotation ? ` transform="rotate(${shape.rotation}, ${x + w / 2}, ${y + h / 2})"` : '';
                const bgFill = shape.svgPathStroke ? fill : 'transparent';
                lines.push(`<g${rot}>`);
                lines.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${bgFill}" stroke="${stroke}" stroke-width="${sw}" opacity="${opacity}"${clickAttrs} />`);
                const iconFill = shape.svgPathStroke ? 'transparent' : fill;
                const iconStroke = shape.svgPathStroke ? '#ffffff' : 'transparent';
                const iconSW = shape.svgPathStroke ? Math.max(1, 2 / Math.min(scX, scY)) : 0;
                lines.push(`<g transform="translate(${x},${y}) scale(${scX},${scY})"><path d="${shape.svgPath}" fill="${iconFill}" stroke="${iconStroke}" stroke-width="${iconSW}" /></g>`);
                lines.push(`</g>`);
            } else if (shape.type === 'rect') {
                const [x, y, w, h] = shape.points;
                const rot = shape.rotation ? ` transform="rotate(${shape.rotation}, ${x + w / 2}, ${y + h / 2})"` : '';
                lines.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${opacity}"${clickAttrs}${rot} />`);
            } else if (shape.type === 'circle') {
                const cx = shape.cx || 0, cy = shape.cy || 0, r = shape.r || 50;
                lines.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${opacity}"${clickAttrs} />`);
            } else if (shape.type === 'text') {
                const [x, y, w, h] = shape.points;
                const rot = shape.rotation ? ` transform="rotate(${shape.rotation}, ${x + w / 2}, ${y + h / 2})"` : '';
                lines.push(`<text x="${x + 4}" y="${y + (shape.fontSize || 16)}" font-size="${shape.fontSize || 16}" fill="${shape.color || '#ffffff'}" opacity="${opacity}"${rot}>${escXml(shape.text || '')}</text>`);
            } else if (shape.type === 'line') {
                const d = shape.points.reduce((acc, v, i) => acc + (i % 2 === 0 ? (i === 0 ? 'M' : 'L') + v : ',' + v + ' '), '');
                lines.push(`<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${sw}" opacity="${opacity}"${clickAttrs} />`);
            } else if (shape.type === 'group') {
                // Group shape: render as a dashed rect (matching canvas rendering)
                const [x, y, w, h] = shape.points;
                lines.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-dasharray="8 4" opacity="${opacity}" />`);
            } else {
                // Polygon: trapezoid, custom
                const d = shape.points.reduce((acc, v, i) => acc + (i % 2 === 0 ? (i === 0 ? 'M' : 'L') + v : ',' + v + ' '), '') + 'Z';
                let extra = '';
                if (shape.type === 'custom' && shape.holeRadius && shape.holeX !== undefined && shape.holeY !== undefined) {
                    extra = `<circle cx="${shape.holeX}" cy="${shape.holeY}" r="${shape.holeRadius}" fill="white" />`;
                }
                lines.push(`<path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${opacity}"${clickAttrs} />`);
                if (extra) lines.push(extra);
            }

            // ── Seat rows inside seat-block shapes ──
            if (shape.seat_block) {
                const rows = shape.seat_block.section_rows || [];
                const bbox = getShapeBBox(shape);
                const LABEL_W = 24;
                const defaultSeatDiam = SEAT_DOT_RADIUS * 2;
                const defaultNatRowH = defaultSeatDiam + ROW_PADDING * 2;

                rows.forEach((row: any, rowIdx: number) => {
                    const rowSeats = row.row_seats || [];
                    if (rowSeats.length === 0) return;

                    const rowSeatRadius = row.seatRadius ?? SEAT_DOT_RADIUS;
                    const seatDiameter = rowSeatRadius * 2;
                    const natRowH = seatDiameter + ROW_PADDING * 2;

                    const bend = row.bend ?? 0;
                    const bendAbs = Math.abs(bend);
                    // Match canvas: use effectiveNaturalH that accounts for bend
                    const effectiveNatH = natRowH + (bendAbs > 0.5 ? bendAbs : 0);
                    const yBendOffset = bend < -0.5 ? -bend : 0;

                    const baseGroupY = bbox.y + defaultNatRowH * rowIdx;
                    const natRowW = LABEL_W + rowSeats.length * seatDiameter + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                    const groupW = row.width ?? natRowW;
                    const groupH = row.height ?? effectiveNatH;
                    const groupX = (row.posX ?? bbox.x) + (row.offsetX ?? 0);
                    const groupY = (row.posY ?? baseGroupY) + (row.offsetY ?? 0);
                    const rowRotation = row.rotation ?? 0;
                    // Scale relative to effectiveNaturalH (matching canvas)
                    const scaleX = groupW / natRowW;
                    const scaleY = groupH / effectiveNatH;
                    const seatsAreaStartX = LABEL_W + ROW_PADDING;
                    const rowId = row.id || `row-${rowIdx}`;

                    // Match canvas: shift group for upward arches
                    const groupBendYShift = Math.min(0, bend) * scaleY;

                    // Build <g> transform to match Konva canvas exactly:
                    // Konva Group is at (groupX, groupY + groupBendYShift) and rotates
                    // around that point (its top-left origin, since no offsetX/Y is set).
                    // Children use RELATIVE coordinates inside the Group.
                    // So in SVG we: translate to (groupX, groupY + groupBendYShift),
                    // then rotate around (0,0) of the translated space.
                    let transformParts: string[] = [];
                    transformParts.push(`translate(${groupX}, ${groupY + groupBendYShift})`);
                    if (rowRotation) {
                        transformParts.push(`rotate(${rowRotation})`);
                    }
                    const groupTransform = ` transform="${transformParts.join(' ')}"`;

                    lines.push(`<g${groupTransform}>`);

                    // ── Row name label (RELATIVE coords inside group) ──
                    const rowName = row.name || 'N/A';
                    {
                        const labelCenterNatX = LABEL_W / 2;
                        const labelT = labelCenterNatX / Math.max(1, natRowW);
                        const labelBendOffsetY = bend * 4 * labelT * (1 - labelT);
                        const labelFontSize = Math.min(10, groupH * 0.6);
                        const labelX = 4 * scaleX;
                        const labelY = (natRowH / 2 + labelBendOffsetY + yBendOffset) * scaleY + labelFontSize * 0.35;
                        lines.push(`<text x="${labelX}" y="${labelY}" font-size="${labelFontSize}" fill="rgba(255,255,255,0.6)" class="row-label">${escXml(rowName)}</text>`);
                    }

                    // ── Seat circles (RELATIVE coords inside group) ──
                    rowSeats.forEach((seat: any, seatIdx: number) => {
                        const t = rowSeats.length > 1 ? seatIdx / (rowSeats.length - 1) : 0.5;
                        const natX = seatsAreaStartX + seatIdx * (seatDiameter + SEAT_GAP) + rowSeatRadius;
                        const natY = natRowH / 2;
                        const bendOff = bend * 4 * t * (1 - t);
                        const sx = natX * scaleX;
                        const sy = (natY + bendOff + yBendOffset) * scaleY;
                        const sr = rowSeatRadius * Math.min(scaleX, scaleY);
                        const seatId = seat.seat_id || `${rowId}-s-${seatIdx + 1}`;
                        lines.push(`<circle cx="${sx}" cy="${sy}" r="${sr}" fill="#87ceeb" stroke="rgba(255,255,255,0.6)" stroke-width="1" class="seat" data-seat-id="${escXml(seatId)}" data-row-id="${escXml(rowId)}" data-section-id="${escXml(sectionId)}" data-clickable="true" />`);
                    });

                    lines.push(`</g>`);
                });
            }
        });

        // ── Standalone seats (sorted by zIndex) ──
        [...seats].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)).forEach(seat => {
            const r = seat.radius || 12;
            const seatBlockId = seat.seat_block?.id || '';
            lines.push(`<circle cx="${seat.x}" cy="${seat.y}" r="${r}" fill="${seat.color || '#87ceeb'}" opacity="${seat.opacity ?? 1}" class="seat" data-seat-id="${escXml(seat.id)}" ${seatBlockId ? `data-section-id="${escXml(seatBlockId)}"` : ''} data-clickable="true" />`);
        });

        lines.push(`</svg>`);
        return lines.join('\n');
    };

    // ── Parse canvas state back from an SVG string (embedded metadata) ────────
    const parseCanvasStateFromSVG = (svgString: string): { shapes: Shape[]; seats: any[]; images: any[] } | null => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgString, 'image/svg+xml');
            const stateEl = doc.querySelector('seatmap-state');
            if (!stateEl || !stateEl.textContent) return null;
            const encoding = stateEl.getAttribute('encoding');
            let json: string;
            if (encoding === 'base64') {
                json = decodeURIComponent(escape(atob(stateEl.textContent.trim())));
            } else {
                json = stateEl.textContent.trim();
            }
            const parsed = JSON.parse(json);
            if (parsed && Array.isArray(parsed.shapes)) {
                return {
                    shapes: parsed.shapes as Shape[],
                    seats: Array.isArray(parsed.seats) ? parsed.seats : [],
                    images: Array.isArray(parsed.images) ? parsed.images : [],
                };
            }
            return null;
        } catch (e) {
            console.warn('Failed to parse seatmap state from SVG:', e);
            return null;
        }
    };

    // ── Build the full payload for export ─────────────────────────────────────
    const getSeatMapPayload = (): SeatMapData => ({
        ...seatMap,
        svg_path: generateSeatMapSVG(),
        sections: buildSectionsFromShapes(shapes),
    });

    // ── Export / save seat map to the backend ─────────────────────────────────
    const exportSeatMap = async () => {
        const payload = getSeatMapPayload();
        try {
            const fd = new FormData();

            // Owner id (backend authoritatively re-stamps it from the session).
            const ownerId = getUser()?.id;
            if (ownerId) fd.append('user_id', String(ownerId));

            // SVG as a .svg file
            const svgBlob = new Blob([payload.svg_path], { type: 'image/svg+xml' });
            fd.append('svg_path', svgBlob, 'seatmap.svg');

            // Venue interior preview image (user-chosen media)
            if (venuePreviewFile) {
                fd.append('image_preview', venuePreviewFile, venuePreviewFile.name);
            }

            // Scalar fields
            fd.append('venue_name', payload.venue_name || '');
            if (typeof payload.capacity === 'number' && payload.capacity > 0) {
                fd.append('capacity', String(payload.capacity));
            }
            fd.append('configuration', payload.configuration || '');

            // Location — send each field as bracket notation so PHP parses it as an array (not a JSON string)
            const loc0 = payload.location?.[0];
            if (loc0) {
                fd.append('location[street]', loc0.street || '');
                fd.append('location[house_number]', loc0.housenumber || '');
                fd.append('location[postal_code]', loc0.postal_code || '');
                fd.append('location[city]', loc0.city || '');
                fd.append('location[state]', loc0.state || '');
                fd.append('location[country]', loc0.country || '');
            }

            // Anchor point
            fd.append('anchor_point[x]', String(payload.anchor_point.x));
            fd.append('anchor_point[y]', String(payload.anchor_point.y));
            fd.append('anchor_point[description]', payload.anchor_point.description || '');

            // Sections — flatten nested objects into bracket notation for Laravel
            payload.sections.forEach((sec, si) => {
                fd.append(`sections[${si}][id]`, sec.id);
                fd.append(`sections[${si}][name]`, sec.name || '');
                fd.append(`sections[${si}][gate]`, sec.gate || '');
                fd.append(`sections[${si}][zone]`, sec.zone || '');
                fd.append(`sections[${si}][level]`, sec.level || '');
                if (typeof sec.capacity === 'number' && sec.capacity > 0) {
                    fd.append(`sections[${si}][capacity]`, String(sec.capacity));
                }
                fd.append(`sections[${si}][category]`, sec.category || '');
                fd.append(`sections[${si}][is_seat_specific]`, sec.is_seat_specific ? '1' : '0');
                if (sec.section_rows.length > 0) {
                    sec.section_rows.forEach((row, ri) => {
                        fd.append(`sections[${si}][section_rows][${ri}][id]`, row.id);
                        if (row.name) fd.append(`sections[${si}][section_rows][${ri}][name]`, row.name);
                        if (row.row_seats.length > 0) {
                            row.row_seats.forEach((seat, seatIdx) => {
                                fd.append(`sections[${si}][section_rows][${ri}][row_seats][${seatIdx}][seat_id]`, seat.seat_id);
                                fd.append(`sections[${si}][section_rows][${ri}][row_seats][${seatIdx}][num]`, seat.num);
                            });
                        }
                    });
                }
            });

            const response = await fetch(`${appUrl}seatmap/create`, {
                method: 'POST',
                headers: { ...authHeader() },
                body: fd,
            });
            if (!response.ok) {
                const errBody = await response.text();
                console.error('Server error body:', errBody);
                throw new Error(`Server responded ${response.status}`);
            }
            const data = await response.json();
            if (data?.id) {
                setSeatMap(prev => ({ ...prev, id: data.id }));
                setImportedFromId(data.id);
            }
            alert('Seat map saved successfully!');
        } catch (err) {
            console.error('Error saving seat map:', err);
            alert('Failed to save seat map. Check console for details.');
        }
    };

    // ── Update existing seat map in DB ────────────────────────────────────────
    const updateExistingSeatMap = async () => {
        if (!importedFromId) return;
        const payload = getSeatMapPayload();
        try {
            const fd = new FormData();
            fd.append('id', String(importedFromId));

            // Owner id (backend keeps the original owner; only claims if unset).
            const ownerId = getUser()?.id;
            if (ownerId) fd.append('user_id', String(ownerId));

            const svgBlob = new Blob([payload.svg_path], { type: 'image/svg+xml' });
            fd.append('svg_path', svgBlob, 'seatmap.svg');

            // Venue interior preview image (user-chosen media)
            if (venuePreviewFile) {
                fd.append('image_preview', venuePreviewFile, venuePreviewFile.name);
            }

            fd.append('venue_name', payload.venue_name || '');
            if (typeof payload.capacity === 'number' && payload.capacity > 0) {
                fd.append('capacity', String(payload.capacity));
            }
            fd.append('configuration', payload.configuration || '');

            // Location — send each field as bracket notation so PHP parses it as an array (not a JSON string)
            const locU = payload.location?.[0];
            if (locU) {
                fd.append('location[street]', locU.street || '');
                fd.append('location[house_number]', locU.housenumber || '');
                fd.append('location[postal_code]', locU.postal_code || '');
                fd.append('location[city]', locU.city || '');
                fd.append('location[state]', locU.state || '');
                fd.append('location[country]', locU.country || '');
            }

            fd.append('anchor_point[x]', String(payload.anchor_point.x));
            fd.append('anchor_point[y]', String(payload.anchor_point.y));
            fd.append('anchor_point[description]', payload.anchor_point.description || '');

            payload.sections.forEach((sec, si) => {
                fd.append(`sections[${si}][id]`, sec.id);
                fd.append(`sections[${si}][name]`, sec.name || '');
                fd.append(`sections[${si}][gate]`, sec.gate || '');
                fd.append(`sections[${si}][zone]`, sec.zone || '');
                fd.append(`sections[${si}][level]`, sec.level || '');
                if (typeof sec.capacity === 'number' && sec.capacity > 0) {
                    fd.append(`sections[${si}][capacity]`, String(sec.capacity));
                }
                fd.append(`sections[${si}][category]`, sec.category || '');
                fd.append(`sections[${si}][is_seat_specific]`, sec.is_seat_specific ? '1' : '0');
                if (sec.section_rows.length > 0) {
                    sec.section_rows.forEach((row, ri) => {
                        fd.append(`sections[${si}][section_rows][${ri}][id]`, row.id);
                        if (row.name) fd.append(`sections[${si}][section_rows][${ri}][name]`, row.name);
                        if (row.row_seats.length > 0) {
                            row.row_seats.forEach((seat, seatIdx) => {
                                fd.append(`sections[${si}][section_rows][${ri}][row_seats][${seatIdx}][seat_id]`, seat.seat_id);
                                fd.append(`sections[${si}][section_rows][${ri}][row_seats][${seatIdx}][num]`, seat.num);
                            });
                        }
                    });
                }
            });

            const response = await fetch(`${appUrl}seatmap/update`, {
                method: 'POST',
                headers: { ...authHeader() },
                body: fd,
            });
            if (!response.ok) {
                const errBody = await response.text();
                console.error('Server error body:', errBody);
                throw new Error(`Server responded ${response.status}`);
            }
            alert('Seat map updated successfully!');
        } catch (err) {
            console.error('Error updating seat map:', err);
            alert('Failed to update seat map. Check console for details.');
        }
    };

    // ── Import venue from DB ──────────────────────────────────────────────────
    // Format location array into readable address
    const formatLocation = (loc: any): string => {
        if (!loc) return '';
        if (typeof loc === 'string') return loc;
        if (Array.isArray(loc) && loc.length > 0) {
            // Check if it's an array of objects or strings
            const first = loc[0];
            if (typeof first === 'object') {
                const { street, housenumber, house_number, state, country, city, zip, postal_code } = first;
                const hn = housenumber || house_number || '';
                const st = street || '';
                const ct = city || '';
                const s = state || '';
                const c = country || '';
                const z = zip || postal_code || '';
                // Detect US-style: number before street
                const isUS = c && (c.toLowerCase().includes('us') || c.toLowerCase().includes('united states'));
                if (isUS) {
                    return [hn && st ? `${hn} ${st}` : st || hn, ct, s ? `${s} ${z}`.trim() : z, c].filter(Boolean).join(', ');
                }
                // European: street number, zip city, country
                return [st && hn ? `${st} ${hn}` : st || hn, z && ct ? `${z} ${ct}` : ct || z, s, c].filter(Boolean).join(', ');
            }
            return loc.filter(Boolean).join(', ');
        }
        if (typeof loc === 'object') {
            const { street, housenumber, house_number, state, country, city, zip, postal_code } = loc;
            const hn = housenumber || house_number || '';
            const st = street || '';
            const ct = city || '';
            const s = state || '';
            const c = country || '';
            const z = zip || postal_code || '';
            const isUS = c && (c.toLowerCase().includes('us') || c.toLowerCase().includes('united states'));
            if (isUS) {
                return [hn && st ? `${hn} ${st}` : st || hn, ct, s ? `${s} ${z}`.trim() : z, c].filter(Boolean).join(', ');
            }
            return [st && hn ? `${st} ${hn}` : st || hn, z && ct ? `${z} ${ct}` : ct || z, s, c].filter(Boolean).join(', ');
        }
        return '';
    };

    const searchImportVenues = async (query: string) => {
        if (!query.trim()) {
            setImportSuggestions([]);
            setImportDropdownOpen(false);
            return;
        }
        setImportLoading(true);
        try {
            const res = await fetch(`${appUrl}seatmap/search?query=${encodeURIComponent(query.trim())}`, {
                headers: { ...authHeader() },
            });
            if (res.status === 401) {
                // Session expired mid-session → re-prompt login, keep the popup.
                setImportSuggestions([]);
                requireAuth(() => searchImportVenues(query));
                return;
            }
            if (res.status === 404) {
                setImportSuggestions([]);
                setImportDropdownOpen(true);
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setImportSuggestions(Array.isArray(data) ? data : []);
            setImportDropdownOpen(true);
        } catch (err) {
            console.error('Venue search failed:', err);
            setImportSuggestions([]);
        } finally {
            setImportLoading(false);
        }
    };

    const handleImportSearchChange = (value: string) => {
        setImportSearchQuery(value);
        // Debounce: wait 350ms after user stops typing
        if (importSearchTimerRef.current) clearTimeout(importSearchTimerRef.current);
        if (value.trim().length >= 1) {
            importSearchTimerRef.current = setTimeout(() => searchImportVenues(value), 350);
        } else {
            setImportSuggestions([]);
            setImportDropdownOpen(false);
        }
    };

    const openImportPopup = () => {
        // Importing an existing venue is an authoring action → require login.
        if (!isLoggedIn()) {
            requireAuth(() => openImportPopup());
            return;
        }
        setImportSearchQuery('');
        setImportSuggestions([]);
        setImportDropdownOpen(false);
        setImportPopupOpen(true);
    };

    const selectImportVenue = async (venueId: number) => {
        setImportDropdownOpen(false);
        setImportLoading(true);
        try {
            const res = await fetch(`${appUrl}seatmap/get/${venueId}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const venueData = await res.json();

            // Clear existing canvas
            setShapes([]);
            setSeats([]);
            setImages([]);
            setSelectedId(null);
            setSelectedType(null);
            setSelectedIds([]);

            // Load venue metadata
            setSeatMap({
                id: venueData.id,
                venue_name: venueData.venue_name || '',
                capacity: typeof venueData.capacity === 'number' ? venueData.capacity : '',
                configuration: venueData.configuration || '',
                svg_path: venueData.svg_path || '',
                image_preview_path: venueData.image_preview_path || '',
                anchor_point: {
                    x: Number(venueData.anchor_point?.x) || 0,
                    y: Number(venueData.anchor_point?.y) || 0,
                    description: venueData.anchor_point?.description || '',
                },
                sections: venueData.sections || [],
                location: (() => {
                    const l = venueData.location;
                    if (Array.isArray(l) && l.length > 0) return l;
                    if (l && typeof l === 'object') return [{ street: l.street || '', housenumber: l.housenumber || l.house_number || '', postal_code: l.postal_code || '', city: l.city || '', state: l.state || '', country: l.country || '' }];
                    return [{ street: '', housenumber: '', postal_code: '', city: '', state: '', country: '' }];
                })(),
            });

            // Try to restore full canvas state from SVG metadata first
            // Prefer inline svg_content from API (avoids CORS/fetch issues),
            // fall back to fetching the SVG file if not available
            const rawSvgPath = venueData.svg_path;
            const svgUrl = rawSvgPath
                ? (rawSvgPath.startsWith('http') ? rawSvgPath : `${storageUrl}${rawSvgPath}`)
                : '';
            let restoredFromSVG = false;

            // 1) Try parsing from inline svg_content (returned by backend)
            if (venueData.svg_content) {
                try {
                    const canvasState = parseCanvasStateFromSVG(venueData.svg_content);
                    if (canvasState) {
                        setShapes(canvasState.shapes);
                        setSeats(canvasState.seats);
                        const restoredImages = canvasState.images.map((img: any) => ({ ...img }));
                        setImages(restoredImages);
                        restoredFromSVG = true;
                    }
                } catch (e) {
                    console.warn('Could not parse inline svg_content metadata:', e);
                }
            }

            // 2) Fall back to fetching the SVG file by URL
            if (!restoredFromSVG && svgUrl) {
                try {
                    const svgRes = await fetch(svgUrl);
                    if (svgRes.ok) {
                        const svgText = await svgRes.text();
                        const canvasState = parseCanvasStateFromSVG(svgText);
                        if (canvasState) {
                            setShapes(canvasState.shapes);
                            setSeats(canvasState.seats);
                            const restoredImages = canvasState.images.map((img: any) => ({ ...img }));
                            setImages(restoredImages);
                            restoredFromSVG = true;
                        }
                    }
                } catch (e) {
                    console.warn('Could not fetch/parse SVG for metadata import, falling back to sections:', e);
                }
            }

            // 3) Fallback: load SVG as background image + reconstruct shapes from sections
            if (!restoredFromSVG) {
                if (svgUrl) {
                    const domImg = new window.Image();
                    domImg.crossOrigin = 'anonymous';
                    domImg.onload = () => {
                        const maxW = stageSize.width * 0.8;
                        const maxH = stageSize.height * 0.8;
                        const aspectRatio = domImg.width / domImg.height;
                        let w = Math.min(domImg.width, maxW);
                        let h = w / aspectRatio;
                        if (h > maxH) { h = maxH; w = h * aspectRatio; }
                        setImages(prev => [...prev, {
                            id: `img-import-${Date.now()}`,
                            x: -w / 2,
                            y: -h / 2,
                            width: Math.round(w),
                            height: Math.round(h),
                            url: svgUrl,
                            zIndex: 50,
                        }]);
                    };
                    domImg.onerror = () => console.warn('Could not load SVG as image:', svgUrl);
                    domImg.src = svgUrl;
                }

                // Reconstruct shapes from sections (basic fallback — no visual overrides)
                if (venueData.sections && Array.isArray(venueData.sections)) {
                    const newShapes: Shape[] = [];
                    venueData.sections.forEach((sec: any, idx: number) => {
                        const shapeId = `sh-import-${Date.now()}-${idx}`;
                        const x = -200 + (idx % 5) * 120;
                        const y = -150 + Math.floor(idx / 5) * 80;
                        newShapes.push({
                            id: shapeId,
                            type: 'rect',
                            points: [x, y, 100, 60],
                            color: '#6496c8',
                            strokeColor: '#333',
                            strokeWidth: 2,
                            zIndex: 500,
                            seat_block: {
                                id: sec.id || shapeId,
                                gate: sec.gate || '',
                                name: sec.name || '',
                                zone: sec.zone || '',
                                level: sec.level || '',
                                capacity: typeof sec.capacity === 'number' ? sec.capacity : undefined,
                                category: sec.category || '',
                                is_seat_specific: !!sec.is_seat_specific,
                                section_rows: (sec.section_rows || []).map((r: any) => ({
                                    id: r.id || '',
                                    name: r.name || '',
                                    row_seats: (r.row_seats || []).map((s: any) => ({
                                        seat_id: s.seat_id || '',
                                        num: s.num || '',
                                    })),
                                })),
                            },
                        });
                    });
                    setShapes(newShapes);
                }
            }

            setImportedFromId(venueData.id);
            setImportPopupOpen(false);

            // Load venue interior preview if it exists
            setVenuePreviewFile(null);
            if (venueData.image_preview_path) {
                const previewPath = venueData.image_preview_path;
                setVenuePreviewUrl(previewPath.startsWith('http') ? previewPath : `${storageUrl}${previewPath}`);
            } else {
                setVenuePreviewUrl('');
            }
        } catch (err) {
            console.error('Failed to import venue:', err);
            alert('Failed to import venue. Check console for details.');
        } finally {
            setImportLoading(false);
        }
    };

    // ── Save preview flow ─────────────────────────────────────────────────────
    const handleSaveClick = () => {
        // Require login before the save flow (model freely, log in to save).
        if (!isLoggedIn()) {
            requireAuth(() => handleSaveClick());
            return;
        }
        setSaveValidationError(null);
        const svgStr = generateSeatMapSVG();
        setSavePreviewSvg(svgStr);
        setExpandedBlocks({});
        setPreviewPan({ x: 0, y: 0 });
        setPreviewZoom(1);
        setSavePreviewOpen(true);
    };

    const handleSaveNew = async () => {
        setSavePreviewOpen(false);
        await exportSeatMap();
    };

    const handleUpdateExisting = async () => {
        setSavePreviewOpen(false);
        await updateExistingSeatMap();
    };

    // Undo
    const undo = () => {
        if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            setShapes(JSON.parse(JSON.stringify(prevState.shapes)));
            setSeats(JSON.parse(JSON.stringify(prevState.seats)));
            setImages(JSON.parse(JSON.stringify(prevState.images)));
            setHistoryIndex(prev => prev - 1);
        }
    };

    useEffect(() => {
        const updateSize = () => {
            if (stageContainerRef.current) {
                const width = stageContainerRef.current.offsetWidth;
                const height = stageContainerRef.current.offsetHeight;
                setStageSize({ width, height });
                // Update center point to be at the center of the new size
                setCenterPoint({ x: width / 2, y: height / 2 });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Attach non-passive wheel listener to the preview container for zoom
    useEffect(() => {
        const el = previewContainerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            setPreviewZoom(prev => Math.max(0.1, Math.min(10, prev * delta)));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    });

    const toggleTool = (tool: typeof activeTool) => { setActiveTool(activeTool === tool ? null : tool); setSelectedId(null); setSelectedType(null); setSelectedIds([]); };

    // Add a prebuilt icon shape to the canvas
    const addPrebuiltIcon = (icon: typeof PREBUILT_ICONS[number]) => {
        saveToHistory();
        const size = 100;
        const newShape: Shape = {
            id: `sh-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: 'rect',
            points: [-size / 2, -size / 2, size, size],
            color: icon.defaultColor,
            strokeColor: 'transparent',
            strokeWidth: 2,
            opacity: 1,
            zIndex: 1000,
            svgPath: icon.svgPath,
            svgPathStroke: icon.strokeMode,
            svgViewBox: icon.viewBox,
            name: icon.label,
        };
        setShapes(prev => [...prev, newShape]);
        setSelectedId(newShape.id);
        setSelectedType('shape');
        setSelectedIds([newShape.id]);
        setPrebuiltMenuOpen(false);
        setActiveTool(null);
    };
    const allTools = [
        { id: 'image', label: '🖼️ Image', action: () => fileInputRef.current?.click(), isImageBtn: true },
        { id: 'custom', label: '🖋️ Draw', action: () => toggleTool('custom') },
        { id: 'rect', label: '▭ Rect', action: () => toggleTool('rect') },
        { id: 'text', label: '📝 Text', action: () => toggleTool('text') },
        { id: 'line', label: '⎯ Line', action: () => toggleTool('line') },
        { id: 'circle', label: '● Circle', action: () => toggleTool('circle') },
        { id: 'trapezoid', label: '⏢ Trapezoid', action: () => toggleTool('trapezoid') },
        { id: 'addrow', label: '➕ Add Row', action: () => {
            if (selectedId && selectedType === 'shape') {
                const sel = shapes.find(s => s.id === selectedId);
                if (sel?.seat_block) { openAddRowDialog(selectedId); return; }
            }
            // If no seat-block shape is selected, do nothing (button appears disabled)
        }, isAddRow: true },
        { id: 'prebuilt', label: '🧩 Custom', action: () => setPrebuiltMenuOpen(prev => !prev), isPrebuilt: true },
        { id: 'reset', label: '🗑️ Reset', action: () => { setSeats([]); setShapes([]); setImages([]); }, isReset: true },
    ];

    // Responsive toolbar: calculate visible tools based on available width
    const [visibleToolCount, setVisibleToolCount] = useState(allTools.length);
    const rightButtonsRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        const calculateVisibleTools = () => {
            if (!toolbarRef.current || !rightButtonsRef.current) return;
            const containerWidth = toolbarRef.current.offsetWidth;
            const rightButtonsWidth = rightButtonsRef.current.offsetWidth;
            const padding = 20; // toolbar padding
            const gap = 8; // gap between buttons
            const moreButtonWidth = 90; // estimated More button width
            const minMargin = 30; // minimum margin from Finish/Save
            const availableWidth = containerWidth - rightButtonsWidth - padding - minMargin;
            
            // Approximate button widths (emoji + text + padding)
            const buttonWidth = 85;
            
            // Calculate how many buttons fit
            let fitCount = 0;
            let totalWidth = 0;
            for (let i = 0; i < allTools.length; i++) {
                const nextWidth = buttonWidth + (fitCount > 0 ? gap : 0);
                // Check if this is the last button or if we need More button space
                const needsMore = i < allTools.length - 1;
                if (totalWidth + nextWidth + (needsMore ? moreButtonWidth + gap : 0) <= availableWidth) {
                    totalWidth += nextWidth;
                    fitCount++;
                } else {
                    break;
                }
            }
            
            // If all tools fit, show all - no need for More button
            if (fitCount >= allTools.length) {
                setVisibleToolCount(allTools.length);
            } else {
                // Show fitCount tools + More button
                setVisibleToolCount(Math.max(1, fitCount));
            }
        };
        
        calculateVisibleTools();
        window.addEventListener('resize', calculateVisibleTools);
        return () => window.removeEventListener('resize', calculateVisibleTools);
    }, []);

    const visibleTools = allTools.slice(0, visibleToolCount);
    const overflowTools = allTools.slice(visibleToolCount);
    const hasOverflow = overflowTools.length > 0;

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            // When the inline HTML text editor is active, let it handle all keystrokes
            if (editingTextId) return;

            // Global shortcuts
            if (e.key === 'Delete' && selectedId) {
                saveToHistory();
                removeElement(selectedId, selectedType!);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                undo();
                return;
            }
            if (e.key === 'Escape') {
                setSelectedId(null);
                setSelectedType(null);
                setSelectedIds([]);
                setEditingTextId(null);
                return;
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [selectedId, selectedType, historyIndex, history, shapes, caretPositions]);

    // Auto-focus the contentEditable overlay when editing starts
    useEffect(() => {
        if (editingTextId && editingOverlayRef.current) {
            const el = editingOverlayRef.current;
            // Set initial content from shape text
            const shape = shapes.find(s => s.id === editingTextId && s.type === 'text');
            const txt = shape?.text || '';
            // If placeholder "new text" or empty, start with empty div
            el.innerHTML = (txt === 'new text' || txt === '') ? '' : txt;
            el.focus();
            // Place cursor at the end
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const sel = window.getSelection();
            if (sel) { sel.removeAllRanges(); sel.addRange(range); }
        }
    }, [editingTextId]);

    // Global mousedown listener: close text editing when clicking outside the overlay and toolbar
    useEffect(() => {
        if (!editingTextId) return;
        const handleGlobalMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't close if clicking inside the contentEditable overlay
            if (editingOverlayRef.current && editingOverlayRef.current.contains(target)) return;
            // Don't close if clicking inside the formatting toolbar (has data attribute)
            if (target.closest('[data-text-toolbar]')) return;
            setEditingTextId(null);
        };
        // Use setTimeout so the current double-click event doesn't immediately close
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleGlobalMouseDown, true);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleGlobalMouseDown, true);
        };
    }, [editingTextId]);

    // Global mousedown: deselect shape when clicking outside the stage container
    // (for clicks on page background, toolbars, etc. that Konva events don't reach)
    useEffect(() => {
        if (!selectedId || editingTextId) return; // editing has its own listener above
        const handleGlobalDeselect = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't deselect if clicking inside the stage container (Konva handles that)
            if (stageContainerRef.current && stageContainerRef.current.contains(target)) return;
            // Don't deselect if clicking inside the formatting toolbar
            if (target.closest('[data-text-toolbar]')) return;
            // Don't deselect if clicking on context menus, modals, or control panels
            if (target.closest('[data-no-deselect]') || target.closest('.modal') || target.closest('[role="dialog"]')) return;
            setSelectedId(null);
            setSelectedType(null);
            setSelectedIds([]);
        };
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleGlobalDeselect, true);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleGlobalDeselect, true);
        };
    }, [selectedId, editingTextId]);

    const sortedSeats = [...seats].sort((a, b) => (a.zIndex ?? 500) - (b.zIndex ?? 500));
    const sortedShapes = [...shapes].sort((a, b) => (a.zIndex ?? 500) - (b.zIndex ?? 500));

    // Helper to add a shape at the event position when a tool is active
    const addShapeAtPosition = (e: any) => {
        const stage = stageRef.current;
        if (!stage || !activeTool) return;
        const pos = stage.getRelativePointerPosition();
        if (!pos) return;
        const relX = pos.x - centerPoint.x;
        const relY = pos.y - centerPoint.y;

        if (activeTool === 'custom') {
            setTempPoints([...tempPoints, relX, relY]);
        } else if (activeTool === 'seat') {
            saveToHistory();
            setSeats([...seats, { id: `s-${Date.now()}`, x: relX, y: relY, zIndex: 500, color: '#87ceeb' }]);
        } else if (activeTool === 'circle') {
            saveToHistory();
            const newShape: Shape = {
                id: `sh-${Date.now()}`,
                type: 'circle',
                points: [],
                cx: relX,
                cy: relY,
                r: 50,
                color: '#64c864',
                strokeColor: '#333',
                strokeWidth: 2,
                zIndex: 500,
            };
            setShapes([...shapes, newShape]);
        } else if (activeTool === 'rect' || activeTool === 'line' || activeTool === 'trapezoid') {
            saveToHistory();
            const isTrapezoid = activeTool === 'trapezoid';
            const newShape: Shape = {
                id: `sh-${Date.now()}`,
                type: activeTool,
                points: activeTool === 'rect'
                    ? [relX, relY, 100, 60]
                    : isTrapezoid
                        ? [relX, relY, relX + 80, relY, relX + 60, relY + 40, relX + 20, relY + 40]
                        : [relX, relY, relX + 50, relY + 50],
                color: '#6496c8',
                strokeColor: '#333',
                strokeWidth: 2,
                zIndex: 500,
            };
            setShapes([...shapes, newShape]);
        } else if (activeTool === 'text') {
            saveToHistory();
            const newShape: Shape = {
                id: `sh-${Date.now()}`,
                type: 'text',
                points: [relX, relY, 150, 30],
                text: 'new text',
                fontSize: 16,
                color: '#000000',
                strokeColor: '#000000',
                strokeWidth: 5,
                zIndex: 500,
            };
            setShapes([...shapes, newShape]);
        }
    };

    const handleStageClick = (e: any) => {
        // Close prebuilt menu on any stage click
        if (prebuiltMenuOpen) setPrebuiltMenuOpen(false);
        // If we just finished dragging, don't process click
        if (isDraggingRef.current) {
            isDraggingRef.current = false;
            return;
        }
        // If a shape was just clicked, don't deselect
        // (text editing exit is handled by handleShapeMouseDown / seat/image mouseDown)
        if (shapeClickedRef.current) {
            shapeClickedRef.current = false;
            return;
        }
        // If we were editing a text shape, exit editing on any stage click
        if (editingTextId) {
            setEditingTextId(null);
        }
        // Check if clicked on empty area — deselect if so.
        // Sometimes Konva returns a nearby node as target; instead compute whether
        // the click point lies inside any seat/image/shape. If not, deselect.
        const stageEl = stageRef.current;
        if (stageEl) {
            const pos = stageEl.getRelativePointerPosition();
            if (pos) {
                const relX = pos.x - centerPoint.x;
                const relY = pos.y - centerPoint.y;

                const pointInPolygon = (px: number, py: number, poly: number[]) => {
                    let inside = false;
                    for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
                        const xi = poly[i], yi = poly[i + 1];
                        const xj = poly[j], yj = poly[j + 1];
                        const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi + 0.0) + xi);
                        if (intersect) inside = !inside;
                    }
                    return inside;
                };

                let clickedOnElement = false;
                const inset = Math.max(0, 2 / (zoomScale || 1)); // shrink hit box slightly to avoid right/bottom edge false-positives

                // Seats
                for (const s of seats) {
                    const r = s.radius ?? 12;
                    const dx = relX - s.x;
                    const dy = relY - s.y;
                    if (dx * dx + dy * dy <= r * r) { clickedOnElement = true; break; }
                }

                // Images
                if (!clickedOnElement) {
                    for (const img of images) {
                        const ix = img.x, iy = img.y, iw = img.width, ih = img.height;
                        if (relX >= ix && relX <= ix + iw && relY >= iy && relY <= iy + ih) { clickedOnElement = true; break; }
                    }
                }

                // Shapes
                if (!clickedOnElement) {
                    for (const sh of shapes) {
                        // Skip the text shape being edited (its Konva node is hidden)
                        if (sh.id === editingTextId) continue;
                        if (sh.type === 'rect' || sh.type === 'text') {
                            const [sx, sy, sw, shh] = sh.points;
                            if (relX >= sx + inset && relX <= sx + sw - inset && relY >= sy + inset && relY <= sy + shh - inset) { clickedOnElement = true; break; }
                        } else if (sh.type === 'circle') {
                            const cx = sh.cx ?? 0, cy = sh.cy ?? 0, r = sh.r ?? 50;
                            const dx = relX - cx, dy = relY - cy;
                            const rr = Math.max(0, r - inset);
                            if (dx * dx + dy * dy <= rr * rr) { clickedOnElement = true; break; }
                        } else if (sh.points && sh.points.length >= 4) {
                            // polygon/line/trapezoid/custom: use point-in-polygon on bbox-closed polygon
                            const poly = sh.points.slice();
                            if (poly.length % 2 === 1) poly.pop();
                            if (poly.length >= 6) {
                                if (pointInPolygon(relX, relY, poly)) { clickedOnElement = true; break; }
                            } else {
                                // fallback bbox test
                                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                                for (let i = 0; i < poly.length; i += 2) {
                                    minX = Math.min(minX, poly[i]); maxX = Math.max(maxX, poly[i]);
                                    minY = Math.min(minY, poly[i + 1]); maxY = Math.max(maxY, poly[i + 1]);
                                }
                                if (relX >= minX && relX <= maxX && relY >= minY && relY <= maxY) { clickedOnElement = true; break; }
                            }
                        }
                    }
                }

                if (!clickedOnElement) {
                    setSelectedId(null);
                    setSelectedType(null);
                    setSelectedIds([]);
                    setEditingTextId(null);
                }
            }
        }
        // If a tool is active, don't handle selection - proceed to draw
        // If no tool and clicked on stage (not shape), deselect happened above
        if (!activeTool) return; // No tool active - selection mode, don't draw
        if (selectedId && activeTool !== 'text') return; // Shape is selected, don't draw (except text tool)
        addShapeAtPosition(e);
    };

    const finishCustomShape = () => {
        if (tempPoints.length < 4) return;
        saveToHistory();
        const newShape: Shape = {
            id: `sh-${Date.now()}`,
            type: 'custom',
            points: tempPoints,
            color: '#6496c8',
            strokeColor: '#333',
            strokeWidth: 2,
            zIndex: 500,
        };
        setShapes([...shapes, newShape]);
        setTempPoints([]);
    };

    const handleSeatClick = (e: any, seatId: string) => {
        e.cancelBubble = true;
        shapeClickedRef.current = true; // Mark that a seat was clicked
        // If a tool is active and this was NOT a right-click, add a new shape at the click position
        if (activeTool) {
            const isRightClick = !!(e?.evt && typeof e.evt.button !== 'undefined' && e.evt.button === 2);
            if (!isRightClick) {
                addShapeAtPosition(e);
            }
            return;
        }
        // Left-click selects the seat (only if not already selected)
        if (selectedId === seatId) return; // Already selected, keep it
        setEditingTextId(null);
        setSelectedId(seatId);
        setSelectedType('seat');
        setSelectedIds([]);
    };

    const handleShapeClick = (e: any, shapeId: string) => {
        e.cancelBubble = true;
        // Don't interfere with marquee selection (use ref for synchronous check)
        if (isMarqueeSelecting || isMarqueeRef.current) return;
        shapeClickedRef.current = true; // Mark that a shape was clicked
        // If a tool is active and this was NOT a right-click, add a new shape at the click position
        if (activeTool) {
            const isRightClick = !!(e?.evt && typeof e.evt.button !== 'undefined' && e.evt.button === 2);
            if (!isRightClick) {
                addShapeAtPosition(e);
            }
            return;
        }
        // Left-click maintains selection if already selected
        if (selectedId === shapeId && !e.evt?.ctrlKey && !e.evt?.metaKey) return;
        if (editingTextId && editingTextId !== shapeId) setEditingTextId(null);
        if (e.evt?.ctrlKey || e.evt?.metaKey) {
            // Ctrl/Cmd+click: toggle in multi-select group
            setSelectedIds(prev =>
                prev.includes(shapeId) ? prev.filter(id => id !== shapeId) : [...prev, shapeId]
            );
            setSelectedId(shapeId);
            setSelectedType('shape');
        } else {
            setSelectedId(shapeId);
            setSelectedType('shape');
            setSelectedIds([shapeId]);
        }
    };

    const handleContextMenu = (e: any, targetId: string, targetType: 'seat' | 'shape' | 'image') => {
        // Only open context menu on right-click (button 2)
        if (e.evt && typeof e.evt.button !== 'undefined' && e.evt.button !== 2) return;
        e.evt?.preventDefault?.();
        e.cancelBubble = true;

        // If a drawing/tool is active, ignore right-clicks inside the layer entirely
        if (activeTool) return;
        
        if (!stageContainerRef.current?.getBoundingClientRect()) return;
        const { x, y } = calcMenuPos(e.evt?.clientX || e.clientX || 0, e.evt?.clientY || e.clientY || 0);
        setContextMenu({ x, y, targetId, targetType });
        setSelectedId(targetId);
        setSelectedType(targetType);
        setSelectedIds([targetId]);
    };

    const addPointToShape = (shapeId: string, clickX: number, clickY: number) => {
        const shape = shapes.find(s => s.id === shapeId);
        if (!shape) return;

        saveToHistory(); // Save before modifying

        // clickX/clickY are absolute stage coords — convert to relative
        const relClickX = clickX - centerPoint.x;
        const relClickY = clickY - centerPoint.y;

        let points = [...shape.points];

        // Handle circle: Convert to polygon and add point
        if (shape.type === 'circle') {
            const cx = shape.cx || 0;
            const cy = shape.cy || 0;
            const r = shape.r || 50;

            // Build polygon from circle with many points to maintain circular appearance
            // Use 32 points for smooth circle that stays round when adding points
            const numInitialPoints = 32;
            const circlePoints: number[] = [];
            for (let j = 0; j < numInitialPoints; j++) {
                const angle = (j / numInitialPoints) * Math.PI * 2;
                circlePoints.push(cx + r * Math.cos(angle), cy + r * Math.sin(angle));
            }

            // Find closest segment
            let closestSegment = 0, closestDistance = Infinity, closestT = 0;
            for (let i = 0; i < circlePoints.length; i += 2) {
                const x1 = circlePoints[i], y1 = circlePoints[i + 1];
                const x2 = circlePoints[(i + 2) % circlePoints.length];
                const y2 = circlePoints[(i + 3) % circlePoints.length];
                const dx = x2 - x1, dy = y2 - y1, len2 = dx * dx + dy * dy;
                if (len2 === 0) continue;
                let t = ((relClickX - x1) * dx + (relClickY - y1) * dy) / len2;
                t = Math.max(0, Math.min(1, t));
                const dist = Math.sqrt((relClickX - (x1 + t * dx)) ** 2 + (relClickY - (y1 + t * dy)) ** 2);
                if (dist < closestDistance) { closestDistance = dist; closestSegment = i; closestT = t; }
            }

            // Insert new point
            const ix = circlePoints[closestSegment] + closestT * (circlePoints[(closestSegment + 2) % circlePoints.length] - circlePoints[closestSegment]);
            const iy = circlePoints[closestSegment + 1] + closestT * (circlePoints[(closestSegment + 3) % circlePoints.length] - circlePoints[closestSegment + 1]);
            circlePoints.splice(closestSegment + 2, 0, ix, iy);

            // Convert to custom
            setShapes(shapes.map(s => s.id === shapeId
                ? { ...s, type: 'custom' as const, points: circlePoints, color: shape.color, strokeColor: '#333', strokeWidth: 2 }
                : s));
            setContextMenu(null);
            return;
        }

        // Handle rect: Convert [x, y, w, h] to 4 corner pairs
        if (shape.type === 'rect') {
            const [x, y, w, h] = shape.points;
            points = [x, y, x + w, y, x + w, y + h, x, y + h];
        }

        // General case: find closest segment in relative-coord points
        // For 'line' type: open polyline (don't wrap last point back to first)
        const isOpenLine = shape.type === 'line';
        let closestSegment = 0, closestDistance = Infinity, closestT = 0;
        const segCount = isOpenLine ? points.length - 2 : points.length;
        for (let i = 0; i < segCount; i += 2) {
            const x1 = points[i], y1 = points[i + 1];
            const x2 = points[(i + 2) % points.length], y2 = points[(i + 3) % points.length];
            const dx = x2 - x1, dy = y2 - y1, len2 = dx * dx + dy * dy;
            if (len2 === 0) continue;
            let t = ((relClickX - x1) * dx + (relClickY - y1) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            const dist = Math.sqrt((relClickX - (x1 + t * dx)) ** 2 + (relClickY - (y1 + t * dy)) ** 2);
            if (dist < closestDistance) { closestDistance = dist; closestSegment = i; closestT = t; }
        }

        const newPoints = [...points];
        const projX = points[closestSegment] + closestT * (points[(closestSegment + 2) % points.length] - points[closestSegment]);
        const projY = points[closestSegment + 1] + closestT * (points[(closestSegment + 3) % points.length] - points[closestSegment + 1]);
        newPoints.splice(closestSegment + 2, 0, projX, projY);

        if (shape.type === 'rect') {
            setShapes(shapes.map(s => s.id === shapeId
                ? { ...s, type: 'custom', points: newPoints, strokeColor: shape.strokeColor, strokeWidth: shape.strokeWidth }
                : s));
        } else {
            setShapes(shapes.map(s => s.id === shapeId ? { ...s, points: newPoints } : s));
        }

        setContextMenu(null);
    };

    const removeElement = (id: string, type: 'seat' | 'shape' | 'image') => {
        saveToHistory();
        if (type === 'image') {
            setImages(images.filter(img => img.id !== id));
        } else if (type === 'seat') {
            setSeats(seats.filter(s => s.id !== id));
        } else {
            setShapes(shapes.filter(s => s.id !== id));
        }
        setSelectedId(null);
        setSelectedType(null);
        setContextMenu(null);
    };

    const handleImageDrag = (id: string, newX: number, newY: number) => {
        const relX = newX - centerPoint.x;
        const relY = newY - centerPoint.y;
        setImages(images.map(img => img.id === id ? { ...img, x: relX, y: relY } : img));
    };

    const handleImageResize = (id: string, newWidth: number, newHeight: number) => {
        setImages(images.map(img => img.id === id ? { ...img, width: Math.max(50, newWidth), height: Math.max(50, newHeight) } : img));
    };

    const handleImageCornerResize = (id: string, corner: number, newX: number, newY: number, proportional: boolean) => {
        setImages(images.map(img => {
            if (img.id !== id) return img;
            
            if (proportional) {
                // Proportional resize: keep aspect ratio
                const aspectRatio = img.width / img.height;
                const oppositeCorner = (corner + 2) % 4;
                const corners = [
                    { x: 0, y: 0 },
                    { x: img.width, y: 0 },
                    { x: img.width, y: img.height },
                    { x: 0, y: img.height },
                ];
                const anchor = corners[oppositeCorner];
                
                // Calculate distance from anchor to new position
                const dx = newX - anchor.x;
                const dy = newY - anchor.y;
                
                // Determine new size based on larger dimension change while keeping ratio
                let newW, newH;
                if (Math.abs(dx) / aspectRatio > Math.abs(dy)) {
                    newW = Math.abs(dx);
                    newH = newW / aspectRatio;
              } else {
                    newH = Math.abs(dy);
                    newW = newH * aspectRatio;
                }
                newW = Math.max(30, newW);
                newH = Math.max(30, newH);
                
                // Calculate new position based on which corner is being dragged
                let offsetX = 0, offsetY = 0;
                if (corner === 0) { // top-left: anchor is bottom-right
                    offsetX = -newW + img.width;
                    offsetY = -newH + img.height;
                } else if (corner === 1) { // top-right: anchor is bottom-left
                    offsetY = -newH + img.height;
                } else if (corner === 3) { // bottom-left: anchor is top-right
                    offsetX = -newW + img.width;
                }
                // corner === 2 (bottom-right): anchor is top-left, no offset needed
                
                return { ...img, x: img.x + offsetX, y: img.y + offsetY, width: newW, height: newH };
            } else {
                // Non-proportional resize (original behavior)
                const corners = [
                    { x: 0, y: 0 },
                    { x: img.width, y: 0 },
                    { x: img.width, y: img.height },
                    { x: 0, y: img.height },
                ];
                corners[corner] = { x: newX, y: newY };
                const minX = Math.min(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
                const maxX = Math.max(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
                const minY = Math.min(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
                const maxY = Math.max(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
                const newW = Math.max(30, maxX - minX);
                const newH = Math.max(30, maxY - minY);
                const offsetX = minX;
                const offsetY = minY;
                return { ...img, x: img.x + offsetX, y: img.y + offsetY, width: newW, height: newH };
            }
        }));
    };
    
    const handleImageEdgeResize = (id: string, edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => {
        setImages(images.map(img => {
            if (img.id !== id) return img;
            let newX = img.x, newY = img.y, newW = img.width, newH = img.height;
            
            if (edge === 'right') {
                newW = Math.max(30, img.width + delta);
            } else if (edge === 'left') {
                newW = Math.max(30, img.width - delta);
                newX = img.x + delta;
            } else if (edge === 'bottom') {
                newH = Math.max(30, img.height + delta);
            } else if (edge === 'top') {
                newH = Math.max(30, img.height - delta);
                newY = img.y + delta;
            }
            
            return { ...img, x: newX, y: newY, width: newW, height: newH };
        }));
    };

    const handleImageRotate = (id: string, rotation: number) => {
        setImages(images.map(img => img.id === id ? { ...img, rotation } : img));
    };

    const rotateImage = (id: string, degrees: number) => {
        setImages(images.map(img => img.id === id ? { ...img, rotation: ((img.rotation ?? 0) + degrees + 360) % 360 } : img));
        setContextMenu(null);
    };

    const scaleImage = (id: string, factor: number) => {
        setImages(images.map(img => {
            if (img.id !== id) return img;
            const newW = Math.max(50, img.width * factor);
            const newH = Math.max(50, img.height * factor);
            return { ...img, width: newW, height: newH };
        }));
        setContextMenu(null);
    };

    // Rect handlers - mirror image behavior exactly
    const handleRectDrag = (id: string, newX: number, newY: number) => {
        const relX = newX - centerPoint.x;
        const relY = newY - centerPoint.y;
        setShapes(shapes.map(s => {
            if (s.id !== id || (s.type !== 'rect' && s.type !== 'text')) return s;
            const [oldX, oldY, w, h] = s.points;
            const dx = relX - oldX;
            const dy = relY - oldY;
            const updated = { ...s, points: [relX, relY, w, h] };
            // Propagate move to seat rows with explicit positions
            if (updated.seat_block?.section_rows?.length) {
                updated.seat_block = { ...updated.seat_block, section_rows: updated.seat_block.section_rows.map((row: any) => {
                    if (row.posX === undefined && row.posY === undefined) return row;
                    return { ...row, ...(row.posX !== undefined ? { posX: row.posX + dx } : {}), ...(row.posY !== undefined ? { posY: row.posY + dy } : {}) };
                }) };
            }
            return updated;
        }));
    };

    const handleRectCornerResize = (id: string, corner: number, newX: number, newY: number) => {
        setShapes(shapes.map(s => {
            if (s.id !== id || (s.type !== 'rect' && s.type !== 'text')) return s;
            const [x, y, w, h] = s.points;
            
            // Non-proportional resize following cursor direction
            const corners = [
                { x: 0, y: 0 },                  // 0: top-left (local)
                { x: w, y: 0 },                  // 1: top-right
                { x: w, y: h },                  // 2: bottom-right
                { x: 0, y: h },                  // 3: bottom-left
            ];
            
            // Update the dragged corner position
            corners[corner] = { x: newX, y: newY };
            
            // Adjust adjacent corners to maintain rectangle shape
            if (corner === 0) { // top-left dragged
                corners[1].y = newY; // top-right y
                corners[3].x = newX; // bottom-left x
            } else if (corner === 1) { // top-right dragged
                corners[0].y = newY; // top-left y
                corners[2].x = newX; // bottom-right x
            } else if (corner === 2) { // bottom-right dragged
                corners[1].x = newX; // top-right x
                corners[3].y = newY; // bottom-left y
            } else { // bottom-left dragged
                corners[0].x = newX; // top-left x
                corners[2].y = newY; // bottom-right y
            }
            
            // Calculate new rect bounds from corners
            const minX = Math.min(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
            const maxX = Math.max(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
            const minY = Math.min(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
            const maxY = Math.max(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
            
            const newW = Math.max(20, maxX - minX);
            const newH = Math.max(20, maxY - minY);
            
            // Convert local back to world coords
            const updated: Shape = { ...s, points: [x + minX, y + minY, newW, newH] };
            // Propagate resize to seat rows with explicit positions
            if (updated.seat_block?.section_rows?.length && (w > 0) && (h > 0)) {
                const scX = newW / w;
                const scY = newH / h;
                const nxW = x + minX;
                const nyW = y + minY;
                updated.seat_block = { ...updated.seat_block, section_rows: updated.seat_block.section_rows.map((row: any) => {
                    const patch: any = {};
                    if (row.posX !== undefined) patch.posX = nxW + (row.posX - x) * scX;
                    if (row.posY !== undefined) patch.posY = nyW + (row.posY - y) * scY;
                    if (row.width !== undefined) patch.width = row.width * scX;
                    if (row.height !== undefined) patch.height = row.height * scY;
                    if (row.offsetX !== undefined) patch.offsetX = row.offsetX * scX;
                    if (row.offsetY !== undefined) patch.offsetY = row.offsetY * scY;
                    return Object.keys(patch).length > 0 ? { ...row, ...patch } : row;
                }) };
            }
            return updated;
        }));
    };

    const handleRectEdgeResize = (id: string, edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => {
        setShapes(shapes.map(s => {
            if (s.id !== id || (s.type !== 'rect' && s.type !== 'text')) return s;
            const [oldX, oldY, oldW, oldH] = s.points;
            let [newX, newY, newW, newH] = s.points;
            
            if (edge === 'right') {
                newW = Math.max(20, newW + delta);
            } else if (edge === 'left') {
                newW = Math.max(20, newW - delta);
                newX = newX + delta;
            } else if (edge === 'bottom') {
                newH = Math.max(20, newH + delta);
            } else if (edge === 'top') {
                newH = Math.max(20, newH - delta);
                newY = newY + delta;
            }
            
            const updated: Shape = { ...s, points: [newX, newY, newW, newH] };
            // Propagate resize to seat rows with explicit positions
            if (updated.seat_block?.section_rows?.length && oldW > 0 && oldH > 0) {
                const scX = newW / oldW;
                const scY = newH / oldH;
                updated.seat_block = { ...updated.seat_block, section_rows: updated.seat_block.section_rows.map((row: any) => {
                    const patch: any = {};
                    if (row.posX !== undefined) patch.posX = newX + (row.posX - oldX) * scX;
                    if (row.posY !== undefined) patch.posY = newY + (row.posY - oldY) * scY;
                    if (row.width !== undefined) patch.width = row.width * scX;
                    if (row.height !== undefined) patch.height = row.height * scY;
                    if (row.offsetX !== undefined) patch.offsetX = row.offsetX * scX;
                    if (row.offsetY !== undefined) patch.offsetY = row.offsetY * scY;
                    return Object.keys(patch).length > 0 ? { ...row, ...patch } : row;
                }) };
            }
            return updated;
        }));
    };

    const handleTextChange = (id: string, newText: string) => {
        // Measure the new text and expand the text shape so the content always fits
        setShapes(prev => prev.map(s => {
            if (s.id !== id || s.type !== 'text') return s;
            return { ...s, text: newText };
        }));
    };

    // Auto-resize text shape — both width and height to fit content precisely
    const resizeTextShape = (id: string, divWidth: number, divHeight: number) => {
        setShapes(prev => prev.map(s => {
            if (s.id !== id || s.type !== 'text') return s;
            const newW = Math.max(40, Math.ceil(divWidth / zoomScale) + 2);
            const newH = Math.max(22, Math.ceil(divHeight / zoomScale) + 2);
            return { ...s, points: [s.points[0], s.points[1], newW, newH] };
        }));
    };

    const startEditingText = (id: string) => {
        const s = shapes.find(sh => sh.id === id && sh.type === 'text');
        if (!s) return;
        setSelectedId(id);
        setSelectedType('shape');
        setSelectedIds([id]);
        setEditingTextId(id);
    };

    const scaleSeat = (id: string, factor: number) => {
        saveToHistory();
        setSeats(seats.map(s => {
            if (s.id !== id) return s;
            const currentRadius = s.radius || 12;
            const newRadius = Math.max(5, Math.min(100, currentRadius * factor));
            return { ...s, radius: newRadius };
        }));
        setContextMenu(null);
    };

    const duplicateElement = (id: string, type: 'seat' | 'shape' | 'image') => {
        if (type === 'image') return; // Images not duplicatable from context menu
        if (type === 'seat') {
            const seat = seats.find(s => s.id === id);
            if (seat) {
                setSeats([...seats, { ...seat, id: `s-${Date.now()}`, x: seat.x + 30, y: seat.y + 30 }]);
            }
        } else {
            const shape = shapes.find(s => s.id === id);
            if (shape) {
                const offset = shape.points.slice(0, 2).length === 2 ? 30 : 0;
                const newPoints = shape.points.map((p, i) => i % 2 === 0 ? p + offset : p + offset);
                setShapes([...shapes, { ...shape, id: `sh-${Date.now()}`, points: newPoints }]);
            }
        }
        setContextMenu(null);
    };

    const setZLayer = (targetZ: number) => {
        if (!selectedId) return;
        const toggle = (z?: number) => z === targetZ ? 500 : targetZ;
        if (selectedType === 'seat') setSeats(seats.map(s => s.id === selectedId ? { ...s, zIndex: toggle(s.zIndex) } : s));
        else setShapes(shapes.map(s => s.id === selectedId ? { ...s, zIndex: toggle(s.zIndex) } : s));
        setContextMenu(null);
    };

    const openColorPicker = (id: string, type: 'seat' | 'shape' | 'image') => {
        if (type === 'image') return;
        const el = type === 'seat' ? seats.find(s => s.id === id) : shapes.find(s => s.id === id);
        setSelectedColor(el?.color || '#ff0000');
        setSelectedOpacity(el?.opacity ?? 1);
        setColorPickerOpen(true);
    };

    const applyColor = (color: string) => {
        const idsToApply = selectedIds.length > 1 ? selectedIds : (selectedId ? [selectedId] : []);
        if (idsToApply.length === 0) return;
        saveToHistory();
        if (selectedType === 'seat') {
            setSeats(prev => prev.map(s => idsToApply.includes(s.id) ? { ...s, color, opacity: selectedOpacity } : s));
        } else {
            setShapes(prev => prev.map(s => idsToApply.includes(s.id) ? { ...s, color, opacity: selectedOpacity } : s));
        }
        setColorPickerOpen(false);
        setContextMenu(null);
    };

    const openPropsDialog = (id: string, type: 'seat' | 'shape' | 'image') => {
        if (type === 'image') return; // Images don't have props dialog
        editPropsTargetRef.current = { id, type };
        if (type === 'seat') {
            const seat = seats.find(s => s.id === id);
            setEditingProps({ name: '', description: '', is_seat_block: !!seat?.seat_block, block: seat?.seat_block ? { ...seat.seat_block } : { id: seat?.id || '', gate: '', name: '', zone: '', level: '', capacity: '', category: '', is_seat_specific: false, section_rows: [] } });
        } else {
            const shape = shapes.find(s => s.id === id);
            setEditingProps({
                name: '',
                description: '',
                is_seat_block: !!shape?.seat_block,
                block: shape?.seat_block ? { ...shape.seat_block } : { id: shape?.id || '', gate: '', name: '', zone: '', level: '', capacity: '', category: '', is_seat_specific: false, section_rows: [] }
            });
        }
        setEditPropsOpen(true);
    };

    const openAddRowDialog = (shapeId: string) => {
        const shape = shapes.find(s => s.id === shapeId);
        if (!shape) return;
        const sectionId = shape.seat_block?.id || shape.id;
        const rows = (shape.seat_block?.section_rows || []).map((r: any) => ({ id: r.id || '', name: r.name || '', seatsCount: (r.row_seats?.length) ?? 0 }));
        setAddRowForShapeId(shapeId);
        setExistingRows(rows);
        // prefills currentRow: if no existing rows, show single input prefilled id; otherwise prefill next id
        if (rows.length === 0) {
            setCurrentRow({ id: `${sectionId}-r-1`, name: '', seatsCount: 0 });
            setEditingIndex(null);
        } else {
            // next candidate id
            let nextIndex = 1;
            const existingIds = rows.map(r => r.id).filter(Boolean);
            while (existingIds.includes(`${sectionId}-r-${nextIndex}`)) nextIndex++;
            setCurrentRow({ id: `${sectionId}-r-${nextIndex}`, name: '', seatsCount: 0 });
            setEditingIndex(null);
        }
        setAddRowOpen(true);
    };

    const saveRows = (rowsArg?: { id: string; name?: string; seatsCount: number }[], closeModal: boolean = true) => {
        if (!addRowForShapeId) return;
        const targetShapeId = addRowForShapeId;
        const rowsToPersist = rowsArg ? rowsArg.slice() : existingRows.slice();
        setShapes(prev => prev.map(s => {
            if (s.id !== targetShapeId) return s;
            const base = { ...s } as Shape;
            if (!base.seat_block) base.seat_block = { id: s.id, gate: '', name: '', zone: '', level: '', capacity: undefined, category: '', is_seat_specific: false, section_rows: [] };
            if (!base.seat_block.section_rows) base.seat_block.section_rows = [];

            // Build a map of existing rows by id for preservation
            const existingById: Record<string, any> = {};
            for (const r of base.seat_block.section_rows) existingById[r.id] = r;

            const newSectionRows: any[] = [];
            const sectionId = base.seat_block.id || base.id;

            for (let i = 0; i < rowsToPersist.length; i++) {
                const row = rowsToPersist[i];
                const rid = (row.id || '').trim();
                if (!rid) continue; // skip empty ids
                const seatsCount = Math.max(0, Math.floor(row.seatsCount || 0));

                // Always generate the canonical seat list for this row.
                // seat_id = "{row-id}-s-{position}", num = position (1-based).
                const rowSeats: any[] = [];
                for (let si = 0; si < seatsCount; si++) {
                    rowSeats.push({ seat_id: `${rid}-s-${si + 1}`, num: String(si + 1) });
                }
                // Preserve visual layout properties from existing row if it exists
                const existing = existingById[rid];
                const visualProps: Record<string, any> = {};
                if (existing) {
                    for (const key of ['offsetX', 'offsetY', 'rotation', 'bend', 'width', 'height', 'posX', 'posY', 'points']) {
                        if (existing[key] !== undefined) visualProps[key] = existing[key];
                    }
                }
                newSectionRows.push({ id: rid, name: row.name || '', row_seats: rowSeats, ...visualProps });
            }

            base.seat_block.section_rows = newSectionRows;
            return base;
        }));

        if (closeModal) {
            setAddRowOpen(false);
            setAddRowForShapeId(null);
            setExistingRows([]);
            setCurrentRow({ id: '', name: '', seatsCount: 0 });
            setEditingIndex(null);
            setContextMenu(null);
        } else {
            // when not closing modal, update existingRows to reflect persisted rows (no placeholders)
            setExistingRows(rowsToPersist.slice());
            setEditingIndex(null);
            // reset currentRow to next available id so re-saving doesn't duplicate
            const sectionId = (shapes.find(s => s.id === targetShapeId)?.seat_block?.id) || targetShapeId;
            const usedIds = rowsToPersist.map(r => r.id);
            let nextIdx = 1;
            while (usedIds.includes(`${sectionId}-r-${nextIdx}`)) nextIdx++;
            setCurrentRow({ id: `${sectionId}-r-${nextIdx}`, name: '', seatsCount: 0 });
        }
    };


    const applyProps = () => {
        const { id: _selectedId, type: _selectedType } = editPropsTargetRef.current;
        if (!_selectedId) return;
        if (_selectedType === 'seat') {
            // Name/Description are no longer edited here
            setSeats(prev => prev.map(s => s.id === _selectedId ? { ...s } : s));
        } else {
            setShapes(prev => prev.map(s => {
                if (s.id !== _selectedId) return s;
                const base = { ...s } as Shape;
                if (editingProps.is_seat_block) {
                    base.seat_block = {
                        id: editingProps.block?.id || '',
                        gate: editingProps.block?.gate || '',
                        name: editingProps.block?.name || '',
                        zone: editingProps.block?.zone || '',
                        level: editingProps.block?.level || '',
                        capacity: typeof editingProps.block?.capacity === 'number' ? editingProps.block!.capacity : (editingProps.block?.capacity === '' ? undefined : Number(editingProps.block?.capacity)),
                        category: editingProps.block?.category || '',
                        is_seat_specific: !!editingProps.block?.is_seat_specific,
                        section_rows: s.seat_block?.section_rows || editingProps.block?.section_rows || [],
                    };
                } else {
                    // remove seat_block if present
                    if (base.seat_block) delete base.seat_block;
                }
                return base;
            }));
        }
        setEditPropsOpen(false);
        setContextMenu(null);
    };

    const handlePointDragStart = (index: number) => {
        setDraggingPointIndex(index);
    };

    const handlePointDrag = (index: number, newX: number, newY: number) => {
        if (selectedType !== 'shape' || !selectedId) return;
        const shape = shapes.find(s => s.id === selectedId);
        if (!shape) return;

        // Convert absolute coordinates to relative coordinates
        const relX = newX - centerPoint.x;
        const relY = newY - centerPoint.y;

        let newPoints = [...shape.points];

        if (shape.type === 'rect') {
            // For rectangles, non-proportional resize following cursor direction
            const [x, y, w, h] = shape.points;
            const corners = [
                { x, y },                    // 0: top-left
                { x: x + w, y },             // 1: top-right
                { x: x + w, y: y + h },      // 2: bottom-right
                { x, y: y + h }              // 3: bottom-left
            ];
            
            // Update the dragged corner position
            corners[index] = { x: relX, y: relY };
            
            // Adjust adjacent corners to maintain rectangle shape
            if (index === 0) { // top-left dragged
                corners[1].y = relY; // top-right y
                corners[3].x = relX; // bottom-left x
            } else if (index === 1) { // top-right dragged
                corners[0].y = relY; // top-left y
                corners[2].x = relX; // bottom-right x
            } else if (index === 2) { // bottom-right dragged
                corners[1].x = relX; // top-right x
                corners[3].y = relY; // bottom-left y
            } else { // bottom-left dragged
                corners[0].x = relX; // top-left x
                corners[2].y = relY; // bottom-right y
            }
            
            // Calculate new rect bounds from corners
            const minX = Math.min(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
            const maxX = Math.max(corners[0].x, corners[1].x, corners[2].x, corners[3].x);
            const minY = Math.min(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
            const maxY = Math.max(corners[0].y, corners[1].y, corners[2].y, corners[3].y);
            
            const newW = Math.max(20, maxX - minX);
            const newH = Math.max(20, maxY - minY);
            
            newPoints = [minX, minY, newW, newH];
        } else if (shape.type === 'trapezoid') {
            // For trapezoid, move individual point (no proportional resize)
            newPoints[index * 2] = relX;
            newPoints[index * 2 + 1] = relY;
        } else if (shape.type === 'custom' && shape.holeRadius !== undefined) {
            // Custom shape with circular hole: only outer points are editable
            // Inner circle stays as fixed circular hole
            newPoints[index * 2] = relX;
            newPoints[index * 2 + 1] = relY;
        } else if (shape.type === 'group') {
            // Group shape: proportional resize keeping aspect ratio and scale all children
            // Use functional updater to always read latest state
            setShapes(prev => {
                const curShape = prev.find(s => s.id === selectedId);
                if (!curShape || curShape.type !== 'group') return prev;
                const [gx, gy, gw, gh] = curShape.points;
                const aspectRatio = gw / gh;
                const oppositeIndex = (index + 2) % 4;
                const corners = [
                    { x: gx, y: gy },
                    { x: gx + gw, y: gy },
                    { x: gx + gw, y: gy + gh },
                    { x: gx, y: gy + gh }
                ];
                const anchor = corners[oppositeIndex];
                const dx = relX - anchor.x;
                const dy = relY - anchor.y;
                let newW: number, newH: number;
                if (Math.abs(dx) / aspectRatio > Math.abs(dy)) {
                    newW = Math.abs(dx);
                    newH = newW / aspectRatio;
                } else {
                    newH = Math.abs(dy);
                    newW = newH * aspectRatio;
                }
                newW = Math.max(30, newW);
                newH = Math.max(30, newH);
                let newMinX = gx, newMinY = gy;
                if (index === 0) { newMinX = anchor.x - newW; newMinY = anchor.y - newH; }
                else if (index === 1) { newMinX = anchor.x; newMinY = anchor.y - newH; }
                else if (index === 2) { newMinX = anchor.x; newMinY = anchor.y; }
                else { newMinX = anchor.x - newW; newMinY = anchor.y; }
                const scale = newW / gw;
                const curChildIds = curShape.childIds || [];
                const seatRowChildIds = curChildIds.filter(cid => cid.startsWith('seatrow-'));
                let updated = prev.map(s => {
                    if (s.id === selectedId) {
                        return { ...s, points: [newMinX, newMinY, newW, newH] };
                    }
                    if (curChildIds.includes(s.id)) {
                        if (s.type === 'rect' || s.type === 'group' || s.type === 'text') {
                            return { ...s, points: [
                                newMinX + (s.points[0] - gx) * scale,
                                newMinY + (s.points[1] - gy) * scale,
                                s.points[2] * scale,
                                s.points[3] * scale
                            ], ...(s.type === 'text' ? { fontSize: Math.max(8, Math.round((s.fontSize || 16) * scale)) } : {}) };
                        } else if (s.type === 'circle') {
                            return { ...s, cx: newMinX + ((s.cx || 0) - gx) * scale, cy: newMinY + ((s.cy || 0) - gy) * scale, r: (s.r || 50) * scale };
                        } else {
                            const newPts = s.points.map((p, i) =>
                                i % 2 === 0 ? newMinX + (p - gx) * scale : newMinY + (p - gy) * scale
                            );
                            let newHoleX = s.holeX, newHoleY = s.holeY, newHoleR = s.holeRadius;
                            if (s.holeX !== undefined && s.holeY !== undefined) {
                                newHoleX = newMinX + (s.holeX - gx) * scale;
                                newHoleY = newMinY + (s.holeY - gy) * scale;
                                if (s.holeRadius) newHoleR = s.holeRadius * scale;
                            }
                            return { ...s, points: newPts, holeX: newHoleX, holeY: newHoleY, holeRadius: newHoleR };
                        }
                    }
                    return s;
                });
                // Also scale seatrow children
                seatRowChildIds.forEach((srId: string) => {
                    const parsed = parseSeatRowId(srId);
                    if (!parsed) return;
                    updated = updated.map(s => {
                        if (s.id !== parsed.shapeId || !s.seat_block) return s;
                        const updRows = [...(s.seat_block.section_rows || [])];
                        if (!updRows[parsed.rowIdx]) return s;
                        const row = updRows[parsed.rowIdx];
                        const shapeBbox = getShapeBBox(s);
                        const seatDiam = (row.seatRadius ?? SEAT_DOT_RADIUS) * 2;
                        const natRowH = seatDiam + ROW_PADDING * 2;
                        const baseGroupY = shapeBbox.y + natRowH * parsed.rowIdx;
                        const rowPosX = (row.posX ?? shapeBbox.x) + (row.offsetX ?? 0);
                        const rowPosY = (row.posY ?? baseGroupY) + (row.offsetY ?? 0);
                        const childRelX = rowPosX - gx;
                        const childRelY = rowPosY - gy;
                        const newOx = newMinX + childRelX * scale - (row.posX ?? shapeBbox.x);
                        const newOy = newMinY + childRelY * scale - (row.posY ?? baseGroupY);
                        const rW = row.width ?? (24 + (row.row_seats || []).length * seatDiam + ((row.row_seats || []).length - 1) * SEAT_GAP + ROW_PADDING * 2);
                        const rH = row.height ?? natRowH;
                        updRows[parsed.rowIdx] = { ...row, offsetX: newOx, offsetY: newOy, width: rW * scale, height: rH * scale };
                        return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
                    });
                });
                return updated;
            });
            return;
        } else {
            // For other shapes (line, trapezoid, custom), update the specific point pair
            newPoints[index * 2] = relX;
            newPoints[index * 2 + 1] = relY;
        }

        setShapes(shapes.map(s => s.id === selectedId ? { ...s, points: newPoints } : s));
    };

    const handlePointDragEnd = () => {
        setDraggingPointIndex(null);
    };

    const handleShapeDrag = (id: string, newX: number, newY: number) => {
        if (selectedType !== 'shape' || !selectedId) return;
        const shape = shapes.find(s => s.id === id);
        if (!shape) return;

        // Rect: e.target.x/y is absolute canvas position
        const relX = newX - centerPoint.x;
        const relY = newY - centerPoint.y;
        const newPoints = [...shape.points];
        newPoints[0] = relX;
        newPoints[1] = relY;
        setShapes(shapes.map(s => s.id === id ? { ...s, points: newPoints } : s));
    };

    // Line / trapezoid / custom: commit drag delta (called once at onDragEnd)
    const handleShapeDragDelta = (id: string, dx: number, dy: number) => {
        setShapes(prev => prev.map(s => {
            if (s.id !== id) return s;
            const newPoints = [...s.points];
            for (let i = 0; i < newPoints.length; i += 2) {
                newPoints[i] += dx;
                newPoints[i + 1] += dy;
            }
            const updated: Shape = {
                ...s,
                points: newPoints,
                holeX: s.holeX !== undefined ? s.holeX + dx : s.holeX,
                holeY: s.holeY !== undefined ? s.holeY + dy : s.holeY,
            };
            // Propagate move to seat rows with explicit positions
            if (updated.seat_block?.section_rows?.length) {
                updated.seat_block = { ...updated.seat_block, section_rows: updated.seat_block.section_rows.map((row: any) => {
                    if (row.posX === undefined && row.posY === undefined) return row;
                    return { ...row, ...(row.posX !== undefined ? { posX: row.posX + dx } : {}), ...(row.posY !== undefined ? { posY: row.posY + dy } : {}) };
                }) };
            }
            return updated;
        }));
    };

    // Group drag: apply same delta to all shapes in selectedIds
    const handleGroupDragEnd = (shapeType: string) => (e: any) => {
        const init = groupDragInitRef.current || { x: 0, y: 0 };
        let dx: number, dy: number;
        const isDelta = shapeType === 'line' || shapeType === 'trapezoid' || shapeType === 'custom';
        if (isDelta) {
            dx = e.target.x(); dy = e.target.y();
            e.target.position({ x: 0, y: 0 });
        } else {
            dx = e.target.x() - init.x; dy = e.target.y() - init.y;
            e.target.position({ x: init.x, y: init.y });
        }
        groupDragInitRef.current = null;
        // Move all selected shapes (regular shapes)
        const seatRowIdsInSelection = selectedIds.filter(id => id.startsWith('seatrow-'));
        setShapes(prev => {
            let updated = prev.map(s => {
                if (!selectedIds.includes(s.id)) return s;
                let moved: Shape;
                if (s.type === 'rect') {
                    moved = { ...s, points: [s.points[0]+dx, s.points[1]+dy, s.points[2], s.points[3]] };
                } else if (s.type === 'circle') {
                    moved = { ...s, cx: (s.cx||0)+dx, cy: (s.cy||0)+dy };
                } else {
                    const newPoints = [...s.points];
                    for (let i = 0; i < newPoints.length; i += 2) { newPoints[i] += dx; newPoints[i+1] += dy; }
                    moved = { ...s, points: newPoints,
                        holeX: s.holeX !== undefined ? s.holeX+dx : s.holeX,
                        holeY: s.holeY !== undefined ? s.holeY+dy : s.holeY,
                    };
                }
                // Propagate move to seat rows with explicit positions
                if (moved.seat_block?.section_rows?.length) {
                    moved.seat_block = { ...moved.seat_block, section_rows: moved.seat_block.section_rows.map((row: any) => {
                        if (row.posX === undefined && row.posY === undefined) return row;
                        return { ...row, ...(row.posX !== undefined ? { posX: row.posX + dx } : {}), ...(row.posY !== undefined ? { posY: row.posY + dy } : {}) };
                    }) };
                }
                return moved;
            });
            // Also move selected seatrow children by updating their offsets
            seatRowIdsInSelection.forEach(srId => {
                const parsed = parseSeatRowId(srId);
                if (!parsed) return;
                updated = updated.map(s => {
                    if (s.id !== parsed.shapeId || !s.seat_block) return s;
                    const updRows = [...(s.seat_block.section_rows || [])];
                    if (!updRows[parsed.rowIdx]) return s;
                    const row = updRows[parsed.rowIdx];
                    updRows[parsed.rowIdx] = { ...row, offsetX: (row.offsetX ?? 0) + dx, offsetY: (row.offsetY ?? 0) + dy };
                    return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
                });
            });
            return updated;
        });
    };

    // ── Mobile long-press → context menu ──────────────────────────────────────
    const startLongPress = (_e: any, _targetId: string, _targetType: 'seat' | 'shape' | 'image') => {
        // Touch long-press context menu disabled — only right-click opens context menu.
        return;
    };
    const endLongPress = () => {
        if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    };

    // ── Mouse long-press (click and hold) → context menu ─────────────────────
    const startMouseLongPress = (e: any, targetId: string, targetType: 'seat' | 'shape' | 'image') => {
        if (activeTool) return;
        // Only trigger on right-click (button 2)
        if (e.evt?.button !== 2) return;
        
        mouseLongPressTimerRef.current = setTimeout(() => {
            // First select the shape/seat/image
            setSelectedId(targetId);
            setSelectedType(targetType);
            setSelectedIds([targetId]);
            // Then open the context menu directly
            if (!stageContainerRef.current?.getBoundingClientRect()) return;
            const pos = calcMenuPos(e.evt?.clientX || 100, e.evt?.clientY || 100);
            setContextMenu({ x: pos.x, y: pos.y, targetId, targetType });
        }, 500);
    };
    const endMouseLongPress = () => {
        if (mouseLongPressTimerRef.current) { clearTimeout(mouseLongPressTimerRef.current); mouseLongPressTimerRef.current = null; }
    };

    // ── Stage panning: right-click drag or touch long-press drag to pan ──────
    const handlePanStart = (e: any) => {
        // Start panning on right-click or when pan mode is active
        const evt = e.evt;
        if (evt && evt.button === 2) { // Right-click
            evt.preventDefault();
            const stage = e.target.getStage();
            const pointer = stage?.getPointerPosition();
            if (pointer) {
                setIsPanning(true);
                panStartRef.current = { x: pointer.x, y: pointer.y, offsetX: panOffset.x, offsetY: panOffset.y };
            }
        }
    };
    
    const handlePanMove = (e: any) => {
        if (!isPanning || !panStartRef.current) return;
        const stage = e.target.getStage();
        const pointer = stage?.getPointerPosition();
        if (!pointer) return;
        
        const dx = (pointer.x - panStartRef.current.x) / zoomScale;
        const dy = (pointer.y - panStartRef.current.y) / zoomScale;
        setPanOffset({
            x: panStartRef.current.offsetX + dx,
            y: panStartRef.current.offsetY + dy,
        });
    };
    
    const handlePanEnd = () => {
        setIsPanning(false);
        panStartRef.current = null;
    };

    // ── Marquee selection: drag on empty area to select multiple shapes ──────
    const handleMarqueeStart = (e: any) => {
        // Check for right-click pan first
        const evt = e.evt;
        if (evt && evt.button === 2) {
            evt.preventDefault();
            evt.stopPropagation();
            handlePanStart(e);
            return;
        }
        // Only start marquee if no tool active and clicking on empty area
        if (activeTool) return;
        // Check if clicking on a shape/seat/image - if so, don't start marquee
        const target = e.target;
        const stage = e.target.getStage();
        if (target !== stage) return; // Clicked on a shape, not background
        
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        // Convert to world coordinates (accounting for zoom and center offset)
        const worldX = (pointer.x - stageSize.width / 2) / zoomScale + stageSize.width / 2 - centerPoint.x;
        const worldY = (pointer.y - stageSize.height / 2) / zoomScale + stageSize.height / 2 - centerPoint.y;
        isMarqueeRef.current = true;
        setIsMarqueeSelecting(true);
        setMarqueeStart({ x: worldX, y: worldY });
        setMarqueeRect({ x: worldX, y: worldY, width: 0, height: 0 });
    };

    // ── Helper: find all shapes whose bbox intersects a given rect ──────────
    const getShapesInRect = (rect: { x: number; y: number; width: number; height: number }): string[] => {
        const ids: string[] = [];
        shapes.forEach(shape => {
            let bbox: { x: number; y: number; w: number; h: number } | null = null;
            if (shape.type === 'rect' || shape.type === 'text' || shape.type === 'group') {
                const [x, y, w, h] = shape.points;
                bbox = { x, y, w, h };
            } else if (shape.type === 'circle') {
                const cx = shape.cx || 0, cy = shape.cy || 0, r = shape.r || 50;
                bbox = { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
            } else {
                const xs = shape.points.filter((_: number, i: number) => i % 2 === 0);
                const ys = shape.points.filter((_: number, i: number) => i % 2 !== 0);
                if (xs.length === 0 || ys.length === 0) return;
                const minX = Math.min(...xs), maxX = Math.max(...xs);
                const minY = Math.min(...ys), maxY = Math.max(...ys);
                bbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
            }
            if (bbox) {
                const intersects = (
                    bbox.x < rect.x + rect.width &&
                    bbox.x + bbox.w > rect.x &&
                    bbox.y < rect.y + rect.height &&
                    bbox.y + bbox.h > rect.y
                );
                if (intersects) ids.push(shape.id);
            }
        });
        // Also check seat rows for intersection
        shapes.forEach(shape => {
            if (!shape.seat_block) return;
            const rows = shape.seat_block.section_rows || [];
            if (rows.length === 0) return;
            const shapeBbox = getShapeBBox(shape);
            const LABEL_W = 24;
            const defaultSeatDiam = SEAT_DOT_RADIUS * 2;
            const defaultNatRowH = defaultSeatDiam + ROW_PADDING * 2;
            rows.forEach((row: any, rowIdx: number) => {
                const rowSeats = row.row_seats || [];
                if (rowSeats.length === 0) return;
                const seatDiam = (row.seatRadius ?? SEAT_DOT_RADIUS) * 2;
                const natRowH = seatDiam + ROW_PADDING * 2;
                const rowId = row.id || `row-${rowIdx}`;
                const seatRowId = `seatrow-${shape.id}-${rowId}`;
                const natRowW = LABEL_W + rowSeats.length * seatDiam + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                const baseGroupY = shapeBbox.y + defaultNatRowH * rowIdx;
                const gw = row.width ?? natRowW;
                const gh = row.height ?? natRowH;
                const gx = (row.posX ?? shapeBbox.x) + (row.offsetX ?? 0);
                const gy = (row.posY ?? baseGroupY) + (row.offsetY ?? 0);
                if (gx < rect.x + rect.width && gx + gw > rect.x &&
                    gy < rect.y + rect.height && gy + gh > rect.y) {
                    ids.push(seatRowId);
                }
            });
        });
        return ids;
    };

    const handleMarqueeMove = (e: any) => {
        // Handle panning
        if (isPanning) {
            handlePanMove(e);
            return;
        }
        if (!isMarqueeSelecting || !marqueeStart) return;
        const stage = e.target.getStage();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        // Convert to world coordinates
        const worldX = (pointer.x - stageSize.width / 2) / zoomScale + stageSize.width / 2 - centerPoint.x;
        const worldY = (pointer.y - stageSize.height / 2) / zoomScale + stageSize.height / 2 - centerPoint.y;
        const x = Math.min(marqueeStart.x, worldX);
        const y = Math.min(marqueeStart.y, worldY);
        const width = Math.abs(worldX - marqueeStart.x);
        const height = Math.abs(worldY - marqueeStart.y);
        const newRect = { x, y, width, height };
        setMarqueeRect(newRect);

        // Live highlight: update selectedIds as the marquee is dragged
        if (width > 5 || height > 5) {
            const intersecting = getShapesInRect(newRect);
            setSelectedIds(intersecting);
            if (intersecting.length > 0) {
                setSelectedId(intersecting[0]);
                setSelectedType('shape');
            }
        }
    };

    const handleMarqueeEnd = () => {
        // Helper: clear the synchronous marquee ref after all Konva events finish
        const clearMarqueeRef = () => { Promise.resolve().then(() => { isMarqueeRef.current = false; }); };
        // Handle pan end
        if (isPanning) {
            handlePanEnd();
            clearMarqueeRef();
            return;
        }
        // If we have an active, valid marquee (large enough), ALWAYS process it.
        // This takes priority over shapeClickedRef/isDraggingRef which can be
        // set accidentally when the marquee passes over interactive elements.
        const hasValidMarquee = isMarqueeSelecting && marqueeRect &&
            (marqueeRect.width >= 5 || marqueeRect.height >= 5);
        if (hasValidMarquee) {
            // Clear any stale flags that were set during the marquee drag
            isDraggingRef.current = false;
            shapeClickedRef.current = false;
        } else {
            // No valid marquee — check if we were dragging/clicking a shape
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                setIsMarqueeSelecting(false);
                setMarqueeStart(null);
                setMarqueeRect(null);
                clearMarqueeRef();
                return;
            }
            if (shapeClickedRef.current) {
                shapeClickedRef.current = false;
                setIsMarqueeSelecting(false);
                setMarqueeStart(null);
                setMarqueeRect(null);
                clearMarqueeRef();
                return;
            }
        }
        if (!isMarqueeSelecting || !marqueeRect) {
            setIsMarqueeSelecting(false);
            setMarqueeStart(null);
            setMarqueeRect(null);
            clearMarqueeRef();
            return;
        }
        // Ignore tiny marquees from single clicks — treat as a click on empty stage, deselect
        if (marqueeRect.width < 5 && marqueeRect.height < 5) {
            setSelectedIds([]);
            setSelectedId(null);
            setSelectedType(null);
            setEditingTextId(null);
            setIsMarqueeSelecting(false);
            setMarqueeStart(null);
            setMarqueeRect(null);
            clearMarqueeRef();
            return;
        }
        // Find all shapes that are within or intersecting the marquee rect
        const selectedShapeIds = getShapesInRect(marqueeRect);
        
        if (selectedShapeIds.length > 0) {
            setSelectedIds(selectedShapeIds);
            setSelectedId(selectedShapeIds[0]);
            setSelectedType('shape');
            // Show selection menu if multiple shapes selected
            if (selectedShapeIds.length > 1) {
                // Position menu at center of marquee rect
                const menuX = (marqueeRect.x + marqueeRect.width / 2) + centerPoint.x;
                const menuY = (marqueeRect.y + marqueeRect.height / 2) + centerPoint.y;
                // Convert to screen coordinates
                const stageRect = stageContainerRef.current?.getBoundingClientRect();
                if (stageRect) {
                    const screenX = stageRect.left + (stageSize.width / 2) + (menuX - stageSize.width / 2) * zoomScale;
                    const screenY = stageRect.top + (stageSize.height / 2) + (menuY - stageSize.height / 2) * zoomScale;
                    setSelectionMenu({ x: Math.min(screenX, window.innerWidth - 200), y: Math.min(screenY, window.innerHeight - 200) });
                }
            }
        } else {
            setSelectedIds([]);
            setSelectedId(null);
            setSelectedType(null);
        }
        
        // Keep isMarqueeRef.current = true until after Konva finishes dispatching
        // all events for this pointer-up cycle (mouseup → click).  Clear it
        // asynchronously so that any click handler that fires synchronously
        // right after this still sees the flag as true.
        clearMarqueeRef();
        setIsMarqueeSelecting(false);
        setMarqueeStart(null);
        setMarqueeRect(null);
    };

    // ── Group selected shapes into a bounding rect ────────────────────────────
    const groupSelectedShapes = () => {
        if (selectedIds.length < 2) return;
        saveToHistory();
        
        // Compute bounding box of all selected shapes (including seat rows)
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        selectedIds.forEach(id => {
            let bbox: { x: number; y: number; w: number; h: number } | null = null;

            // Handle seatrow IDs (e.g. "seatrow-{shapeId}-{rowId}")
            if (id.startsWith('seatrow-')) {
                const parsed = parseSeatRowId(id);
                if (parsed) {
                    const sh = shapes.find(s => s.id === parsed.shapeId);
                    if (sh?.seat_block?.section_rows?.[parsed.rowIdx]) {
                        const row = sh.seat_block.section_rows[parsed.rowIdx];
                        const shapeBbox = getShapeBBox(sh);
                        const rowSeats = row.row_seats || [];
                        const seatDiam = (row.seatRadius ?? SEAT_DOT_RADIUS) * 2;
                        const LABEL_W = 24;
                        const natRowW = LABEL_W + rowSeats.length * seatDiam + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                        const natRowH = seatDiam + ROW_PADDING * 2;
                        const baseGroupY = shapeBbox.y + natRowH * parsed.rowIdx;
                        bbox = {
                            x: (row.posX ?? shapeBbox.x) + (row.offsetX ?? 0),
                            y: (row.posY ?? baseGroupY) + (row.offsetY ?? 0),
                            w: row.width ?? natRowW,
                            h: row.height ?? natRowH,
                        };
                    }
                }
            } else {
                const shape = shapes.find(s => s.id === id);
                if (!shape) return;
                if (shape.type === 'rect' || shape.type === 'text' || shape.type === 'group') {
                    const [x, y, w, h] = shape.points;
                    bbox = { x, y, w, h };
                } else if (shape.type === 'circle') {
                    const cx = shape.cx || 0, cy = shape.cy || 0, r = shape.r || 50;
                    bbox = { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
                } else {
                    const xs = shape.points.filter((_, i) => i % 2 === 0);
                    const ys = shape.points.filter((_, i) => i % 2 !== 0);
                    if (xs.length > 0 && ys.length > 0) {
                        bbox = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
                    }
                }
            }
            if (bbox) {
                minX = Math.min(minX, bbox.x);
                minY = Math.min(minY, bbox.y);
                maxX = Math.max(maxX, bbox.x + bbox.w);
                maxY = Math.max(maxY, bbox.y + bbox.h);
            }
        });
        
        // Add some padding
        const padding = 5;
        minX -= padding; minY -= padding; maxX += padding; maxY += padding;
        
        // Create a group shape (rect) that contains all selected shapes
        const groupId = `group-${Date.now()}`;
        const groupShape: Shape = {
            id: groupId,
            type: 'group',
            points: [minX, minY, maxX - minX, maxY - minY],
            color: '#6464ff',
            strokeColor: '#4488ff',
            strokeWidth: 2,
            zIndex: 400,
            childIds: [...selectedIds],
        };
        
        // Mark child shapes with groupId (regular shapes + seatrow groupId on row data)
        const seatRowIds = selectedIds.filter(id => id.startsWith('seatrow-'));
        let updatedShapes = shapes.map(s =>
            selectedIds.includes(s.id) ? { ...s, groupId } : s
        );
        // Also mark seatrow children with groupId on their row data
        seatRowIds.forEach(srId => {
            const parsed = parseSeatRowId(srId);
            if (!parsed) return;
            updatedShapes = updatedShapes.map(s => {
                if (s.id !== parsed.shapeId || !s.seat_block) return s;
                const updRows = [...(s.seat_block.section_rows || [])];
                if (!updRows[parsed.rowIdx]) return s;
                updRows[parsed.rowIdx] = { ...updRows[parsed.rowIdx], groupId };
                return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
            });
        });
        
        setShapes([...updatedShapes, groupShape]);
        setSelectedId(groupId);
        setSelectedType('shape');
        setSelectedIds([groupId]);
        setSelectionMenu(null);
    };

    // ── Ungroup a group shape ─────────────────────────────────────────────────
    const ungroupShape = (groupId: string) => {
        const group = shapes.find(s => s.id === groupId && s.type === 'group');
        if (!group || !group.childIds) return;
        saveToHistory();
        
        // Remove groupId from children and remove the group shape
        let updatedShapes = shapes
            .filter(s => s.id !== groupId)
            .map(s => group.childIds!.includes(s.id) ? { ...s, groupId: undefined } : s);
        
        // Also clear groupId from seatrow children
        const seatRowChildIds = group.childIds!.filter(id => id.startsWith('seatrow-'));
        seatRowChildIds.forEach(srId => {
            const parsed = parseSeatRowId(srId);
            if (!parsed) return;
            updatedShapes = updatedShapes.map(s => {
                if (s.id !== parsed.shapeId || !s.seat_block) return s;
                const updRows = [...(s.seat_block.section_rows || [])];
                if (!updRows[parsed.rowIdx]) return s;
                const { groupId: _, ...rest } = updRows[parsed.rowIdx];
                updRows[parsed.rowIdx] = rest;
                return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
            });
        });
        
        setShapes(updatedShapes);
        setSelectedId(null);
        setSelectedType(null);
        setSelectedIds([]);
        setContextMenu(null);
    };

    // ── Rotate a shape by `degrees` around its own centre ─────────────────────
    const rotateShape = (id: string, degrees: number) => {
        saveToHistory();
        const rad = degrees * Math.PI / 180;
        setShapes(prev => {
            const target = prev.find(s => s.id === id);
            if (!target) return prev;

            // Group rotation: rigidly rotate every child (shape geometry + seat
            // rows) around the group centre, then refit the group box. Rect children
            // become rotated quads (custom polygons) instead of growing axis-aligned
            // bounding boxes — that AABB behaviour was what made groups "expand"
            // instead of turning.
            if (target.type === 'group') {
                const [gx, gy, gw, gh] = target.points;
                const cx = gx + gw / 2, cy = gy + gh / 2;
                const childSet = new Set(target.childIds || []);
                const rotPt = (px: number, py: number) => ({
                    x: cx + (px - cx) * Math.cos(rad) - (py - cy) * Math.sin(rad),
                    y: cy + (px - cx) * Math.sin(rad) + (py - cy) * Math.cos(rad),
                });
                const addDeg = (cur?: number) => ((((cur ?? 0) + degrees) % 360) + 360) % 360;
                const rowBoxes: { anchorX: number; anchorY: number; w: number; h: number; rotDeg: number }[] = [];

                let updated = prev.map(s => {
                    const which = rowsInGroup(s, childSet);
                    // Rotate participating seat rows: pin posX/posY to the rotated
                    // anchor (decoupling from the parent bbox) and bump the row's own
                    // rotation so its seats turn too.
                    let nextSeatBlock = s.seat_block;
                    if (which && s.seat_block) {
                        const pbb = getShapeBBox(s);
                        const newRows = s.seat_block.section_rows!.map((r: any, i: number) => {
                            if (which !== 'all' && !which.has(i)) return r;
                            const g = getRowAnchorGeom(pbb, r, i);
                            const na = rotPt(g.anchorX, g.anchorY);
                            const newRot = addDeg(r.rotation);
                            rowBoxes.push({ anchorX: na.x, anchorY: na.y, w: g.w, h: g.h, rotDeg: newRot });
                            return { ...r, posX: na.x, posY: na.y, offsetX: 0, offsetY: 0, width: g.w, height: g.h, rotation: newRot };
                        });
                        nextSeatBlock = { ...s.seat_block, section_rows: newRows };
                    }

                    if (!childSet.has(s.id)) {
                        return nextSeatBlock !== s.seat_block ? { ...s, seat_block: nextSeatBlock } : s;
                    }

                    if (s.type === 'rect') {
                        if (s.svgPath) {
                            // Prebuilt icon: rotate its centre about the group centre and
                            // bump its own rotation, keeping the rect + svgPath artwork.
                            const [rx, ry, rw, rh] = s.points;
                            const cen = rotPt(rx + rw / 2, ry + rh / 2);
                            return { ...s, points: [cen.x - rw / 2, cen.y - rh / 2, rw, rh], rotation: addDeg(s.rotation), seat_block: nextSeatBlock };
                        }
                        const [rx, ry, rw, rh] = s.points;
                        const c = [rotPt(rx, ry), rotPt(rx + rw, ry), rotPt(rx + rw, ry + rh), rotPt(rx, ry + rh)];
                        const pts: number[] = [];
                        c.forEach(p => pts.push(p.x, p.y));
                        return { ...s, type: 'custom' as const, points: pts, seat_block: nextSeatBlock };
                    } else if (s.type === 'text') {
                        const [rx, ry, rw, rh] = s.points;
                        const cen = rotPt(rx + rw / 2, ry + rh / 2);
                        return { ...s, points: [cen.x - rw / 2, cen.y - rh / 2, rw, rh], rotation: addDeg(s.rotation), seat_block: nextSeatBlock };
                    } else if (s.type === 'circle') {
                        const rp = rotPt(s.cx || 0, s.cy || 0);
                        return { ...s, cx: rp.x, cy: rp.y, rotation: addDeg(s.rotation), seat_block: nextSeatBlock };
                    } else if (s.type === 'group') {
                        // Nested group: rotate its box centre, keep it axis-aligned.
                        const [rx, ry, rw, rh] = s.points;
                        const cen = rotPt(rx + rw / 2, ry + rh / 2);
                        return { ...s, points: [cen.x - rw / 2, cen.y - rh / 2, rw, rh], seat_block: nextSeatBlock };
                    }
                    // Polygon-based (custom / line / trapezoid): rotate every vertex.
                    const np: number[] = [];
                    for (let i = 0; i < s.points.length; i += 2) { const rp = rotPt(s.points[i], s.points[i + 1]); np.push(rp.x, rp.y); }
                    const nh = s.holePoints ? (() => { const a: number[] = []; for (let i = 0; i < s.holePoints!.length; i += 2) { const rp = rotPt(s.holePoints![i], s.holePoints![i + 1]); a.push(rp.x, rp.y); } return a; })() : s.holePoints;
                    return { ...s, points: np, holePoints: nh, seat_block: nextSeatBlock };
                });

                const newBox = recomputeGroupBBox(updated.filter(s => childSet.has(s.id)), rowBoxes);
                if (newBox) updated = updated.map(s => s.id === id ? { ...s, points: newBox } : s);
                return updated;
            }

            return prev.map(s => {
            if (s.id !== id) return s;

            // Pivot = the same centre the shape turns around, so a section's bound
            // seat rows (its chairs) rotate together with the section outline.
            let pX: number, pY: number;
            if (s.type === 'circle') { pX = s.cx ?? 0; pY = s.cy ?? 0; }
            else if (s.type === 'rect' || s.type === 'text') { pX = s.points[0] + s.points[2] / 2; pY = s.points[1] + s.points[3] / 2; }
            else { const n = s.points.length / 2; pX = s.points.reduce((a, v, i) => i % 2 === 0 ? a + v : a, 0) / n; pY = s.points.reduce((a, v, i) => i % 2 !== 0 ? a + v : a, 0) / n; }
            const rotA = (x: number, y: number) => ({ x: pX + (x - pX) * Math.cos(rad) - (y - pY) * Math.sin(rad), y: pY + (x - pX) * Math.sin(rad) + (y - pY) * Math.cos(rad) });
            const bumpRot = (cur?: number) => ((((cur ?? 0) + degrees) % 360) + 360) % 360;
            const nextSeatBlock = transformBoundSeatRows(s, rotA, (r, g) => ({ width: g.w, height: g.h, rotation: bumpRot(r.rotation) }));

            if (s.type === 'circle') {
                // Symmetric shapes — store visual rotation for the Group
                return { ...s, rotation: bumpRot(s.rotation), seat_block: nextSeatBlock };
            } else if (s.type === 'text') {
                // Text shapes are rectangular visual elements; store rotation rather than converting to polygon
                return { ...s, rotation: bumpRot(s.rotation), seat_block: nextSeatBlock };
            } else if (s.type === 'rect') {
                // Prebuilt-icon rects carry an svgPath: keep them rects and just store
                // a rotation (the icon renderer + export both honour shape.rotation).
                // Converting them to polygons dropped the artwork, leaving a plain box.
                if (s.svgPath) {
                    return { ...s, rotation: bumpRot(s.rotation) };
                }
                // Plain rect → custom polygon, then rotate around its centre
                const [rx, ry, rw, rh] = s.points;
                const corners = [rx, ry, rx + rw, ry, rx + rw, ry + rh, rx, ry + rh];
                const rotated: number[] = [];
                for (let i = 0; i < corners.length; i += 2) {
                    const dx = corners[i] - pX, dy = corners[i + 1] - pY;
                    rotated.push(pX + dx * Math.cos(rad) - dy * Math.sin(rad), pY + dx * Math.sin(rad) + dy * Math.cos(rad));
                }
                return { ...s, type: 'custom' as const, points: rotated, seat_block: nextSeatBlock };
            } else {
                // Poly-based shapes: rotate all points around centroid (= pivot)
                const newPoints: number[] = [];
                for (let i = 0; i < s.points.length; i += 2) {
                    const dx = s.points[i] - pX, dy = s.points[i + 1] - pY;
                    newPoints.push(pX + dx * Math.cos(rad) - dy * Math.sin(rad), pY + dx * Math.sin(rad) + dy * Math.cos(rad));
                }
                const newHolePoints = s.holePoints ? (() => {
                    const hp: number[] = [];
                    for (let i = 0; i < s.holePoints!.length; i += 2) {
                        const dx = s.holePoints![i] - pX, dy = s.holePoints![i + 1] - pY;
                        hp.push(pX + dx * Math.cos(rad) - dy * Math.sin(rad), pY + dx * Math.sin(rad) + dy * Math.cos(rad));
                    }
                    return hp;
                })() : s.holePoints;
                return { ...s, points: newPoints, holePoints: newHolePoints, seat_block: nextSeatBlock };
            }
        });
        });
        setContextMenu(null);
    };

    // ── Scale a shape by `factor` around its own centre ───────────────────────
    const scaleShape = (id: string, factor: number) => {
        saveToHistory();
        setShapes(prev => {
            const target = prev.find(s => s.id === id);

            // Group scale: uniformly scale every child (shape geometry + seat rows,
            // incl. seat radius) about the group centre, then refit the group box.
            // Without this the panel's scale buttons changed only the dashed box and
            // left the children behind.
            if (target?.type === 'group') {
                const [gx, gy, gw, gh] = target.points;
                const cx = gx + gw / 2, cy = gy + gh / 2;
                const childSet = new Set(target.childIds || []);
                const scPt = (px: number, py: number) => ({ x: cx + (px - cx) * factor, y: cy + (py - cy) * factor });
                const rowBoxes: { anchorX: number; anchorY: number; w: number; h: number; rotDeg: number }[] = [];

                let updated = prev.map(s => {
                    const which = rowsInGroup(s, childSet);
                    let nextSeatBlock = s.seat_block;
                    if (which && s.seat_block) {
                        const pbb = getShapeBBox(s);
                        const newRows = s.seat_block.section_rows!.map((r: any, i: number) => {
                            if (which !== 'all' && !which.has(i)) return r;
                            const g = getRowAnchorGeom(pbb, r, i);
                            const na = scPt(g.anchorX, g.anchorY);
                            const nW = g.w * factor, nH = g.h * factor;
                            rowBoxes.push({ anchorX: na.x, anchorY: na.y, w: nW, h: nH, rotDeg: r.rotation ?? 0 });
                            return { ...r, posX: na.x, posY: na.y, offsetX: 0, offsetY: 0, width: nW, height: nH, seatRadius: (r.seatRadius ?? SEAT_DOT_RADIUS) * factor };
                        });
                        nextSeatBlock = { ...s.seat_block, section_rows: newRows };
                    }

                    if (!childSet.has(s.id)) {
                        return nextSeatBlock !== s.seat_block ? { ...s, seat_block: nextSeatBlock } : s;
                    }

                    if (s.type === 'rect' || s.type === 'text' || s.type === 'group') {
                        const p = scPt(s.points[0], s.points[1]);
                        const out: Shape = { ...s, points: [p.x, p.y, s.points[2] * factor, s.points[3] * factor], seat_block: nextSeatBlock };
                        if (s.type === 'text') out.fontSize = Math.max(8, Math.round((s.fontSize || 16) * factor));
                        return out;
                    } else if (s.type === 'circle') {
                        const p = scPt(s.cx || 0, s.cy || 0);
                        return { ...s, cx: p.x, cy: p.y, r: Math.max(5, (s.r || 50) * factor), seat_block: nextSeatBlock };
                    }
                    const np: number[] = [];
                    for (let i = 0; i < s.points.length; i += 2) { const p = scPt(s.points[i], s.points[i + 1]); np.push(p.x, p.y); }
                    const nh = s.holePoints ? (() => { const a: number[] = []; for (let i = 0; i < s.holePoints!.length; i += 2) { const p = scPt(s.holePoints![i], s.holePoints![i + 1]); a.push(p.x, p.y); } return a; })() : s.holePoints;
                    return { ...s, points: np, holePoints: nh, seat_block: nextSeatBlock };
                });

                const newBox = recomputeGroupBBox(updated.filter(s => childSet.has(s.id)), rowBoxes);
                if (newBox) updated = updated.map(s => s.id === id ? { ...s, points: newBox } : s);
                return updated;
            }

            return prev.map(s => {
            if (s.id !== id) return s;

            // Pivot = the shape's centre; a section's bound seat rows (chairs) scale
            // about the same point and grow/shrink with it (incl. seat radius).
            let pX: number, pY: number;
            if (s.type === 'circle') { pX = s.cx ?? 0; pY = s.cy ?? 0; }
            else if (s.type === 'rect' || s.type === 'text') { pX = s.points[0] + s.points[2] / 2; pY = s.points[1] + s.points[3] / 2; }
            else { const n = s.points.length / 2; pX = s.points.reduce((a, v, i) => i % 2 === 0 ? a + v : a, 0) / n; pY = s.points.reduce((a, v, i) => i % 2 !== 0 ? a + v : a, 0) / n; }
            const scA = (x: number, y: number) => ({ x: pX + (x - pX) * factor, y: pY + (y - pY) * factor });
            const nextSeatBlock = transformBoundSeatRows(s, scA, (r, g) => ({ width: g.w * factor, height: g.h * factor, seatRadius: (r.seatRadius ?? SEAT_DOT_RADIUS) * factor }));

            if (s.type === 'circle') {
                return { ...s, r: Math.max(5, (s.r || 50) * factor), seat_block: nextSeatBlock };
            } else if (s.type === 'rect') {
                const newW = Math.max(10, s.points[2] * factor);
                const newH = Math.max(10, s.points[3] * factor);
                return { ...s, points: [pX - newW / 2, pY - newH / 2, newW, newH], seat_block: nextSeatBlock };
            } else {
                // Poly-based: scale all points around their centroid (= pivot)
                const newPoints = s.points.map((v, i) =>
                    i % 2 === 0 ? pX + (v - pX) * factor : pY + (v - pY) * factor
                );
                return { ...s, points: newPoints, seat_block: nextSeatBlock };
            }
            });
        });
        setContextMenu(null);
    };

    const handleSeatDrag = (id: string, newX: number, newY: number) => {
        // Convert absolute coordinates to relative coordinates
        const relX = newX - centerPoint.x;
        const relY = newY - centerPoint.y;
        setSeats(seats.map(s => s.id === id ? { ...s, x: relX, y: relY } : s));
    };

    // ── Helper: compute axis-aligned bounding box for any shape (in relative world coords) ──
    const getShapeBBox = (shape: Shape): { x: number; y: number; w: number; h: number } => {
        if (shape.type === 'rect' || shape.type === 'text' || shape.type === 'group') {
            return { x: shape.points[0], y: shape.points[1], w: shape.points[2], h: shape.points[3] };
        }
        if (shape.type === 'circle') {
            const cx = shape.cx ?? 0, cy = shape.cy ?? 0, r = shape.r ?? 50;
            return { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
        }
        // Polygon-based shapes (trapezoid, custom, line, group)
        const pts = shape.points;
        if (!pts || pts.length < 2) return { x: 0, y: 0, w: 0, h: 0 };
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < pts.length; i += 2) {
            minX = Math.min(minX, pts[i]);
            maxX = Math.max(maxX, pts[i]);
            minY = Math.min(minY, pts[i + 1]);
            maxY = Math.max(maxY, pts[i + 1]);
        }
        return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    };

    // ── Compute the horizontal span at a given Y inside a polygon (for non-rect shapes) ──
    const getPolygonRowSpan = (shape: Shape, worldY: number): { left: number; right: number } | null => {
        if (shape.type === 'rect' || shape.type === 'text') {
            return { left: shape.points[0], right: shape.points[0] + shape.points[2] };
        }
        if (shape.type === 'circle') {
            const cx = shape.cx ?? 0, cy = shape.cy ?? 0, r = shape.r ?? 50;
            const dy = worldY - cy;
            if (Math.abs(dy) >= r) return null;
            const half = Math.sqrt(r * r - dy * dy);
            return { left: cx - half, right: cx + half };
        }
        // General polygon scan-line
        const pts = shape.points;
        if (!pts || pts.length < 4) return null;
        const numVerts = pts.length / 2;
        const intersections: number[] = [];
        for (let i = 0; i < numVerts; i++) {
            const x1 = pts[i * 2], y1 = pts[i * 2 + 1];
            const j = (i + 1) % numVerts;
            const x2 = pts[j * 2], y2 = pts[j * 2 + 1];
            if ((y1 <= worldY && y2 > worldY) || (y2 <= worldY && y1 > worldY)) {
                const t = (worldY - y1) / (y2 - y1);
                intersections.push(x1 + t * (x2 - x1));
            }
        }
        if (intersections.length < 2) return null;
        intersections.sort((a, b) => a - b);
        return { left: intersections[0], right: intersections[intersections.length - 1] };
    };

    // ── Render seat-rows layer for a single seat-block shape ─────────────────
    const SEAT_DOT_RADIUS = 6;
    const ROW_PADDING = 4; // vertical padding inside each row band
    const SEAT_GAP = 4; // horizontal gap between seat dots

    // Absolute (relative-to-centerPoint) top-left anchor + box size of a seat row,
    // mirroring renderSeatRows() so group transforms line up with what's drawn.
    // The row's Konva <Group> is positioned at this anchor and rotates about it,
    // so rotating/scaling the anchor (and adjusting row.rotation) moves a row
    // rigidly with its group.
    const getRowAnchorGeom = (parentBbox: { x: number; y: number; w: number; h: number }, row: any, rowIdx: number) => {
        const seatR = row.seatRadius ?? SEAT_DOT_RADIUS;
        const seatDiam = seatR * 2;
        const natRowH = seatDiam + ROW_PADDING * 2;
        const nSeats = (row.row_seats || []).length;
        const natRowW = 24 + nSeats * seatDiam + Math.max(0, nSeats - 1) * SEAT_GAP + ROW_PADDING * 2;
        const bendAbs = Math.abs(row.bend ?? 0);
        const effNatH = natRowH + (bendAbs > 0.5 ? bendAbs : 0);
        const defaultNatRowH = SEAT_DOT_RADIUS * 2 + ROW_PADDING * 2;
        const baseGroupY = parentBbox.y + defaultNatRowH * rowIdx;
        return {
            anchorX: (row.posX ?? parentBbox.x) + (row.offsetX ?? 0),
            anchorY: (row.posY ?? baseGroupY) + (row.offsetY ?? 0),
            w: row.width ?? natRowW,
            h: row.height ?? effNatH,
        };
    };

    // Which of a shape's seat rows participate in a group transform: 'all' when the
    // parent shape itself is a group child, otherwise the specific rows that were
    // grouped individually (childIds containing their seatrow-* id), or null.
    const rowsInGroup = (s: Shape, childSet: Set<string>): 'all' | Set<number> | null => {
        const rows = s.seat_block?.section_rows || [];
        if (rows.length === 0) return null;
        if (childSet.has(s.id)) return 'all';
        const idxs = new Set<number>();
        rows.forEach((r: any, i: number) => {
            if (childSet.has(`seatrow-${s.id}-${r.id || `row-${i}`}`)) idxs.add(i);
        });
        return idxs.size ? idxs : null;
    };

    // Recompute a group's bounding box [x,y,w,h] (with padding) from its already-
    // transformed children + the transformed seat-row boxes collected during the op.
    const recomputeGroupBBox = (
        childShapes: Shape[],
        rowBoxes: { anchorX: number; anchorY: number; w: number; h: number; rotDeg: number }[],
    ): [number, number, number, number] | null => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const incl = (x: number, y: number) => { if (x < minX) minX = x; if (y < minY) minY = y; if (x > maxX) maxX = x; if (y > maxY) maxY = y; };
        for (const s of childShapes) {
            if (s.type === 'rect' || s.type === 'group' || s.type === 'text') {
                incl(s.points[0], s.points[1]); incl(s.points[0] + s.points[2], s.points[1] + s.points[3]);
            } else if (s.type === 'circle') {
                const r = s.r || 50; incl((s.cx || 0) - r, (s.cy || 0) - r); incl((s.cx || 0) + r, (s.cy || 0) + r);
            } else {
                for (let i = 0; i < s.points.length; i += 2) incl(s.points[i], s.points[i + 1]);
            }
        }
        for (const rb of rowBoxes) {
            const rr = (rb.rotDeg * Math.PI) / 180;
            for (const [lx, ly] of [[0, 0], [rb.w, 0], [rb.w, rb.h], [0, rb.h]]) {
                incl(rb.anchorX + lx * Math.cos(rr) - ly * Math.sin(rr), rb.anchorY + lx * Math.sin(rr) + ly * Math.cos(rr));
            }
        }
        if (minX === Infinity) return null;
        const PAD = 10;
        return [minX - PAD, minY - PAD, maxX - minX + PAD * 2, maxY - minY + PAD * 2];
    };

    // Transform a section's bound seat rows so the chairs follow when the parent
    // shape is rotated/scaled. `mapPoint` maps each row's (original) top-left anchor
    // to its new anchor; `perRow` supplies the rest of the patch (rotation delta for
    // rotate; width/height/seatRadius scale for scale). posX/posY are pinned so rows
    // no longer depend on the parent's (now changed) bounding box.
    const transformBoundSeatRows = (
        shape: Shape,
        mapPoint: (x: number, y: number) => { x: number; y: number },
        perRow: (row: any, g: { anchorX: number; anchorY: number; w: number; h: number }) => Record<string, any>,
    ) => {
        if (!shape.seat_block?.section_rows?.length) return shape.seat_block;
        const bbox = getShapeBBox(shape);
        const rows = shape.seat_block.section_rows.map((r: any, i: number) => {
            const g = getRowAnchorGeom(bbox, r, i);
            const na = mapPoint(g.anchorX, g.anchorY);
            return { ...r, posX: na.x, posY: na.y, offsetX: 0, offsetY: 0, ...perRow(r, g) };
        });
        return { ...shape.seat_block, section_rows: rows };
    };

    // Helper: update a row's visual override inside a shape's seat_block
    const updateRowVisual = (shapeId: string, rowIdx: number, patch: Record<string, any>) => {
        setShapes(prev => prev.map(s => {
            if (s.id !== shapeId || !s.seat_block) return s;
            const updatedRows = [...(s.seat_block.section_rows || [])];
            if (!updatedRows[rowIdx]) return s;
            updatedRows[rowIdx] = { ...updatedRows[rowIdx], ...patch };
            return { ...s, seat_block: { ...s.seat_block, section_rows: updatedRows } };
        }));
    };

    // Helper: parse a seatrow selectedId like "seatrow-{shapeId}-{rowId}" into { shapeId, rowId, rowIdx }
    const parseSeatRowId = (id: string): { shapeId: string; rowId: string; rowIdx: number } | null => {
        if (!id || !id.startsWith('seatrow-')) return null;
        const rest = id.slice('seatrow-'.length);
        // Find the matching shape — the rest = "{shapeId}-{rowId}"
        for (const s of shapes) {
            if (rest.startsWith(s.id + '-')) {
                const rowId = rest.slice(s.id.length + 1);
                const rows = s.seat_block?.section_rows || [];
                const rowIdx = rows.findIndex((r: any) => (r.id || `row-${rows.indexOf(r)}`) === rowId);
                if (rowIdx >= 0) return { shapeId: s.id, rowId, rowIdx };
            }
        }
        return null;
    };

    // Rotate a seat-row group by given degrees
    const rotateSeatRow = (seatRowId: string, degrees: number) => {
        const parsed = parseSeatRowId(seatRowId);
        if (!parsed) return;
        saveToHistory();
        const shape = shapes.find(s => s.id === parsed.shapeId);
        if (!shape?.seat_block?.section_rows?.[parsed.rowIdx]) return;
        const currentRotation = shape.seat_block.section_rows[parsed.rowIdx].rotation ?? 0;
        updateRowVisual(parsed.shapeId, parsed.rowIdx, { rotation: ((currentRotation + degrees) % 360 + 360) % 360 });
    };

    // Scale a seat-row group by given factor
    const scaleSeatRow = (seatRowId: string, factor: number) => {
        const parsed = parseSeatRowId(seatRowId);
        if (!parsed) return;
        saveToHistory();
        const shape = shapes.find(s => s.id === parsed.shapeId);
        if (!shape?.seat_block?.section_rows?.[parsed.rowIdx]) return;
        const row = shape.seat_block.section_rows[parsed.rowIdx];
        const rowSeatR = row.seatRadius ?? SEAT_DOT_RADIUS;
        const seatDiameter = rowSeatR * 2;
        const naturalRowH = seatDiameter + ROW_PADDING * 2;
        const LABEL_WIDTH = 24;
        const rowSeats = row.row_seats || [];
        const naturalRowW = LABEL_WIDTH + rowSeats.length * seatDiameter + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
        const curW = row.width ?? naturalRowW;
        const curH = row.height ?? naturalRowH;
        updateRowVisual(parsed.shapeId, parsed.rowIdx, {
            width: Math.max(naturalRowW * 0.3, curW * factor),
            height: Math.max(naturalRowH * 0.5, curH * factor),
        });
    };

    // Set seat radius for a seat row
    const setSeatRowRadius = (seatRowId: string, radius: number) => {
        const parsed = parseSeatRowId(seatRowId);
        if (!parsed) return;
        saveToHistory();
        const clamped = Math.max(2, Math.min(50, radius));
        updateRowVisual(parsed.shapeId, parsed.rowIdx, { seatRadius: clamped });
    };

    // Get current seat radius for a seat row
    const getSeatRowRadius = (seatRowId: string): number => {
        const parsed = parseSeatRowId(seatRowId);
        if (!parsed) return SEAT_DOT_RADIUS;
        const shape = shapes.find(s => s.id === parsed.shapeId);
        return shape?.seat_block?.section_rows?.[parsed.rowIdx]?.seatRadius ?? SEAT_DOT_RADIUS;
    };

    const renderSeatRows = (shape: Shape) => {
        if (!shape.seat_block) return null;
        const rows = shape.seat_block.section_rows || [];
        if (rows.length === 0) return null;

        const bbox = getShapeBBox(shape);
        const LABEL_WIDTH = 24; // reserved width for the row name label on the left
        // Default row height for stacking (uses global SEAT_DOT_RADIUS)
        const defaultSeatDiameter = SEAT_DOT_RADIUS * 2;
        const defaultNatRowH = defaultSeatDiameter + ROW_PADDING * 2;

        return rows.map((row: any, rowIdx: number) => {
            const rowSeats = row.row_seats || [];
            if (rowSeats.length === 0) return null;

            // Per-row seat radius (custom or default)
            const rowSeatRadius = row.seatRadius ?? SEAT_DOT_RADIUS;
            const seatDiameter = rowSeatRadius * 2;
            const naturalRowH = seatDiameter + ROW_PADDING * 2;

            // Per-row custom offsets (set by dragging the row group)
            const ox = row.offsetX ?? 0;
            const oy = row.offsetY ?? 0;
            const rowRotation = row.rotation ?? 0;
            const bend = row.bend ?? 0; // vertical bend factor for curved rows

            // Account for bend in the effective natural height so seats stay within group bounds
            const bendAbs = Math.abs(bend);
            const effectiveNaturalH = naturalRowH + (bendAbs > 0.5 ? bendAbs : 0);
            // For negative bend, shift content down so it stays in positive Y
            const yBendOffset = bend < -0.5 ? -bend : 0;

            // Default Y position: stack rows using default height for consistent spacing
            const baseGroupY = bbox.y + defaultNatRowH * rowIdx;

            // Determine row width & height — use stored overrides or compute natural size
            const naturalRowW = LABEL_WIDTH + rowSeats.length * seatDiameter + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
            const groupW = row.width ?? naturalRowW;
            const groupH = row.height ?? effectiveNaturalH;
            const groupX = (row.posX ?? bbox.x) + ox;
            const groupY = (row.posY ?? baseGroupY) + oy;

            // Scale factors relative to effective natural size so bent contents always fit inside
            const scaleX = groupW / naturalRowW;
            const scaleY = groupH / effectiveNaturalH;

            // Shift the whole group visually so the bend follows the cursor direction:
            // For upward arch (bend < 0): shift group UP so center appears higher while edges stay in place.
            // For downward U (bend > 0): no shift needed, center naturally goes down.
            const groupBendYShift = Math.min(0, bend) * scaleY;

            // Seat layout (in natural unscaled coordinates, then scaled)
            const labelW = LABEL_WIDTH;
            const seatsAreaStartX = labelW + ROW_PADDING;

            const rowId = row.id || `row-${rowIdx}`;
            const seatRowSelId = `seatrow-${shape.id}-${rowId}`;
            const isRowSelected = selectedId === seatRowSelId && selectedType === 'shape';
            const isRowInMultiSelect = selectedIds.includes(seatRowSelId) && selectedIds.length > 1;

            // Initial state for this row (used by resize ref)
            const initPosX = row.posX ?? bbox.x;
            const initPosY = row.posY ?? baseGroupY;

            // Start a resize drag: snapshot current row geometry + pointer
            const startRowResize = (e: any) => {
                e.cancelBubble = true;
                isDraggingRef.current = true;
                const stage = e.target.getStage();
                const ptr = stage?.getPointerPosition();
                rowResizeStartRef.current = {
                    posX: initPosX, posY: initPosY,
                    width: groupW, height: groupH,
                    ptrX: ptr?.x ?? 0, ptrY: ptr?.y ?? 0,
                };
            };

            // Corner resize using stable stage pointer delta
            const moveRowCorner = (e: any, corner: number) => {
                e.cancelBubble = true;
                const init = rowResizeStartRef.current;
                if (!init) return;
                const stage = e.target.getStage();
                const ptr = stage?.getPointerPosition();
                if (!ptr) return;
                const dx = (ptr.x - init.ptrX) / zoomScale;
                const dy = (ptr.y - init.ptrY) / zoomScale;
                let nX = init.posX, nY = init.posY, nW = init.width, nH = init.height;
                if (corner === 0) { nX += dx; nY += dy; nW -= dx; nH -= dy; }
                else if (corner === 1) { nY += dy; nW += dx; nH -= dy; }
                else if (corner === 2) { nW += dx; nH += dy; }
                else { nX += dx; nW -= dx; nH += dy; }
                nW = Math.max(naturalRowW * 0.3, nW);
                nH = Math.max(naturalRowH * 0.5, nH);
                updateRowVisual(shape.id, rowIdx, { posX: nX, posY: nY, width: nW, height: nH });
            };

            // Edge resize using stable stage pointer delta
            const moveRowEdge = (e: any, edge: string) => {
                e.cancelBubble = true;
                const init = rowResizeStartRef.current;
                if (!init) return;
                const stage = e.target.getStage();
                const ptr = stage?.getPointerPosition();
                if (!ptr) return;
                const dx = (ptr.x - init.ptrX) / zoomScale;
                const dy = (ptr.y - init.ptrY) / zoomScale;
                let nX = init.posX, nY = init.posY, nW = init.width, nH = init.height;
                if (edge === 'right') nW += dx;
                else if (edge === 'left') { nX += dx; nW -= dx; }
                else if (edge === 'bottom') nH += dy;
                else if (edge === 'top') { nY += dy; nH -= dy; }
                nW = Math.max(naturalRowW * 0.3, nW);
                nH = Math.max(naturalRowH * 0.5, nH);
                updateRowVisual(shape.id, rowIdx, { posX: nX, posY: nY, width: nW, height: nH });
            };

            // Compute seat positions with optional bend (arc)
            const computeSeatPos = (seatIdx: number, totalSeats: number): { x: number; y: number } => {
                const t = totalSeats > 1 ? seatIdx / (totalSeats - 1) : 0.5; // 0..1
                const naturalX = seatsAreaStartX + seatIdx * (seatDiameter + SEAT_GAP) + rowSeatRadius;
                const naturalY = naturalRowH / 2;
                // Bend: parabolic offset on Y axis, max at center (t=0.5)
                const bendOffset = bend * 4 * t * (1 - t); // parabola: max at midpoint
                return { x: naturalX * scaleX, y: (naturalY + bendOffset + yBendOffset) * scaleY };
            };

            // ── Outline key-point calculations (shared by polygon + handles) ──
            const scaledR = rowSeatRadius * Math.min(scaleX, scaleY);
            const outlineMargin = scaledR + ROW_PADDING * Math.min(scaleX, scaleY);

            // At the left/right edges (t=0 / t=1) the bend parabola is 0,
            // so centreline sits at the "resting" Y.
            const edgeCenterY = (naturalRowH / 2 + yBendOffset) * scaleY;
            const edgeTopY    = edgeCenterY - outlineMargin;
            const edgeBottomY = edgeCenterY + outlineMargin;

            // At the midpoint (t=0.5) the bend is at its maximum.
            const midCenterY  = (naturalRowH / 2 + bend + yBendOffset) * scaleY;
            const midTopY     = midCenterY - outlineMargin;
            const midBottomY  = midCenterY + outlineMargin;

            // Compute global vertical bounds for the bent outline
            const globalMinTop = Math.min(edgeTopY, midTopY);
            const globalMaxBottom = Math.max(edgeBottomY, midBottomY);

            // Compute a bent outline polygon that follows the bend curve tightly
            const computeBentOutline = (): number[] => {
                if (Math.abs(bend) < 0.5) {
                    // No bend — simple rect
                    return [0, 0, groupW, 0, groupW, groupH, 0, groupH];
                }
                // Pure curve band: every point follows centerY ± margin.
                // Left / right vertical edges extend to the full vertical extent
                // of the curve so the outline fully wraps all content.
                const STEPS = Math.max(16, rowSeats.length * 2);
                const topPts: number[] = [];
                const bottomPts: number[] = [];
                for (let i = 0; i <= STEPS; i++) {
                    const t = i / STEPS;
                    const x = t * groupW;
                    const centerY = (naturalRowH / 2 + bend * 4 * t * (1 - t) + yBendOffset) * scaleY;
                    if (i === 0 || i === STEPS) {
                        // Vertical edges: extend to global bounds so outline contains all content
                        topPts.push(x, globalMinTop);
                        bottomPts.push(x, globalMaxBottom);
                    } else {
                        topPts.push(x, centerY - outlineMargin);
                        bottomPts.push(x, centerY + outlineMargin);
                    }
                }
                // Combine: top-edge left→right, then bottom-edge right→left
                const reversedBottom: number[] = [];
                for (let i = bottomPts.length - 2; i >= 0; i -= 2) {
                    reversedBottom.push(bottomPts[i], bottomPts[i + 1]);
                }
                return [...topPts, ...reversedBottom];
            };
            const outlinePoints = computeBentOutline();

            return (
                <Group
                    key={`seatrow-${shape.id}-${rowId}`}
                    x={groupX + centerPoint.x}
                    y={groupY + centerPoint.y + groupBendYShift}
                    rotation={rowRotation}
                    draggable={!activeTool && !isMarqueeSelecting}
                    onDragStart={(e: any) => {
                        // Don't start drag during marquee — cancel if ref says marquee is active
                        if (isMarqueeSelecting || isMarqueeRef.current) {
                            e.target.stopDrag();
                            return;
                        }
                        isDraggingRef.current = true; shapeClickedRef.current = true;
                    }}
                    onDragEnd={(e: any) => {
                        if (isMarqueeSelecting || isMarqueeRef.current) return;
                        isDraggingRef.current = true;
                        shapeClickedRef.current = true;
                        const dx = e.target.x() - (groupX + centerPoint.x);
                        const dy = e.target.y() - (groupY + centerPoint.y + groupBendYShift);
                        e.target.position({ x: groupX + centerPoint.x, y: groupY + centerPoint.y + groupBendYShift });
                        updateRowVisual(shape.id, rowIdx, { offsetX: ox + dx, offsetY: oy + dy });
                    }}
                    onMouseDown={(e: any) => {
                        if (activeTool) return;
                        // Don't interfere with marquee selection in progress
                        if (isMarqueeSelecting || isMarqueeRef.current) return;
                        e.cancelBubble = true;
                        shapeClickedRef.current = true;
                        startMouseLongPress(e, seatRowSelId, 'shape');
                        if (e.evt?.ctrlKey || e.evt?.metaKey) {
                            setSelectedIds(prev =>
                                prev.includes(seatRowSelId) ? prev.filter(id => id !== seatRowSelId) : [...prev, seatRowSelId]
                            );
                            setSelectedId(seatRowSelId);
                            setSelectedType('shape');
                        } else {
                            if (selectedId !== seatRowSelId) {
                                setSelectedId(seatRowSelId);
                                setSelectedType('shape');
                                setSelectedIds([seatRowSelId]);
                            }
                        }
                    }}
                    onMouseUp={endMouseLongPress}
                    onClick={(e: any) => {
                        // Don't override marquee selection (synchronous ref check is critical here)
                        if (isMarqueeSelecting || isMarqueeRef.current) return;
                        e.cancelBubble = true;
                        shapeClickedRef.current = true;
                        if (e.evt?.ctrlKey || e.evt?.metaKey) {
                            setSelectedIds(prev =>
                                prev.includes(seatRowSelId) ? prev.filter(id => id !== seatRowSelId) : [...prev, seatRowSelId]
                            );
                            setSelectedId(seatRowSelId);
                            setSelectedType('shape');
                        } else {
                            setSelectedId(seatRowSelId);
                            setSelectedType('shape');
                            setSelectedIds([seatRowSelId]);
                        }
                    }}
                    onContextMenu={(e: any) => {
                        e.evt?.preventDefault?.();
                        e.cancelBubble = true;
                        openAddRowDialog(shape.id);
                    }}
                >
                    {/* Invisible hit area for drag/click — no visible outline */}
                    <Line
                        points={outlinePoints}
                        closed
                        fill="transparent"
                        stroke="transparent"
                        strokeWidth={0}
                        hitStrokeWidth={12}
                    />
                    {/* Row name label — left side, follows bend curve at its position */}
                    {(() => {
                        const labelCenterNatX = LABEL_WIDTH / 2;
                        const labelT = labelCenterNatX / Math.max(1, naturalRowW);
                        const labelBendOffsetY = bend * 4 * labelT * (1 - labelT);
                        const labelFontSize = Math.min(10, groupH * 0.6);
                        return (
                            <Text
                                x={4 * scaleX}
                                y={(naturalRowH / 2 + labelBendOffsetY + yBendOffset) * scaleY - labelFontSize / 2}
                                text={row.name || 'N/A'}
                                fill="rgba(255,255,255,0.6)"
                                fontSize={labelFontSize}
                                listening={false}
                            />
                        );
                    })()}
                    {/* Seat dots — positioned after the label, with optional bend curve */}
                    {rowSeats.map((seat: any, seatIdx: number) => {
                        const pos = computeSeatPos(seatIdx, rowSeats.length);
                        return (
                            <Circle
                                key={`seat-${seat.seat_id}`}
                                x={pos.x}
                                y={pos.y}
                                radius={rowSeatRadius * Math.min(scaleX, scaleY)}
                                fill={isRowSelected ? '#00ff00' : '#87ceeb'}
                                stroke={isRowSelected ? '#00aa00' : 'rgba(255,255,255,0.6)'}
                                strokeWidth={1}
                                listening={false}
                            />
                        );
                    })}
                    {/* Rect-style corner resize handles — always at bbox corners */}
                    {isRowSelected && [
                        { cx: 0, cy: 0, ci: 0 },
                        { cx: groupW, cy: 0, ci: 1 },
                        { cx: groupW, cy: groupH, ci: 2 },
                        { cx: 0, cy: groupH, ci: 3 },
                    ].map(({ cx: hx, cy: hy, ci }) => (
                        <Circle
                            key={`rh-${ci}`}
                            x={hx} y={hy} radius={5}
                            fill="#00ff00" stroke="#00aa00" strokeWidth={1.5}
                            draggable
                            onDragStart={(e: any) => startRowResize(e)}
                            onDragMove={(e: any) => { moveRowCorner(e, ci); e.target.position({ x: hx, y: hy }); }}
                            onDragEnd={(e: any) => {
                                e.cancelBubble = true;
                                rowResizeStartRef.current = null;
                                e.target.position({ x: hx, y: hy });
                                // Bake visual seat size into seatRadius and reset row to natural dimensions
                                const visualR = rowSeatRadius * Math.min(scaleX, scaleY);
                                const clampedR = Math.max(2, Math.min(50, Math.round(visualR * 10) / 10));
                                const newDiam = clampedR * 2;
                                const newNatW = LABEL_WIDTH + rowSeats.length * newDiam + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                                const newNatH = newDiam + ROW_PADDING * 2 + (bendAbs > 0.5 ? bendAbs : 0);
                                updateRowVisual(shape.id, rowIdx, { seatRadius: clampedR, width: newNatW, height: newNatH });
                                if (editPanelMode === 'seatsize') setEditInputValue(String(clampedR));
                            }}
                        />
                    ))}
                    {/* Edge resize handles — follow bent outline */}
                    {isRowSelected && [
                        { ex: groupW / 2, ey: Math.abs(bend) >= 0.5 ? midTopY : 0, edge: 'top', cursor: 'ns-resize' },
                        { ex: groupW / 2, ey: Math.abs(bend) >= 0.5 ? midBottomY : groupH, edge: 'bottom', cursor: 'ns-resize' },
                        { ex: 0, ey: Math.abs(bend) >= 0.5 ? (globalMinTop + globalMaxBottom) / 2 : edgeCenterY, edge: 'left', cursor: 'ew-resize' },
                        { ex: groupW, ey: Math.abs(bend) >= 0.5 ? (globalMinTop + globalMaxBottom) / 2 : edgeCenterY, edge: 'right', cursor: 'ew-resize' },
                    ].map(({ ex, ey, edge, cursor }) => (
                        <Rect
                            key={`edge-${edge}`}
                            x={ex - 4} y={ey - 4}
                            width={8} height={8}
                            fill="rgba(0,255,0,0.2)" stroke="#00ff00" strokeWidth={1}
                            cornerRadius={2}
                            draggable
                            onMouseEnter={(e: any) => { e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = cursor); }}
                            onMouseLeave={(e: any) => { e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'default'); }}
                            onDragStart={(e: any) => startRowResize(e)}
                            onDragMove={(e: any) => moveRowEdge(e, edge)}
                            onDragEnd={(e: any) => {
                                e.cancelBubble = true;
                                rowResizeStartRef.current = null;
                                // Bake visual seat size into seatRadius and reset row to natural dimensions
                                const visualR = rowSeatRadius * Math.min(scaleX, scaleY);
                                const clampedR = Math.max(2, Math.min(50, Math.round(visualR * 10) / 10));
                                const newDiam = clampedR * 2;
                                const newNatW = LABEL_WIDTH + rowSeats.length * newDiam + (rowSeats.length - 1) * SEAT_GAP + ROW_PADDING * 2;
                                const newNatH = newDiam + ROW_PADDING * 2 + (bendAbs > 0.5 ? bendAbs : 0);
                                updateRowVisual(shape.id, rowIdx, { seatRadius: clampedR, width: newNatW, height: newNatH });
                                if (editPanelMode === 'seatsize') setEditInputValue(String(clampedR));
                            }}
                        />
                    ))}
                    {/* Bend handle — diamond on top-center, drag up/down to bend the row */}
                    {isRowSelected && (
                        <Circle
                            key="bend-handle"
                            x={groupW / 2}
                            y={Math.min(edgeTopY, midTopY) - 10}
                            radius={6}
                            fill="#ff9800" stroke="#e65100" strokeWidth={1.5}
                            draggable
                            onMouseEnter={(e: any) => { e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'ns-resize'); }}
                            onMouseLeave={(e: any) => { e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'default'); }}
                            onDragStart={(e: any) => {
                                e.cancelBubble = true;
                                isDraggingRef.current = true;
                                isBendDraggingRef.current = true;
                                const ptr = e.target.getStage()?.getPointerPosition();
                                bendDragStartRef.current = { bend, ptrY: ptr?.y ?? 0 };
                            }}
                            onDragMove={(e: any) => {
                                e.cancelBubble = true;
                                const start = bendDragStartRef.current;
                                if (!start) return;
                                const ptr = e.target.getStage()?.getPointerPosition();
                                if (!ptr) return;
                                const dy = (ptr.y - start.ptrY) / zoomScale;
                                const newBend = start.bend + dy;
                                const newBendAbs = Math.abs(newBend);
                                const autoH = naturalRowH + (newBendAbs > 0.5 ? newBendAbs : 0);
                                updateRowVisual(shape.id, rowIdx, { bend: newBend, height: autoH });
                                // Compute updated handle Y from the new bend value
                                const newMidCenterY = (naturalRowH / 2 + newBend + yBendOffset) * scaleY;
                                const newMidTopY = newMidCenterY - outlineMargin;
                                const newEdgeTopY = edgeTopY; // edge doesn't change with bend
                                const handleY = Math.min(newEdgeTopY, newMidTopY) - 10;
                                e.target.position({ x: groupW / 2, y: handleY });
                            }}
                            onDragEnd={(e: any) => {
                                e.cancelBubble = true;
                                isBendDraggingRef.current = false;
                                const start = bendDragStartRef.current;
                                bendDragStartRef.current = null;
                                if (!start) return;
                                const ptr = e.target.getStage()?.getPointerPosition();
                                if (!ptr) return;
                                const dy = (ptr.y - start.ptrY) / zoomScale;
                                const finalBend = start.bend + dy;
                                const finalBendAbs = Math.abs(finalBend);
                                const autoHeight = naturalRowH + (finalBendAbs > 0.5 ? finalBendAbs : 0);
                                updateRowVisual(shape.id, rowIdx, { bend: finalBend, height: autoHeight });
                                // Compute final handle Y from the final bend value (don't use stale edgeTopY/midTopY)
                                const finalMidCenterY = (naturalRowH / 2 + finalBend + yBendOffset) * scaleY;
                                const finalMidTopY = finalMidCenterY - outlineMargin;
                                const handleY = Math.min(edgeTopY, finalMidTopY) - 10;
                                e.target.position({ x: groupW / 2, y: handleY });
                            }}
                        />
                    )}

                </Group>
            );
        });
    };

    const renderShape = (shape: Shape) => {
        const selected = selectedId === shape.id && selectedType === 'shape';
        const inGroup = selectedIds.includes(shape.id) && selectedIds.length > 1;
        const opacity = shape.opacity ?? 1;
        // When opacity is 1, strip any baked-in alpha from the color so the shape fully occludes what's behind it
        const fillColor = opaqueColor(shape.color, opacity);

        // Stroke: green = primary selected, blue = group member, otherwise default
        const strokeColor = selected ? '#00ff00' : inGroup ? '#4488ff' : (shape.strokeColor || 'transparent');

        // Select shape immediately on mouse down (not waiting for drag or click)
        const handleShapeMouseDown = (e: any) => {
            if (activeTool) return; // Don't select if a tool is active
            // Don't interfere with marquee selection in progress
            if (isMarqueeSelecting || isMarqueeRef.current) return;
            e.cancelBubble = true;
            shapeClickedRef.current = true; // Mark that a shape was clicked
            // Exit text editing when clicking any other shape
            if (editingTextId && editingTextId !== shape.id) setEditingTextId(null);
            // Start mouse long-press timer
            startMouseLongPress(e, shape.id, 'shape');
            if (e.evt?.ctrlKey || e.evt?.metaKey) {
                // Ctrl/Cmd+click: toggle in multi-select group
                setSelectedIds(prev =>
                    prev.includes(shape.id) ? prev.filter(id => id !== shape.id) : [...prev, shape.id]
                );
                setSelectedId(shape.id);
                setSelectedType('shape');
            } else {
                // Only select if not already selected (maintain focus)
                if (selectedId !== shape.id) {
                    setSelectedId(shape.id);
                    setSelectedType('shape');
                    setSelectedIds([shape.id]);
                }
            }
        };

        const baseProps = {
            key: shape.id,
            onClick: (e: any) => handleShapeClick(e, shape.id),
            onMouseDown: handleShapeMouseDown,
            onMouseUp: endMouseLongPress,
            onMouseLeave: (e: any) => {
                e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'default');
                borderResizeRef.current = { edge: null, shapeId: null };
                endMouseLongPress();
            },
            onContextMenu: (e: any) => handleContextMenu(e, shape.id, 'shape'),
            onTouchStart: (e: any) => startLongPress(e, shape.id, 'shape'),
            onTouchEnd: endLongPress,
            onTouchMove: endLongPress,
            stroke: strokeColor,
            strokeWidth: shape.strokeWidth || 2,
            opacity,
        };

        // Helper: Convert relative points to absolute screen coordinates
        const applyCenter = (points: number[]): number[] => {
            const result: number[] = [];
            for (let i = 0; i < points.length; i += 2) {
                result.push(points[i] + centerPoint.x, points[i + 1] + centerPoint.y);
            }
            return result;
        };

        // Single-shape delta drag (line/trapezoid/custom)
        const lineDragEnd = (e: any) => {
            isDraggingRef.current = true; // Prevent click handler from deselecting
            shapeClickedRef.current = true; // Keep selection after drag
            const dx = e.target.x();
            const dy = e.target.y();
            e.target.position({ x: 0, y: 0 });
            handleShapeDragDelta(shape.id, dx, dy);
        };

        // Common drag start handler - select shape on drag start
        const onShapeDragStart = () => {
            isDraggingRef.current = true;
            shapeClickedRef.current = true;
            endMouseLongPress(); // Cancel long-press when drag starts
            // Select this shape when drag starts (enables drag-to-select)
            if (selectedId !== shape.id) {
                setSelectedId(shape.id);
                setSelectedType('shape');
                setSelectedIds([shape.id]);
            }
        };

        // Determine drag props: group mode vs solo mode
        // Shapes are draggable when no tool is active (selection mode)
        const isDraggable = !activeTool;
        const isGroupDrag = inGroup && selectedIds.length > 1;
        const groupDragEnd = isGroupDrag ? handleGroupDragEnd(shape.type) : undefined;
        const groupDragStart = isGroupDrag ? (e: any) => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); groupDragInitRef.current = { x: e.target.x(), y: e.target.y() }; } : onShapeDragStart;

        if (shape.type === 'rect' && shape.svgPath) {
            // Prebuilt icon shape: render SVG path inside a resizable group
            const [x, y, w, h] = shape.points;
            const vb = shape.svgViewBox || 100;
            const scX = w / vb;
            const scY = h / vb;
            const iconStroke = shape.svgPathStroke;
            const strokeW = iconStroke ? Math.max(1, 2 / Math.min(scX, scY)) : 0;
            return (
                <ResizableGroup
                    key={shape.id}
                    x={x} y={y} width={w} height={h} rotation={shape.rotation}
                    selected={selected} isDraggable={isDraggable}
                    onSelect={() => { if (!activeTool) { shapeClickedRef.current = true; if (selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } } }}
                    onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); if (!activeTool && selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } }}
                    onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                    onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                    onTouchEnd={endLongPress}
                    onDragEnd={(newX: number, newY: number) => handleRectDrag(shape.id, newX, newY)}
                    onResizeStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); }}
                    onResizeCorner={(corner: number, newX: number, newY: number) => handleRectCornerResize(shape.id, corner, newX, newY)}
                    onResizeEdge={(edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => handleRectEdgeResize(shape.id, edge, delta)}
                    centerPoint={centerPoint} zoomScale={zoomScale}
                >
                    {(onMM) => (<>
                        {/* Background rect */}
                        <Rect x={0} y={0} width={w} height={h}
                            fill={iconStroke ? opaqueColor(shape.color, opacity) || 'transparent' : 'transparent'}
                            stroke={selected ? '#00ff00' : inGroup ? '#4488ff' : (shape.strokeColor || 'transparent')}
                            strokeWidth={shape.strokeWidth || 2}
                            opacity={opacity}
                            onMouseMove={onMM} />
                        {/* SVG icon path */}
                        <Path
                            data={shape.svgPath}
                            fill={iconStroke ? 'transparent' : (opaqueColor(shape.color, opacity) || '#ffffff')}
                            stroke={iconStroke ? '#ffffff' : 'transparent'}
                            strokeWidth={strokeW}
                            scaleX={scX} scaleY={scY}
                            listening={false}
                        />
                        {selected && <Rect x={0} y={0} width={w} height={h} stroke="#00ff00" strokeWidth={2} fill="transparent" onMouseMove={onMM} />}
                    </>)}
                </ResizableGroup>
            );
        }

        if (shape.type === 'rect') {
            const [x, y, w, h] = shape.points;
            
            return (
                <RectKonvaRenderer
                    key={shape.id}
                    rect={{
                        id: shape.id,
                        x,
                        y,
                        width: w,
                        height: h,
                        color: shape.color,
                        strokeColor: shape.strokeColor,
                        strokeWidth: shape.strokeWidth,
                        opacity: shape.opacity,
                        rotation: shape.rotation,
                    }}
                    selected={selected}
                    inGroup={inGroup}
                    isDraggable={isDraggable}
                    onSelect={() => { if (!activeTool) { shapeClickedRef.current = true; if (editingTextId && editingTextId !== shape.id) setEditingTextId(null); if (selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } } }}
                    onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); if (editingTextId) setEditingTextId(null); if (!activeTool && selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } }}
                    onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                    onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                    onTouchEnd={endLongPress}
                    onDragEnd={(newX: number, newY: number) => handleRectDrag(shape.id, newX, newY)}
                    onResizeStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); }}
                    onResizeCorner={(corner: number, newX: number, newY: number) => handleRectCornerResize(shape.id, corner, newX, newY)}
                    onResizeEdge={(edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => handleRectEdgeResize(shape.id, edge, delta)}
                    centerPoint={centerPoint}
                    zoomScale={zoomScale}
                />
            );
        } else if (shape.type === 'text') {
            const [x, y, w, h] = shape.points;
            
            return (
                <TextKonvaRenderer
                    key={shape.id}
                    textShape={{
                        id: shape.id,
                        x,
                        y,
                        width: w,
                        height: h,
                        text: shape.text,
                        fontSize: shape.fontSize,
                        color: shape.color,
                        opacity: shape.opacity,
                        rotation: shape.rotation,
                    }}
                    selected={selected}
                    inGroup={inGroup}
                    isEditing={editingTextId === shape.id}
                    isDraggable={isDraggable}
                    onSelect={() => { if (!activeTool) { shapeClickedRef.current = true; if (editingTextId && editingTextId !== shape.id) setEditingTextId(null); if (selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } } }}
                    onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); if (editingTextId) setEditingTextId(null); if (!activeTool && selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); } }}
                    onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                    onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                    onTouchEnd={endLongPress}
                    onDragEnd={(newX: number, newY: number) => handleRectDrag(shape.id, newX, newY)}
                    onResizeStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); }}
                    onResizeCorner={(corner: number, newX: number, newY: number) => handleRectCornerResize(shape.id, corner, newX, newY)}
                    onResizeEdge={(edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => handleRectEdgeResize(shape.id, edge, delta)}
                    onRequestEdit={(id: string) => startEditingText(id)}
                    centerPoint={centerPoint}
                    zoomScale={zoomScale}
                />
            );
        } else if (shape.type === 'line') {
            return (
                <Line
                    {...baseProps}
                    points={applyCenter(shape.points)}
                    fill={undefined}
                    closed={false}
                    hitStrokeWidth={8}
                    draggable={isDraggable}
                    onDragStart={groupDragStart}
                    onDragEnd={isDraggable ? (isGroupDrag ? groupDragEnd : lineDragEnd) : undefined}
                />
            );
        } else if (shape.type === 'trapezoid') {
            // Trapezoid uses only corner drawpoints for resizing, not border drag
            return (
                <Line
                    {...baseProps}
                    points={applyCenter(shape.points)}
                    fill={fillColor}
                    closed
                    hitStrokeWidth={8}
                    draggable={isDraggable}
                    onDragStart={groupDragStart}
                    onDragEnd={(e: any) => {
                        isDraggingRef.current = true;
                        shapeClickedRef.current = true;
                        if (isGroupDrag && groupDragEnd) {
                            groupDragEnd(e);
                        } else if (isDraggable) {
                            lineDragEnd(e);
                        }
                    }}
                />
            );
        } else if (shape.type === 'custom') {
            if (shape.holeRadius && shape.holeX !== undefined && shape.holeY !== undefined) {
                return (
                    <Group
                        key={shape.id}
                        opacity={opacity}
                        onClick={(e: any) => handleShapeClick(e, shape.id)}
                        onMouseDown={handleShapeMouseDown}
                        onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                        onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                        onTouchEnd={endLongPress}
                        draggable={isDraggable}
                        onDragStart={groupDragStart}
                        onDragEnd={isDraggable ? (isGroupDrag ? groupDragEnd : lineDragEnd) : undefined}
                    >
                        <Line
                            points={applyCenter(shape.points)}
                            fill={fillColor}
                            closed
                            stroke={strokeColor}
                            strokeWidth={shape.strokeWidth || 2}
                            hitStrokeWidth={8}
                        />
                        <Circle
                            x={shape.holeX + centerPoint.x}
                            y={shape.holeY + centerPoint.y}
                            radius={shape.holeRadius}
                            fill="white"
                            listening={false}
                        />
                    </Group>
                );
            }
            return (
                <Line
                    {...baseProps}
                    points={applyCenter(shape.points)}
                    fill={fillColor}
                    closed
                    hitStrokeWidth={8}
                    draggable={isDraggable}
                    onDragStart={groupDragStart}
                    onDragEnd={isDraggable ? (isGroupDrag ? groupDragEnd : lineDragEnd) : undefined}
                />
            );
        } else if (shape.type === 'circle') {
            const cx = shape.cx || 0;
            const cy = shape.cy || 0;
            const r = shape.r || 50;
            return (
                <Group
                    key={shape.id}
                    x={cx + centerPoint.x}
                    y={cy + centerPoint.y}
                    rotation={shape.rotation ?? 0}
                    opacity={opacity}
                    onClick={(e: any) => handleShapeClick(e, shape.id)}
                    onMouseDown={handleShapeMouseDown}
                    onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                    onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                    onTouchEnd={endLongPress}
                    onTouchMove={endLongPress}
                    draggable={isDraggable}
                    onDragStart={groupDragStart}
                    onDragEnd={!isGroupDrag && selected ? (e: any) => {
                        isDraggingRef.current = true;
                        shapeClickedRef.current = true;
                        // Commit final position to state; Konva already moved the group visually
                        const newCx = e.target.x() - centerPoint.x;
                        const newCy = e.target.y() - centerPoint.y;
                        setShapes(prev => prev.map(s => {
                            if (s.id !== shape.id) return s;
                            const dx = newCx - (s.cx || 0);
                            const dy = newCy - (s.cy || 0);
                            const updated: Shape = { ...s, cx: newCx, cy: newCy };
                            if (updated.seat_block?.section_rows?.length) {
                                updated.seat_block = { ...updated.seat_block, section_rows: updated.seat_block.section_rows.map((row: any) => {
                                    if (row.posX === undefined && row.posY === undefined) return row;
                                    return { ...row, ...(row.posX !== undefined ? { posX: row.posX + dx } : {}), ...(row.posY !== undefined ? { posY: row.posY + dy } : {}) };
                                }) };
                            }
                            return updated;
                        }));
                    } : (isGroupDrag ? groupDragEnd : undefined)}
                >
                    <Circle
                        x={0} y={0} radius={r}
                        fill={fillColor}
                        stroke={selected ? '#00ff00' : inGroup ? '#4488ff' : (shape.strokeColor || 'transparent')}
                        strokeWidth={shape.strokeWidth || 2}
                    />
                    {selected && (
                        // Radius handle on the right edge — drag anywhere, commits on release
                        <Circle
                            x={r} y={0}
                            radius={6}
                            fill="#00ff00" stroke="#00aa00" strokeWidth={2}
                            draggable
                            onDragStart={(e: any) => { e.cancelBubble = true; }}
                            onDragMove={(e: any) => { e.cancelBubble = true; }}
                            onDragEnd={(e: any) => {
                                e.cancelBubble = true;
                                const newRadius = Math.max(10, Math.sqrt(e.target.x() ** 2 + e.target.y() ** 2));
                                // Reset handle back to canonical position on x-axis
                                e.target.position({ x: newRadius, y: 0 });
                                setShapes(prev => prev.map(s => s.id === shape.id ? { ...s, r: newRadius } : s));
                            }}
                        />
                    )}
                </Group>
            );
        } else if (shape.type === 'group') {
            // Group shape: render as a dashed rect containing child shapes
            const [x, y, w, h] = shape.points;
            const groupX = x + centerPoint.x;
            const groupY = y + centerPoint.y;
            
            const onGroupMouseMove = selected ? (e: any) => {
                const stage = e.target.getStage();
                if (!stage) return;
                // Use getRelativePointerPosition to account for zoom/pan transformations
                const pointer = stage.getRelativePointerPosition();
                if (!pointer) return;
                const edge = detectEdge(pointer.x, pointer.y, groupX, groupY, w, h, 12);
                if (edge) {
                    const cursor = (edge === 'left' || edge === 'right') ? 'ew-resize' : 'ns-resize';
                    stage.container().style.cursor = cursor;
                    borderResizeRef.current = { edge, shapeId: shape.id };
                } else {
                    stage.container().style.cursor = 'default';
                    borderResizeRef.current = { edge: null, shapeId: null };
                }
            } : undefined;
            
            const onGroupDragMove = (e: any) => {
                if (!selected) return;
                const resizeEdge = borderResizeRef.current.edge;
                if (resizeEdge && borderResizeRef.current.shapeId === shape.id) {
                    // Border resize mode - resize group and scale children proportionally
                    e.cancelBubble = true;
                    const stage = e.target.getStage();
                    // Use getRelativePointerPosition to account for zoom/pan transformations
                    const pointer = stage?.getRelativePointerPosition();
                    if (!pointer) return;
                    
                    const shapeId = shape.id;
                    const childIds = shape.childIds || [];
                    const seatRowChildIds = childIds.filter((cid: string) => cid.startsWith('seatrow-'));
                    setShapes(prev => {
                        const curGroup = prev.find(s => s.id === shapeId);
                        if (!curGroup) return prev;
                        const [curX, curY, curW, curH] = curGroup.points;
                        
                        let newX = curX, newY = curY, newW = curW, newH = curH;
                        if (resizeEdge === 'right') {
                            newW = Math.max(30, pointer.x - centerPoint.x - newX);
                        } else if (resizeEdge === 'left') {
                            const newRight = newX + newW;
                            newX = pointer.x - centerPoint.x;
                            newW = Math.max(30, newRight - newX);
                        } else if (resizeEdge === 'bottom') {
                            newH = Math.max(30, pointer.y - centerPoint.y - newY);
                        } else if (resizeEdge === 'top') {
                            const newBottom = newY + newH;
                            newY = pointer.y - centerPoint.y;
                            newH = Math.max(30, newBottom - newY);
                        }
                        
                        const scX = newW / curW;
                        const scY = newH / curH;
                        
                        // Imperatively sync Konva node to prevent visual lag
                        e.target.position({ x: newX + centerPoint.x, y: newY + centerPoint.y });
                        e.target.width(newW);
                        e.target.height(newH);
                        
                        let updated = prev.map(s => {
                            if (s.id === shapeId) {
                                return { ...s, points: [newX, newY, newW, newH] };
                            }
                            if (childIds.includes(s.id)) {
                                if (s.type === 'rect' || s.type === 'group' || s.type === 'text') {
                                    const childRelX = s.points[0] - curX;
                                    const childRelY = s.points[1] - curY;
                                    const avgScale = (scX + scY) / 2;
                                    return { ...s, points: [
                                        newX + childRelX * scX,
                                        newY + childRelY * scY,
                                        s.points[2] * scX,
                                        s.points[3] * scY
                                    ], ...(s.type === 'text' ? { fontSize: Math.max(8, Math.round((s.fontSize || 16) * avgScale)) } : {}) };
                                } else if (s.type === 'circle') {
                                    const childRelX = (s.cx || 0) - curX;
                                    const childRelY = (s.cy || 0) - curY;
                                    const avgScale = (scX + scY) / 2;
                                    return { ...s, cx: newX + childRelX * scX, cy: newY + childRelY * scY, r: (s.r || 50) * avgScale };
                                } else {
                                    const newPoints = s.points.map((p, i) =>
                                        i % 2 === 0 ? newX + (p - curX) * scX : newY + (p - curY) * scY
                                    );
                                    return { ...s, points: newPoints };
                                }
                            }
                            return s;
                        });
                        seatRowChildIds.forEach((srId: string) => {
                            const parsed = parseSeatRowId(srId);
                            if (!parsed) return;
                            updated = updated.map(s => {
                                if (s.id !== parsed.shapeId || !s.seat_block) return s;
                                const updRows = [...(s.seat_block.section_rows || [])];
                                if (!updRows[parsed.rowIdx]) return s;
                                const row = updRows[parsed.rowIdx];
                                const shapeBbox = getShapeBBox(s);
                                const seatDiam = (row.seatRadius ?? SEAT_DOT_RADIUS) * 2;
                                const natRowH = seatDiam + ROW_PADDING * 2;
                                const baseGroupY = shapeBbox.y + natRowH * parsed.rowIdx;
                                const rowPosX = (row.posX ?? shapeBbox.x) + (row.offsetX ?? 0);
                                const rowPosY = (row.posY ?? baseGroupY) + (row.offsetY ?? 0);
                                const childRelX = rowPosX - curX;
                                const childRelY = rowPosY - curY;
                                const newOx = newX + childRelX * scX - (row.posX ?? shapeBbox.x);
                                const newOy = newY + childRelY * scY - (row.posY ?? baseGroupY);
                                const rW = row.width ?? (24 + (row.row_seats || []).length * seatDiam + ((row.row_seats || []).length - 1) * SEAT_GAP + ROW_PADDING * 2);
                                const rH = row.height ?? natRowH;
                                updRows[parsed.rowIdx] = { ...row, offsetX: newOx, offsetY: newOy, width: rW * scX, height: rH * scY };
                                return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
                            });
                        });
                        return updated;
                    });
                }
            };
            
            return (
                <Rect
                    key={shape.id}
                    x={groupX}
                    y={groupY}
                    width={w}
                    height={h}
                    fill={fillColor || '#6464ff'}
                    stroke={selected ? '#00ff00' : inGroup ? '#4488ff' : 'transparent'}
                    strokeWidth={selected || inGroup ? 2 : 0}
                    dash={[8, 4]}
                    opacity={opacity}
                    onClick={(e: any) => handleShapeClick(e, shape.id)}
                    onMouseDown={handleShapeMouseDown}
                    onMouseMove={onGroupMouseMove}
                    onMouseLeave={(e: any) => {
                        e.target.getStage()?.container()?.style && (e.target.getStage().container().style.cursor = 'default');
                        borderResizeRef.current = { edge: null, shapeId: null };
                    }}
                    onContextMenu={(e: any) => handleContextMenu(e, shape.id, 'shape')}
                    onTouchStart={(e: any) => startLongPress(e, shape.id, 'shape')}
                    onTouchEnd={endLongPress}
                    draggable={isDraggable}
                    onDragStart={() => { 
                        isDraggingRef.current = true;
                        shapeClickedRef.current = true;
                        if (selectedId !== shape.id) { setSelectedId(shape.id); setSelectedType('shape'); setSelectedIds([shape.id]); }
                    }}
                    onDragMove={selected ? onGroupDragMove : undefined}
                    onDragEnd={(e: any) => {
                        isDraggingRef.current = true;
                        shapeClickedRef.current = true; // Keep selection after drag/resize
                        const resizeEdge = borderResizeRef.current.edge;
                        borderResizeRef.current = { edge: null, shapeId: null };
                        
                        // If it was a resize, we already updated state in onDragMove
                        if (resizeEdge) return;
                        
                        // Move the group and all its children
                        const dx = e.target.x() - (x + centerPoint.x);
                        const dy = e.target.y() - (y + centerPoint.y);
                        e.target.position({ x: x + centerPoint.x, y: y + centerPoint.y });
                        if (shape.childIds) {
                            // Collect seatrow child IDs for batch update
                            const seatRowChildIds = shape.childIds.filter((cid: string) => cid.startsWith('seatrow-'));
                            setShapes(prev => {
                                let updated = prev.map(s => {
                                    if (s.id === shape.id) {
                                        return { ...s, points: [x + dx, y + dy, w, h] };
                                    }
                                    if (shape.childIds!.includes(s.id)) {
                                        // Move child shape
                                        if (s.type === 'rect' || s.type === 'group' || s.type === 'text') {
                                            return { ...s, points: [s.points[0] + dx, s.points[1] + dy, s.points[2], s.points[3]] };
                                        } else if (s.type === 'circle') {
                                            return { ...s, cx: (s.cx || 0) + dx, cy: (s.cy || 0) + dy };
                                        } else {
                                            // Polygon types
                                            const newPoints = s.points.map((p, i) => i % 2 === 0 ? p + dx : p + dy);
                                            const newHolePoints = s.holePoints?.map((p, i) => i % 2 === 0 ? p + dx : p + dy);
                                            return { ...s, points: newPoints, holePoints: newHolePoints };
                                        }
                                    }
                                    return s;
                                });
                                // Also move seatrow children by updating their offsets
                                seatRowChildIds.forEach((srId: string) => {
                                    const parsed = parseSeatRowId(srId);
                                    if (!parsed) return;
                                    updated = updated.map(s => {
                                        if (s.id !== parsed.shapeId || !s.seat_block) return s;
                                        const updRows = [...(s.seat_block.section_rows || [])];
                                        if (!updRows[parsed.rowIdx]) return s;
                                        const row = updRows[parsed.rowIdx];
                                        updRows[parsed.rowIdx] = { ...row, offsetX: (row.offsetX ?? 0) + dx, offsetY: (row.offsetY ?? 0) + dy };
                                        return { ...s, seat_block: { ...s.seat_block, section_rows: updRows } };
                                    });
                                });
                                return updated;
                            });
                        }
                    }}
                />
            );
        }
    };

    return (
        <div style={{ background: '#1a1a1a', height: '100dvh', padding: '10px', boxSizing: 'border-box', color: 'white', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <style>{`
                html, body { height: 100%; margin: 0; overflow: hidden; }
                @media (max-width: 600px) {
                    .desktop-only { display: none !important; }
                    .mobile-only { display: block !important; }
                    .toolbar { flex-direction: row !important; flex-wrap: nowrap !important; overflow-x: auto !important; }
                    .toolbar > div:first-child { flex-shrink: 0; overflow-x: auto; }
                    .toolbar button { padding: 6px 8px !important; font-size: 11px !important; white-space: nowrap; flex-shrink: 0; min-width: auto !important; }
                    .stage-container { height: calc(100dvh - 180px) !important; min-height: 300px !important; }
                    .header-title { font-size: 1rem !important; }
                    .stats-bar { font-size: 11px !important; flex-wrap: wrap; gap: 4px; }
                }
                @media (max-width: 400px) {
                    .toolbar button { padding: 4px 6px !important; font-size: 10px !important; }
                    .header-title { font-size: 0.85rem !important; }
                    .stats-bar { font-size: 10px !important; }
                }
                @media (min-width: 601px) {
                    .mobile-only { display: none !important; }
                }
                /* Prevent context menu from being hidden */
                .context-menu-wrapper {
                    position: fixed;
                    max-height: 80vh;
                    overflow-y: auto;
                }
            `}</style>

            <div className="header-title" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', margin: '10px 0' }}>
                <h2 style={{ fontSize: '1.2rem', margin: 0, whiteSpace: 'nowrap' }}>Main Events SeatMap Designer</h2>
                {hasVenueDetails ? (
                    <span onClick={openVenueDetails} title="Click to edit venue details" style={{ fontSize: '0.95rem', opacity: 0.85, cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px dashed rgba(255,255,255,0.4)', paddingBottom: '1px' }}>
                        {[venueName, venueConfiguration, typeof venueCapacity === 'number' && venueCapacity > 0 ? `${venueCapacity} cap.` : ''].filter(Boolean).join('  |  ')}
                    </span>
                ) : (
                    <button onClick={openVenueDetails} style={{ padding: '4px 12px', borderRadius: '4px', border: '1px dashed #888', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Enter venue details here</button>
                )}
                <button onClick={openImportPopup} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '4px', border: '1px solid #3498db', background: 'rgba(52,152,219,0.15)', color: '#5dade2', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', fontWeight: 500 }}>📥 Import</button>
            </div>

            <div className="stats-bar" style={{ marginBottom: '10px', fontSize: '14px', opacity: 0.8, display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                Shapes: {shapes.length} | Selected: {selectedId || 'None'}
                &nbsp;&nbsp;|&nbsp;&nbsp;
                W: <strong>{stageSize.width.toFixed(1)}px</strong>&nbsp; H: <strong>{stageSize.height.toFixed(1)}px</strong>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                Zoom: <strong>{Math.round(zoomScale * 100)}%</strong>
            </div>

            <input type="file" ref={fileInputRef} onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                    const url = URL.createObjectURL(file);
                    const domImg = new window.Image();
                    domImg.onload = () => {
                        const maxW = stageSize.width * 0.5;
                        const maxH = stageSize.height * 0.5;
                        const aspectRatio = domImg.width / domImg.height;
                        let w = Math.min(domImg.width, maxW);
                        let h = w / aspectRatio;
                        if (h > maxH) { h = maxH; w = h * aspectRatio; }
                        setImages(prev => [...prev, {
                            id: `img-${Date.now()}`,
                            x: -w / 2,          // center on world origin
                            y: -h / 2,
                            width: Math.round(w),
                            height: Math.round(h),
                            url,
                            zIndex: 100,
                        }]);
                    };
                    domImg.onerror = () => {
                        // Fallback: add placeholder image at center
                        setImages(prev => [...prev, {
                            id: `img-${Date.now()}`,
                            x: -100, y: -60, width: 200, height: 120, url, zIndex: 100,
                        }]);
                    };
                    domImg.src = url;
                }
            }} style={{ display: 'none' }} accept="image/*" />

            {/* Hidden file input for venue interior preview media */}
            <input type="file" ref={venuePreviewInputRef} onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                    setVenuePreviewFile(file);
                    setVenuePreviewUrl(URL.createObjectURL(file));
                }
                e.target.value = '';
            }} style={{ display: 'none' }} accept="image/*,video/mp4,video/webm" />

            <div ref={toolbarRef} className="toolbar" data-no-deselect style={{
                marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#333', padding: '10px', borderRadius: '8px', gap: '10px', flexWrap: 'nowrap', overflow: 'hidden'
            }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 1, overflow: 'hidden' }}>
                    {visibleTools.map(tool => {
                        const isAddRow = (tool as any).isAddRow;
                        const isPrebuilt = (tool as any).isPrebuilt;
                        const addRowEnabled = isAddRow && selectedId && selectedType === 'shape' && shapes.find(s => s.id === selectedId)?.seat_block;
                        const buttonStyles = tool.isImageBtn
                            ? { ...buttonStyle(false), background: '#27ae60' }
                            : (tool as any).isReset
                                ? { ...buttonStyle(false), background: '#c0392b' }
                                : isAddRow
                                    ? { ...buttonStyle(false), background: addRowEnabled ? 'rgba(39,174,96,0.25)' : '#333', border: addRowEnabled ? '1px solid #27ae60' : '1px solid #555', color: addRowEnabled ? '#2ecc71' : '#666', opacity: addRowEnabled ? 1 : 0.5 }
                                    : isPrebuilt
                                        ? { ...buttonStyle(prebuiltMenuOpen), background: prebuiltMenuOpen ? '#6c3483' : '#8e44ad', position: 'relative' as const }
                                        : buttonStyle(activeTool === tool.id);
                        return (
                            <div key={tool.id} style={{ position: 'relative', display: 'inline-block' }}>
                                <button onClick={isPrebuilt ? (e: React.MouseEvent<HTMLButtonElement>) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setPrebuiltMenuPos({ top: rect.bottom + 4, left: rect.left });
                                    setPrebuiltMenuOpen(prev => !prev);
                                } : tool.action} style={buttonStyles}>
                                    {tool.label}
                                </button>
                            </div>
                        );
                    })}
                    {/* Show More button when there are overflow tools */}
                    {hasOverflow && (
                        <button onClick={() => setShowPopup(true)} style={{ ...buttonStyle(false), background: '#444' }}>➕ More</button>
                    )}
                </div>

                <div ref={rightButtonsRef} className="mode-switcher" style={{ display: 'flex', gap: '5px', marginLeft: '30px' }}>
                    {tempPoints.length > 0 && <button onClick={finishCustomShape} style={{...buttonStyle(false), background: '#e67e22'}}>Finish</button>}
                    <button onClick={handleSaveClick} style={{ ...buttonStyle(false), background: '#27ae60' }}>💾 Save</button>
                    {/* Text formatting controls were moved to the shape control panel (bottom-right) */}
                </div>
            </div>

            {/* Prebuilt icons dropdown — position:fixed escapes overflow:hidden toolbar containers */}
            {prebuiltMenuOpen && (
                <div data-no-deselect style={{
                    position: 'fixed', top: prebuiltMenuPos.top, left: prebuiltMenuPos.left,
                    background: '#2c2c2c', border: '1px solid #555', borderRadius: 8,
                    padding: 6, zIndex: 9999, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                }}>
                    {PREBUILT_ICONS.map(icon => (
                        <button key={icon.id} onClick={() => addPrebuiltIcon(icon)} style={{
                            display: 'block', width: '100%', padding: '8px 12px',
                            background: 'transparent', color: '#fff', border: 'none',
                            cursor: 'pointer', textAlign: 'left', fontSize: 13,
                            borderRadius: 4, whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#444')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            {icon.emoji} {icon.label}
                        </button>
                    ))}
                </div>
            )}

            {showPopup && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <h3 style={{ marginTop: 0 }}>{hasOverflow ? 'More Tools' : 'Actions'}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            {/* Show overflow tools that didn't fit in toolbar */}
                            {overflowTools.map(tool => {
                                const s = (tool as any).isReset ? { ...popupBtnStyle, color: 'red' } : popupBtnStyle;
                                return (
                                    <button key={tool.id} onClick={() => { tool.action(); setShowPopup(false); }} style={s}>
                                        {tool.label}
                                    </button>
                                );
                            })}
                        </div>
                        <button onClick={() => setShowPopup(false)} style={{ marginTop: '20px', width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', cursor: 'pointer' }}>Close</button>
                    </div>
                </div>
            )}

            {/* ── Text formatting toolbar (shown when editing a text shape) ── */}
            {editingTextId && (
                <div data-text-toolbar style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px',
                    background: '#1e1e1e', borderRadius: 6, margin: '0 auto 2px auto',
                    width: 'fit-content', border: '1px solid #444',
                }}>
                    <button title="Bold (Ctrl+B)" onClick={() => { document.execCommand('bold'); editingOverlayRef.current?.focus(); }}
                        style={{ fontWeight: 'bold', fontSize: 15, padding: '3px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 4, cursor: 'pointer' }}>B</button>
                    <button title="Italic (Ctrl+I)" onClick={() => { document.execCommand('italic'); editingOverlayRef.current?.focus(); }}
                        style={{ fontStyle: 'italic', fontSize: 15, padding: '3px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 4, cursor: 'pointer' }}>I</button>
                    <button title="Cursive / Script" onClick={() => {
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0 && sel.toString().length > 0) {
                            const range = sel.getRangeAt(0);
                            const span = document.createElement('span');
                            span.style.fontFamily = 'cursive';
                            range.surroundContents(span);
                        }
                        editingOverlayRef.current?.focus();
                    }} style={{ fontFamily: 'cursive', fontSize: 15, padding: '3px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 4, cursor: 'pointer' }}>C</button>
                    <div style={{ width: 1, height: 22, background: '#555', margin: '0 4px' }} />
                    <button title="Decrease font size" onClick={() => {
                        setShapes(prev => prev.map(s => s.id === editingTextId && s.type === 'text'
                            ? { ...s, fontSize: Math.max(8, (s.fontSize || 16) - 2) } : s));
                    }} style={{ fontSize: 15, padding: '3px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 4, cursor: 'pointer' }}>A−</button>
                    <span style={{ color: '#aaa', fontSize: 12, minWidth: 30, textAlign: 'center' }}>
                        {shapes.find(s => s.id === editingTextId)?.fontSize || 16}
                    </span>
                    <button title="Increase font size" onClick={() => {
                        setShapes(prev => prev.map(s => s.id === editingTextId && s.type === 'text'
                            ? { ...s, fontSize: Math.min(200, (s.fontSize || 16) + 2) } : s));
                    }} style={{ fontSize: 15, padding: '3px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 4, cursor: 'pointer' }}>A+</button>
                </div>
            )}

            <div
                ref={stageContainerRef}
                className="stage-container"
                onContextMenu={(e) => e.preventDefault()}
                style={{
                    background: '#fff',
                    border: '2px solid #555',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    flex: 1,
                    position: 'relative',
                    margin: '0 auto',
                    width: '90vw',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                }}
            >
                {stageSize.width > 0 && (
                    <div 
                        onClick={handleStageClick} 
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ cursor: activeTool ? 'crosshair' : 'default' }}
                    >
                        {/* @ts-ignore react-konva Stage children typing issue */}
                        <Stage
                            ref={stageRef}
                            width={stageSize.width}
                            height={stageSize.height}
                            scaleX={zoomScale}
                            scaleY={zoomScale}
                            offsetX={stageSize.width / 2}
                            offsetY={stageSize.height / 2}
                            x={stageSize.width / 2 + panOffset.x}
                            y={stageSize.height / 2 + panOffset.y}
                            onMouseDown={handleMarqueeStart}
                            onMouseMove={handleMarqueeMove}
                            onMouseUp={handleMarqueeEnd}
                            onTouchStart={handleMarqueeStart}
                            onTouchMove={handleMarqueeMove}
                            onTouchEnd={handleMarqueeEnd}
                            onContextMenu={(e) => e.evt.preventDefault()}
                        >
                        <Layer>
                            {images.map(img => (
                                <ImageKonvaRenderer
                                    key={img.id}
                                    img={img}
                                    selected={selectedId === img.id && selectedType === 'image'}
                                    isDraggable={!activeTool}
                                    centerPoint={centerPoint}
                                    zoomScale={zoomScale}
                                    onSelect={() => { if (!activeTool) { shapeClickedRef.current = true; if (selectedId !== img.id) { setSelectedId(img.id); setSelectedType('image'); setSelectedIds([]); } } }}
                                    onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); if (!activeTool && selectedId !== img.id) { setSelectedId(img.id); setSelectedType('image'); setSelectedIds([]); } }}
                                    onContextMenu={(e: any) => handleContextMenu(e, img.id, 'image')}
                                    onMouseDown={(e: any) => { shapeClickedRef.current = true; if (editingTextId) setEditingTextId(null); startMouseLongPress(e, img.id, 'image'); }}
                                    onMouseUp={endMouseLongPress}
                                    onDragEnd={(x: number, y: number) => handleImageDrag(img.id, x, y)}
                                    onResizeStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); }}
                                    onResizeCorner={(corner: number, newX: number, newY: number, proportional: boolean) => handleImageCornerResize(img.id, corner, newX, newY, proportional)}
                                    onResizeEdge={(edge: 'left' | 'right' | 'top' | 'bottom', delta: number) => handleImageEdgeResize(img.id, edge, delta)}
                                    onRotate={(rotation: number) => handleImageRotate(img.id, rotation)}
                                    onTouchStart={(e: any) => startLongPress(e, img.id, 'image')}
                                    onTouchEnd={endLongPress}
                                />
                            ))}
                        </Layer>

                        <Layer>
                            {sortedShapes.map(shape => renderShape(shape))}
                        </Layer>

                        {/* Seat-rows overlay layer — renders seat dots inside seat-block shapes */}
                        <Layer>
                            {shapes.filter(s => s.seat_block && (s.seat_block.section_rows || []).length > 0).map(shape => (
                                <React.Fragment key={`seatrows-${shape.id}`}>
                                    {renderSeatRows(shape)}
                                </React.Fragment>
                            ))}
                        </Layer>

                        <Layer>
                            {sortedSeats.map(seat => {
                                const seatRadius = seat.radius || 12;
                                const isSelected = selectedId === seat.id && selectedType === 'seat';
                                return (
                                    <React.Fragment key={seat.id}>
                                        <Circle
                                            x={seat.x + centerPoint.x}
                                            y={seat.y + centerPoint.y}
                                            radius={seatRadius}
                                            fill={seat.color || '#87ceeb'}
                                            opacity={seat.opacity ?? 1}
                                            stroke={isSelected ? '#00ff00' : 'transparent'}
                                            strokeWidth={2}
                                            onClick={(e: any) => handleSeatClick(e, seat.id)}
                                            onMouseDown={(e: any) => {
                                                if (activeTool) return;
                                                e.cancelBubble = true;
                                                shapeClickedRef.current = true;
                                                if (editingTextId) setEditingTextId(null);
                                                startMouseLongPress(e, seat.id, 'seat');
                                                if (selectedId !== seat.id) {
                                                    setSelectedId(seat.id);
                                                    setSelectedType('seat');
                                                    setSelectedIds([]);
                                                }
                                            }}
                                            onMouseUp={endMouseLongPress}
                                            onMouseLeave={() => endMouseLongPress()}
                                            onContextMenu={(e: any) => handleContextMenu(e, seat.id, 'seat')}
                                            onTouchStart={(e: any) => startLongPress(e, seat.id, 'seat')}
                                            onTouchEnd={endLongPress}
                                            onTouchMove={endLongPress}
                                            draggable={!activeTool}
                                            onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; endMouseLongPress(); if (selectedId !== seat.id) { setSelectedId(seat.id); setSelectedType('seat'); setSelectedIds([]); } }}
                                            onDragMove={!activeTool ? (e: any) => handleSeatDrag(seat.id, e.target.x(), e.target.y()) : undefined}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </Layer>

                        <Layer>
                            {tempPoints.map((_, i) => {
                                if (i % 2 === 0) {
                                    return (
                                        <Circle
                                            key={`temp-${i}`}
                                            x={tempPoints[i] + centerPoint.x}
                                            y={tempPoints[i + 1] + centerPoint.y}
                                            radius={6}
                                            fill="#ffaa00"
                                            stroke="#ff6600"
                                            strokeWidth={1}
                                        />
                                    );
                                }
                                return null;
                            })}
                            {tempPoints.length >= 2 && (
                                <Line
                                    points={tempPoints.map((val, i) => i % 2 === 0 ? val + centerPoint.x : val + centerPoint.y)}
                                    stroke="#ffaa00"
                                    strokeWidth={2}
                                    fill="transparent"
                                />
                            )}
                        </Layer>

                        <Layer>
                            {selectedType === 'shape' && selectedId
                                ? (() => {
                                    const shape = shapes.find(s => s.id === selectedId);
                                    if (!shape) return null;

                                    let editPoints: { x: number; y: number }[] = [];

                                    if (shape.type === 'rect' || shape.type === 'text') {
                                        // Rect and text shapes use their own built-in handles
                                        editPoints = [];
                                    } else if (shape.type === 'circle') {
                                        // For circles, show center point (handled by group drag)
                                        editPoints = [];
                                    } else if (shape.type === 'custom' && shape.holeRadius !== undefined) {
                                        // For custom shapes with circular hole, only show OUTER points (inner stays as circle)
                                        const outerCount = shape.points.length / 2;
                                        for (let i = 0; i < outerCount; i++) {
                                            editPoints.push({ x: shape.points[i * 2], y: shape.points[i * 2 + 1] });
                                        }
                                    } else if (shape.type === 'group') {
                                        // For groups, show 4 corner points like a rect
                                        const [x, y, w, h] = shape.points;
                                        editPoints = [
                                            { x, y },                    // 0: top-left
                                            { x: x + w, y },             // 1: top-right
                                            { x: x + w, y: y + h },      // 2: bottom-right
                                            { x, y: y + h }              // 3: bottom-left
                                        ];
                                    } else {
                                        // For other shapes, use point pairs directly
                                        const pointCount = shape.points.length / 2;
                                        for (let i = 0; i < pointCount; i++) {
                                            editPoints.push({ x: shape.points[i * 2], y: shape.points[i * 2 + 1] });
                                        }
                                    }

                                    return editPoints.map((point, i) => {
                                        return (
                                        <Circle
                                            key={`edit-point-${i}`}
                                            x={point.x + centerPoint.x}
                                            y={point.y + centerPoint.y}
                                            radius={8}
                                            fill={'#00ff00'}
                                            stroke={'#00aa00'}
                                            strokeWidth={2}
                                            draggable
                                            onDragStart={() => { isDraggingRef.current = true; shapeClickedRef.current = true; handlePointDragStart(i); }}
                                            onDragMove={(e: any) => {
                                                e.cancelBubble = true;
                                                // Live update for all shapes with edit points
                                                const pos = e.target.position();
                                                handlePointDrag(i, pos.x, pos.y);
                                            }}
                                            onDragEnd={() => {
                                                handlePointDragEnd();
                                            }}
                                        />
                                    )});
                                })()
                                : null
                            }
                        </Layer>

                        <Layer>
                            {/* Anchor Point — draggable reference (e.g. stage center, venue center)
                                Stored in world-space; rendered at anchorPoint + centerPoint (screen). */}
                            <Circle
                                x={anchorPoint.x + centerPoint.x}
                                y={anchorPoint.y + centerPoint.y}
                                radius={10}
                                fill="#ff9900"
                                stroke="#ffffff"
                                strokeWidth={2}
                                draggable={true}
                                listening={true}
                                onClick={(e: any) => {
                                    e.cancelBubble = true;
                                    setAnchorForm({ description: anchorPoint.description || '', x: String(anchorPoint.x), y: String(anchorPoint.y) });
                                    setAnchorPopupOpen(true);
                                }}
                                onDragStart={() => { isDraggingRef.current = true; }}
                                onDragMove={(e: any) => {
                                    setAnchorPoint({
                                        x: e.target.x() - centerPoint.x,
                                        y: e.target.y() - centerPoint.y,
                                    });
                                }}
                            />
                            {/* Anchor crosshair */}
                            <Line
                                points={[
                                    anchorPoint.x + centerPoint.x - 20, anchorPoint.y + centerPoint.y,
                                    anchorPoint.x + centerPoint.x + 20, anchorPoint.y + centerPoint.y,
                                ]}
                                stroke="#ff9900"
                                strokeWidth={1}
                                opacity={0.8}
                                listening={false}
                            />
                            <Line
                                points={[
                                    anchorPoint.x + centerPoint.x, anchorPoint.y + centerPoint.y - 20,
                                    anchorPoint.x + centerPoint.x, anchorPoint.y + centerPoint.y + 20,
                                ]}
                                stroke="#ff9900"
                                strokeWidth={1}
                                opacity={0.8}
                                listening={false}
                            />
                            <Text
                                x={anchorPoint.x + centerPoint.x + 14}
                                y={anchorPoint.y + centerPoint.y - 8}
                                text={`${anchorPoint.description ? anchorPoint.description + ' ' : ''}Anchor (${Number(anchorPoint.x).toFixed(2)}px, ${Number(anchorPoint.y).toFixed(2)}px)`}
                                fill="#ff9900"
                                fontSize={11}
                                listening={false}
                            />
                        </Layer>

                        {/* Marquee selection rectangle */}
                        {isMarqueeSelecting && marqueeRect && (
                            <Layer>
                                <Rect
                                    x={marqueeRect.x + centerPoint.x}
                                    y={marqueeRect.y + centerPoint.y}
                                    width={marqueeRect.width}
                                    height={marqueeRect.height}
                                    fill="rgba(66, 165, 245, 0.2)"
                                    stroke="#42a5f5"
                                    strokeWidth={1}
                                    dash={[4, 4]}
                                    listening={false}
                                />
                            </Layer>
                        )}
                    </Stage>
                    </div>
                )}

                <textarea
                    id="__sm_hidden_textarea"
                    style={{ position: 'fixed', left: -9999, top: -9999, opacity: 0, width: 1, height: 1 }}
                    readOnly
                />

                {/* ── Inline rich-text editor overlay for text shapes ── */}
                {editingTextId && (() => {
                    const editShape = shapes.find(s => s.id === editingTextId && s.type === 'text');
                    if (!editShape) return null;
                    const shapeW = editShape.points[2] ?? 50;
                    const shapeH = editShape.points[3] ?? 20;
                    // Calculate screen position: apply Konva stage transform (world → screen)
                    // Stage transform: screenPos = (worldPos - offset) * scale + position
                    const worldX = editShape.points[0] + centerPoint.x;
                    const worldY = editShape.points[1] + centerPoint.y;
                    const screenX = (worldX - stageSize.width / 2) * zoomScale + stageSize.width / 2 + panOffset.x;
                    const screenY = (worldY - stageSize.height / 2) * zoomScale + stageSize.height / 2 + panOffset.y;
                    return (
                        <div
                            ref={editingOverlayRef}
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => {
                                const div = e.currentTarget as HTMLDivElement;
                                const html = div.innerHTML;
                                handleTextChange(editingTextId, html);
                                // Resize both width and height to fit content (allows shrinking too)
                                requestAnimationFrame(() => {
                                    resizeTextShape(editingTextId, div.scrollWidth, div.scrollHeight);
                                });
                            }}
                            onKeyDown={(e) => {
                                // Prevent global shortcuts when typing in text editor
                                e.stopPropagation();
                                if (e.key === 'Escape') {
                                    setEditingTextId(null);
                                }
                                // Tab inserts a line break (vertical expansion)
                                if (e.key === 'Tab') {
                                    e.preventDefault();
                                    document.execCommand('insertLineBreak');
                                    const div = editingOverlayRef.current;
                                    if (div) {
                                        handleTextChange(editingTextId, div.innerHTML);
                                        requestAnimationFrame(() => {
                                            resizeTextShape(editingTextId, div.scrollWidth, div.scrollHeight);
                                        });
                                    }
                                }
                                // Enter saves text and exits focus mode (keeps shape selected)
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const div = editingOverlayRef.current;
                                    if (div) {
                                        // Measure dimensions synchronously BEFORE closing the overlay
                                        const html = div.innerHTML;
                                        const sw = div.scrollWidth;
                                        const sh = div.scrollHeight;
                                        handleTextChange(editingTextId, html);
                                        resizeTextShape(editingTextId, sw, sh);
                                    }
                                    setEditingTextId(null);
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                position: 'absolute',
                                left: screenX,
                                top: screenY,
                                minWidth: 40 * zoomScale,
                                minHeight: 22 * zoomScale,
                                width: 'auto',
                                fontSize: (editShape.fontSize || 16) * zoomScale,
                                color: editShape.color || '#ffffff',
                                fontFamily: 'Arial, sans-serif',
                                lineHeight: 1.2,
                                padding: 4 * zoomScale,
                                outline: '2px solid #00ff00',
                                background: 'rgba(0,0,0,0.6)',
                                whiteSpace: 'nowrap',
                                direction: 'ltr',
                                textAlign: 'left',
                                zIndex: 10000,
                                cursor: 'text',
                                boxSizing: 'border-box',
                                transformOrigin: 'top left',
                            }}
                        />
                    );
                })()}

                {/* ── Venue Interior Preview – bottom-left ── */}
                <div
                    data-no-deselect
                    style={{
                        position: 'absolute',
                        bottom: '14px',
                        left: '14px',
                        zIndex: 10,
                    }}
                >
                    {venuePreviewUrl ? (
                        <div
                            onClick={() => setVenuePreviewModalOpen(true)}
                            title="View venue preview"
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '2px solid #27ae60',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                                background: '#000',
                            }}
                        >
                            {venuePreviewFile?.type.startsWith('video/') || venuePreviewUrl.match(/\.(mp4|webm)$/i) ? (
                                <video
                                    src={venuePreviewUrl}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    muted
                                />
                            ) : (
                                <img
                                    src={venuePreviewUrl}
                                    alt="Venue preview"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )}
                        </div>
                    ) : (
                        <button
                            onClick={() => venuePreviewInputRef.current?.click()}
                            title="Add venue interior preview"
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                border: '2px dashed #888',
                                background: 'rgba(255,255,255,0.85)',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                fontSize: 10,
                                color: '#555',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                        >
                            <span style={{ fontSize: 20 }}>📷</span>
                            <span>Preview</span>
                        </button>
                    )}
                </div>

                {/* ── Control panel at bottom-right ── */}
                <div style={{
                    position: 'absolute',
                    bottom: '14px',
                    right: '14px',
                    display: 'flex',
                    flexDirection: 'column',

                    gap: '6px',
                    zIndex: 10,
                }}>
                    {/* Top row: seat size ● (seatrow only) + context-menu ⋮ (any shape) */}
                    {selectedType === 'shape' && selectedId && (
                        <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-end', marginRight: '6px', gap: '4px' }}>
                            {selectedId.startsWith('seatrow-') && (
                                <button
                                    title="Seat size"
                                    onClick={() => {
                                        setEditPanelMode('seatsize');
                                        setEditInputValue(String(getSeatRowRadius(selectedId)));
                                    }}
                                    style={{
                                        ...zoomBtnStyle,
                                        width: '36px',
                                        height: '36px',
                                        background: editPanelMode === 'seatsize' ? '#e67e22' : undefined,
                                        border: editPanelMode === 'seatsize' ? '2px solid #f0a04b' : undefined,
                                    }}
                                >●</button>
                            )}
                            <button
                                title="Open shape menu"
                                onClick={(e) => handleContextMenu(e, selectedId as string, 'shape')}
                                style={{ ...zoomBtnStyle, width: '36px', height: '36px' }}
                            >⋮</button>
                        </div>
                    )}
                    {/* Shape edit controls - shown above zoom when shape selected */}
                    {selectedId && (selectedType === 'shape' || selectedType === 'image' || selectedType === 'seat') && (
                        <div style={{
                            width: '80px',
                            background: 'rgba(30,30,30,0.9)',
                            border: '1px solid #555',
                            borderRadius: '6px',
                            padding: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}>
                            {/* Top row: Rotation buttons (left) | Scale button (right) with mode selector */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2px' }}>
                                {/* Rotation buttons */}
                                <div style={{ display: 'flex', gap: '2px' }}>
                                    <button
                                        title="Rotate left"
                                        onClick={() => {
                                            if (selectedType === 'seat') return; // Seats don't rotate
                                            setEditPanelMode('rotate');
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) rotateSeatRow(selectedId, -15);
                                                else rotateShape(selectedId, -15);
                                            }
                                            else if (selectedType === 'image') rotateImage(selectedId, -15);
                                            setEditInputValue(prev => String(parseInt(prev || '0') - 15));
                                        }}
                                        style={{
                                            ...zoomBtnStyle,
                                            width: '24px',
                                            height: '24px',
                                            fontSize: '12px',
                                            background: editPanelMode === 'rotate' ? '#3498db' : 'rgba(30,30,30,0.85)',
                                            border: editPanelMode === 'rotate' ? '2px solid #5dade2' : '1px solid #555',
                                            opacity: selectedType === 'seat' ? 0.3 : 1,
                                        }}
                                    >↺</button>
                                    <button
                                        title="Rotate right"
                                        onClick={() => {
                                            if (selectedType === 'seat') return; // Seats don't rotate
                                            setEditPanelMode('rotate');
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) rotateSeatRow(selectedId, 15);
                                                else rotateShape(selectedId, 15);
                                            }
                                            else if (selectedType === 'image') rotateImage(selectedId, 15);
                                            setEditInputValue(prev => String(parseInt(prev || '0') + 15));
                                        }}
                                        style={{
                                            ...zoomBtnStyle,
                                            width: '24px',
                                            height: '24px',
                                            fontSize: '12px',
                                            background: editPanelMode === 'rotate' ? '#3498db' : 'rgba(30,30,30,0.85)',
                                            border: editPanelMode === 'rotate' ? '2px solid #5dade2' : '1px solid #555',
                                            opacity: selectedType === 'seat' ? 0.3 : 1,
                                        }}
                                    >↻</button>
                                </div>
                                {/* Scale button */}
                                <button
                                    title="Scale mode"
                                    onClick={() => setEditPanelMode('scale')}
                                    style={{
                                        ...zoomBtnStyle,
                                        width: '24px',
                                        height: '24px',
                                        fontSize: '12px',
                                        background: editPanelMode === 'scale' ? '#27ae60' : 'rgba(30,30,30,0.85)',
                                        border: editPanelMode === 'scale' ? '2px solid #58d68d' : '1px solid #555',
                                    }}
                                >⊕</button>
                            </div>
                            
                            {/* Value row: - input + */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px' }}>
                                <button
                                    title={editPanelMode === 'rotate' ? 'Rotate left 1°' : editPanelMode === 'seatsize' ? 'Shrink seat 1px' : 'Shrink 5%'}
                                    onClick={() => {
                                        if (editPanelMode === 'seatsize' && selectedId?.startsWith('seatrow-')) {
                                            const cur = getSeatRowRadius(selectedId);
                                            setSeatRowRadius(selectedId, cur - 1);
                                            setEditInputValue(String(Math.max(2, cur - 1)));
                                        } else if (editPanelMode === 'rotate') {
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) rotateSeatRow(selectedId, -1);
                                                else rotateShape(selectedId, -1);
                                            }
                                            else if (selectedType === 'image') rotateImage(selectedId, -1);
                                            if (selectedType !== 'seat') setEditInputValue(prev => String(parseInt(prev || '0') - 1));
                                        } else {
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) scaleSeatRow(selectedId, 1 / 1.05);
                                                else scaleShape(selectedId, 1 / 1.05);
                                            }
                                            else if (selectedType === 'image') scaleImage(selectedId, 1 / 1.05);
                                            else if (selectedType === 'seat') scaleSeat(selectedId, 1 / 1.05);
                                        }
                                    }}
                                    style={{ ...zoomBtnStyle, width: '20px', height: '20px', fontSize: '12px' }}
                                >−</button>
                                <input
                                    type="text"
                                    value={editInputValue}
                                    onChange={(e) => setEditInputValue(e.target.value)}
                                    placeholder={editPanelMode === 'rotate' ? '0°' : editPanelMode === 'seatsize' ? '6' : '100%'}
                                    onBlur={() => {
                                        const val = parseFloat(editInputValue);
                                        if (isNaN(val)) return;
                                        if (editPanelMode === 'seatsize' && selectedId?.startsWith('seatrow-')) {
                                            setSeatRowRadius(selectedId, val);
                                            setEditInputValue(String(Math.max(2, Math.min(50, val))));
                                        } else if (editPanelMode === 'rotate') {
                                            let current = 0;
                                            if (selectedType === 'shape' && selectedId?.startsWith('seatrow-')) {
                                                const parsed = parseSeatRowId(selectedId);
                                                if (parsed) {
                                                    const sh = shapes.find(s => s.id === parsed.shapeId);
                                                    current = sh?.seat_block?.section_rows?.[parsed.rowIdx]?.rotation ?? 0;
                                                }
                                            } else if (selectedType === 'shape') {
                                                current = shapes.find(s => s.id === selectedId)?.rotation ?? 0;
                                            } else {
                                                current = images.find(i => i.id === selectedId)?.rotation ?? 0;
                                            }
                                            const delta = val - current;
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) rotateSeatRow(selectedId, delta);
                                                else rotateShape(selectedId, delta);
                                            } else rotateImage(selectedId, delta);
                                        } else {
                                            if (val > 0) {
                                                const factor = val / 100;
                                                if (selectedType === 'shape') {
                                                    if (selectedId?.startsWith('seatrow-')) scaleSeatRow(selectedId, factor);
                                                    else scaleShape(selectedId, factor);
                                                } else scaleImage(selectedId, factor);
                                                setEditInputValue('100');
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    }}
                                    style={{
                                        width: '32px',
                                        padding: '3px',
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        border: '1px solid #555',
                                        borderRadius: '3px',
                                        background: '#222',
                                        color: '#fff',
                                    }}
                                />
                                <button
                                    title={editPanelMode === 'rotate' ? 'Rotate right 1°' : editPanelMode === 'seatsize' ? 'Grow seat 1px' : 'Grow 5%'}
                                    onClick={() => {
                                        if (editPanelMode === 'seatsize' && selectedId?.startsWith('seatrow-')) {
                                            const cur = getSeatRowRadius(selectedId);
                                            setSeatRowRadius(selectedId, cur + 1);
                                            setEditInputValue(String(Math.min(50, cur + 1)));
                                        } else if (editPanelMode === 'rotate') {
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) rotateSeatRow(selectedId, 1);
                                                else rotateShape(selectedId, 1);
                                            }
                                            else if (selectedType === 'image') rotateImage(selectedId, 1);
                                            if (selectedType !== 'seat') setEditInputValue(prev => String(parseInt(prev || '0') + 1));
                                        } else {
                                            if (selectedType === 'shape') {
                                                if (selectedId?.startsWith('seatrow-')) scaleSeatRow(selectedId, 1.05);
                                                else scaleShape(selectedId, 1.05);
                                            }
                                            else if (selectedType === 'image') scaleImage(selectedId, 1.05);
                                            else if (selectedType === 'seat') scaleSeat(selectedId, 1.05);
                                        }
                                    }}
                                    style={{ ...zoomBtnStyle, width: '20px', height: '20px', fontSize: '12px' }}
                                >+</button>
                            </div>
                            {/* Mode label */}
                            <span style={{ color: '#888', fontSize: '9px', textAlign: 'center' }}>
                                {editPanelMode === 'rotate' ? 'Angle (°)' : editPanelMode === 'seatsize' ? 'Seat r (px)' : 'Scale (%)'}
                            </span>
                        </div>
                    )}
                    {/* Zoom controls - always visible */}
                    <div style={{
                        width: '40px',
                        background: 'rgba(30,30,30,0.9)',
                        border: '1px solid #555',
                        borderRadius: '6px',
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'center',
                        alignSelf: 'flex-end',
                    }}>
                        <button
                            title="Zoom in"
                            onClick={() => setZoomScale(z => Math.min(5, parseFloat((z + 0.1).toFixed(2))))}
                            style={{ ...zoomBtnStyle, width: '28px', height: '28px' }}
                        >+</button>
                        <span style={{ color: '#fff', fontSize: '9px', textAlign: 'center' }}>{Math.round(zoomScale * 100)}%</span>
                        <button
                            title="Zoom out"
                            onClick={() => setZoomScale(z => Math.max(0.1, parseFloat((z - 0.1).toFixed(2))))}
                            style={{ ...zoomBtnStyle, width: '28px', height: '28px' }}
                        >−</button>
                    </div>
                </div>
            </div>

            {contextMenu && (
                <div 
                    className="context-menu-wrapper"
                    data-no-deselect
                    style={{
                        position: 'fixed',
                        top: `${contextMenu.y}px`,
                        left: `${contextMenu.x}px`,
                        background: '#333',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        padding: '5px 0',
                        zIndex: 10000,
                        minWidth: '180px',
                        maxHeight: 'calc(100vh - 20px)',
                        overflowY: 'auto',
                    }} 
                    onMouseLeave={() => setContextMenu(null)}
                >
                    <button onClick={() => removeElement(contextMenu.targetId, contextMenu.targetType)} style={contextMenuBtnStyle}>🗑️ Remove</button>
                    <button onClick={() => duplicateElement(contextMenu.targetId, contextMenu.targetType)} style={contextMenuBtnStyle}>📋 Duplicate</button>
                    <button onClick={() => setZLayer(0)} style={contextMenuBtnStyle}>📤 Send to Back</button>
                    <button onClick={() => setZLayer(1000)} style={contextMenuBtnStyle}>📥 Bring to Front</button>
                    <button onClick={() => openColorPicker(contextMenu.targetId, contextMenu.targetType)} style={contextMenuBtnStyle}>🎨 Change Color</button>
                    <button onClick={() => openPropsDialog(contextMenu.targetId, contextMenu.targetType)} style={contextMenuBtnStyle}>⚙️ Edit Properties</button>
                    {/* Add Row: visible when the target is a shape with seat_block metadata */}
                    {contextMenu.targetType === 'shape' && (() => {
                        const shape = shapes.find(s => s.id === contextMenu.targetId);
                        if (shape && shape.seat_block) {
                            return (
                                <button onClick={() => openAddRowDialog(contextMenu.targetId)} style={contextMenuBtnStyle}>➕ Add Row</button>
                            );
                        }
                        return null;
                    })()}
                    {contextMenu.targetType === 'shape' && (
                        <>
                            <hr style={{ border: 'none', borderTop: '1px solid #555', margin: '4px 0' }} />
                            <button onClick={() => rotateShape(contextMenu.targetId, -15)} style={contextMenuBtnStyle}>↺ Rotate Left 15°</button>
                            <button onClick={() => rotateShape(contextMenu.targetId, 15)} style={contextMenuBtnStyle}>↻ Rotate Right 15°</button>
                            <button onClick={() => scaleShape(contextMenu.targetId, 1.15)} style={contextMenuBtnStyle}>🔍+ Grow (×1.15)</button>
                            <button onClick={() => scaleShape(contextMenu.targetId, 1 / 1.15)} style={contextMenuBtnStyle}>🔍− Shrink (÷1.15)</button>
                        </>
                    )}
                    {contextMenu.targetType === 'image' && (
                        <>
                            <hr style={{ border: 'none', borderTop: '1px solid #555', margin: '4px 0' }} />
                            <button onClick={() => rotateImage(contextMenu.targetId, -15)} style={contextMenuBtnStyle}>↺ Rotate Left 15°</button>
                            <button onClick={() => rotateImage(contextMenu.targetId, 15)} style={contextMenuBtnStyle}>↻ Rotate Right 15°</button>
                            <button onClick={() => scaleImage(contextMenu.targetId, 1.15)} style={contextMenuBtnStyle}>🔍+ Grow (×1.15)</button>
                            <button onClick={() => scaleImage(contextMenu.targetId, 1 / 1.15)} style={contextMenuBtnStyle}>🔍− Shrink (÷1.15)</button>
                        </>
                    )}
                    {contextMenu.targetType === 'seat' && (
                        <>
                            <hr style={{ border: 'none', borderTop: '1px solid #555', margin: '4px 0' }} />
                            <button onClick={() => scaleSeat(contextMenu.targetId, 1.15)} style={contextMenuBtnStyle}>🔍+ Grow (×1.15)</button>
                            <button onClick={() => scaleSeat(contextMenu.targetId, 1 / 1.15)} style={contextMenuBtnStyle}>🔍− Shrink (÷1.15)</button>
                        </>
                    )}
                    {contextMenu.targetType === 'shape' && (() => {
                        const shape = shapes.find(s => s.id === contextMenu.targetId);
                        // Show Ungroup option for group shapes
                        if (shape?.type === 'group') {
                            return (
                                <button onClick={() => ungroupShape(contextMenu.targetId)} style={contextMenuBtnStyle}>📦 Ungroup</button>
                            );
                        }
                        // Allow Add Point for non-group shapes (already checked above)
                        return shape ? (
                            <button onClick={() => {
                                const stage = stageRef.current;
                                if (!stage) return;
                                const rect = stageContainerRef.current?.getBoundingClientRect();
                                if (!rect) return;
                                // Convert screen coordinates back to stage coordinates
                                const stagePos = stage.getRelativePointerPosition();
                                if (stagePos) {
                                    addPointToShape(contextMenu.targetId, stagePos.x, stagePos.y);
                                }
                            }} style={contextMenuBtnStyle}>➕ Add Point</button>
                        ) : null;
                    })()}
                </div>
            )}

            {colorPickerOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setColorPickerOpen(false)}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Pick Color {selectedIds.length > 1 ? `(${selectedIds.length} shapes)` : ''}</h3>
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={(e) => setSelectedColor(e.target.value)}
                            style={{ width: '100%', height: '60px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <label style={{ display: 'block', marginTop: '14px', color: '#333', fontSize: '13px' }}>
                            Opacity: {Math.round(selectedOpacity * 100)}%
                            <input
                                type="range"
                                min={0} max={1} step={0.01}
                                value={selectedOpacity}
                                onChange={(e) => setSelectedOpacity(parseFloat(e.target.value))}
                                style={{ width: '100%', marginTop: '4px' }}
                            />
                        </label>
                        <div style={{ marginTop: '6px', height: '24px', borderRadius: '4px', background: selectedColor, opacity: selectedOpacity, border: '1px solid #ccc' }} />
                        <div style={{ marginTop: '14px', display: 'flex', gap: '10px' }}>
                            <button onClick={() => applyColor(selectedColor)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Apply</button>
                            <button onClick={() => setColorPickerOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {editPropsOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setEditPropsOpen(false)}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Edit Properties{(() => { const sum = shapes.reduce((acc, s) => acc + (typeof s.seat_block?.capacity === 'number' ? s.seat_block.capacity : 0), 0); const total = typeof seatMap.capacity === 'number' && seatMap.capacity > 0 ? seatMap.capacity : 0; if (sum === 0 && total === 0) return ''; return ` (${sum}/${total > 0 ? total : '—'})`; })()}</h3>
                        {/* Name and Description removed from properties form */}

                        {/* Seat-block toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <input type="checkbox" checked={!!editingProps.is_seat_block} onChange={(e) => setEditingProps({ ...editingProps, is_seat_block: e.target.checked })} />
                            <span style={{ color: '#333' }}>Is seat block (section)</span>
                        </label>

                        {/* Fade-in block details */}
                        <div style={{ transition: 'all 220ms ease', opacity: editingProps.is_seat_block ? 1 : 0, maxHeight: editingProps.is_seat_block ? '800px' : '0px', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Section ID</div>
                                    <input type="text" value={editingProps.block?.id} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), id: e.target.value } })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Name</div>
                                    <input type="text" value={editingProps.block?.name || ''} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), name: e.target.value } })} placeholder="e.g. VIP Block 1" style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Gate</div>
                                    <input type="text" value={editingProps.block?.gate} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), gate: e.target.value } })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Zone</div>
                                    <input type="text" value={editingProps.block?.zone} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), zone: e.target.value } })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Level</div>
                                    <input type="text" value={editingProps.block?.level} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), level: e.target.value } })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                    <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Capacity</div>
                                    <input
                                        type="number"
                                        min={0}
                                        placeholder="0"
                                        value={typeof editingProps.block?.capacity === 'number' && editingProps.block.capacity > 0 ? editingProps.block.capacity : (editingProps.block?.capacity === '' ? '' : '')}
                                        onChange={(e) => {
                                            const raw = e.target.value || '';
                                            const digits = raw.replace(/\D/g, '');
                                            const val = digits === '' ? '' : Math.max(0, parseInt(digits, 10));
                                            setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), capacity: val } });
                                        }}
                                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </label>
                                <label style={{ display: 'block' }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Category</div>
                                    <input type="text" value={editingProps.block?.category} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), category: e.target.value } })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '5px' }}>
                                    <span style={{ color: '#333', marginRight: '8px' }}>Is seat specific</span>
                                    <input type="checkbox" checked={!!editingProps.block?.is_seat_specific} onChange={(e) => setEditingProps({ ...editingProps, block: { ...(editingProps.block||{}), is_seat_specific: e.target.checked } })} />
                                </label>
                            </div>
                            
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                Section rows and seat lists can be managed in data mode or imported; this dialog stores the high-level metadata.
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={applyProps} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Apply</button>
                            <button onClick={() => setEditPropsOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {addRowOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={(e) => { e.stopPropagation(); }}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Add Row{(() => { const sum = existingRows.reduce((acc, r) => acc + r.seatsCount, 0); const cap = shapes.find(s => s.id === addRowForShapeId)?.seat_block?.capacity; const total = typeof cap === 'number' && cap > 0 ? cap : 0; if (sum === 0 && total === 0) return ''; return ` (${sum}/${total > 0 ? total : '—'})`; })()}</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {existingRows.length === 0 && <RowFormFields row={currentRow} onChange={setCurrentRow} />}
                            {existingRows.length > 0 && (
                                <div style={{ maxHeight: '240px', overflowY: 'auto', borderRadius: '6px' }}>
                                    {existingRows.map((r, idx) => {
                                        const expanded = editingIndex === idx;
                                        const isLast = idx === existingRows.length - 1;
                                        return (
                                            <div key={`row-${idx}`} style={{ borderBottom: !isLast ? '1px solid #000' : 'none', background: expanded ? '#fff' : '#fafafa' }}>
                                                <div onClick={() => {
                                                    if (expanded) {
                                                        setEditingIndex(null);
                                                        setCurrentRow({ id: '', name: '', seatsCount: 0 });
                                                    } else {
                                                        setEditingIndex(idx);
                                                        setCurrentRow({ id: r.id, name: r.name, seatsCount: r.seatsCount });
                                                    }
                                                }} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px', cursor: 'pointer' }}>
                                                    <div style={{ flex: 1, fontSize: '13px', color: '#222' }}>{`${r.id || '(empty)'} | ${r.name || '-'} | ${r.seatsCount}`}</div>
                                                    <div style={{ width: '24px', textAlign: 'center', color: '#999' }}>{expanded ? '▾' : '▸'}</div>
                                                </div>
                                                {expanded && <RowFormFields row={currentRow} onChange={setCurrentRow} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <button onClick={() => {
                                // If there are no existing rows yet, require a seatsCount to add the first populated row.
                                if (existingRows.length === 0 && (!currentRow.seatsCount || currentRow.seatsCount <= 0)) return;

                                const sectionId = (shapes.find(s => s.id === addRowForShapeId)?.seat_block?.id) || shapes.find(s => s.id === addRowForShapeId)?.id || 'section';

                                if (editingIndex !== null) {
                                    // update existing row being edited and prepare next prefilled id
                                    setExistingRows(prev => {
                                        const copy = [...prev];
                                        copy[editingIndex] = { id: currentRow.id, name: currentRow.name, seatsCount: currentRow.seatsCount };
                                        const ids = copy.map(r => r.id);
                                        let nextIndex = 1;
                                        while (ids.includes(`${sectionId}-r-${nextIndex}`)) nextIndex++;
                                        setCurrentRow({ id: `${sectionId}-r-${nextIndex}`, name: '', seatsCount: 0 });
                                        return copy;
                                    });
                                    setEditingIndex(null);
                                } else {
                                    // Use functional update so we can compute new index from fresh array
                                    setExistingRows(prev => {
                                        // First, remove trailing placeholders (seatsCount === 0)
                                        const cleaned = [...prev];
                                        while (cleaned.length > 0 && (cleaned[cleaned.length - 1].seatsCount === 0 || !(cleaned[cleaned.length - 1].id || '').trim())) {
                                            cleaned.pop();
                                        }

                                        if (cleaned.length === 0) {
                                            // First row: add the populated row and immediately append a prefilled placeholder
                                            const ids = [ ...(cleaned.map(r => r.id)), currentRow.id ];
                                            let nextIndex = 1;
                                            while (ids.includes(`${sectionId}-r-${nextIndex}`)) nextIndex++;
                                            const placeholder = { id: `${sectionId}-r-${nextIndex}`, name: '', seatsCount: 0 };
                                            const newArr = [...cleaned, { id: currentRow.id, name: currentRow.name, seatsCount: currentRow.seatsCount }, placeholder];
                                            // Open the placeholder for editing
                                            setEditingIndex(newArr.length - 1);
                                            setCurrentRow(placeholder);
                                            return newArr;
                                        } else {
                                            // If last cleaned row has seatsCount 0, do not add — placeholders removed above
                                            const last = cleaned[cleaned.length - 1];
                                            if (last.seatsCount === 0) {
                                                // nothing to add; return cleaned (placeholders removed)
                                                setEditingIndex(null);
                                                setCurrentRow({ id: '', name: '', seatsCount: 0 });
                                                return cleaned;
                                            }

                                            // Append a placeholder with a computed next id for user to fill
                                            const ids = cleaned.map(r => r.id);
                                            let nextIndex = 1;
                                            while (ids.includes(`${sectionId}-r-${nextIndex}`)) nextIndex++;
                                            const placeholder = { id: `${sectionId}-r-${nextIndex}`, name: '', seatsCount: 0 };
                                            const newIdx = cleaned.length;
                                            const newArr = [...cleaned, placeholder];
                                            setEditingIndex(newIdx);
                                            setCurrentRow(placeholder);
                                            return newArr;
                                        }
                                    });
                                }
                            }} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer' }}>+ Add</button>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                            <button onClick={() => {
                                // Save: include currentRow if filled and not currently editing
                                const toSaveExisting = [...existingRows];
                                if (editingIndex !== null) {
                                    // apply currentRow to existingRows
                                    toSaveExisting[editingIndex] = { id: currentRow.id, name: currentRow.name, seatsCount: currentRow.seatsCount };
                                } else if (currentRow.id && currentRow.id.trim() !== '' && currentRow.seatsCount && currentRow.seatsCount > 0) {
                                    toSaveExisting.push({ id: currentRow.id, name: currentRow.name, seatsCount: currentRow.seatsCount });
                                }
                                // now persist only rows with seatsCount > 0 and call saveRows with that array
                                const rowsToPersist = toSaveExisting.filter(r => r.seatsCount > 0 && r.id && r.id.trim() !== '');
                                saveRows(rowsToPersist, false);
                            }} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Save</button>
                            <button onClick={() => { setAddRowOpen(false); setExistingRows([]); setCurrentRow({ id: '', name: '', seatsCount: 0 }); setEditingIndex(null); }} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {anchorPopupOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setAnchorPopupOpen(false)}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Anchor Point</h3>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Description</div>
                            <input type="text" value={anchorForm.description} onChange={(e) => setAnchorForm({ ...anchorForm, description: e.target.value })} placeholder="e.g. Stage center" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                            <label style={{ display: 'block' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>X</div>
                                <input type="number" step="any" value={anchorForm.x} onChange={(e) => setAnchorForm({ ...anchorForm, x: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Y</div>
                                <input type="number" step="any" value={anchorForm.y} onChange={(e) => setAnchorForm({ ...anchorForm, y: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                            <button onClick={() => { const px = parseFloat(anchorForm.x); const py = parseFloat(anchorForm.y); setAnchorPoint({ x: isNaN(px) ? anchorPoint.x : px, y: isNaN(py) ? anchorPoint.y : py, description: anchorForm.description }); setAnchorPopupOpen(false); }} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setAnchorPopupOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {venueDetailsOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setVenueDetailsOpen(false)}>
                    <div style={{ ...modalStyle, display: 'flex', flexDirection: 'column', maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0, flexShrink: 0 }}>Venue Details</h3>
                        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                            <label style={{ display: 'block', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Venue Name</div>
                                <input type="text" value={venueForm.name} onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })} placeholder="e.g. Main Arena" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Configuration</div>
                                <input type="text" value={venueForm.configuration} onChange={(e) => setVenueForm({ ...venueForm, configuration: e.target.value })} placeholder="e.g. concert" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Capacity</div>
                                <input type="number" min={0} value={venueForm.capacity === '' ? '' : venueForm.capacity} onChange={(e) => { const d = (e.target.value || '').replace(/\D/g, ''); setVenueForm({ ...venueForm, capacity: d === '' ? '' : Math.max(0, parseInt(d, 10)) }); }} placeholder="e.g. 5000" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Country</div>
                                <input type="text" value={venueForm.country} onChange={(e) => setVenueForm({ ...venueForm, country: e.target.value })} placeholder="e.g. Germany" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block', marginBottom: '10px' }}>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>City</div>
                                <input type="text" value={venueForm.city} onChange={(e) => setVenueForm({ ...venueForm, city: e.target.value })} placeholder="e.g. Berlin" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </label>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <label style={{ display: 'block', flex: 2 }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Street</div>
                                    <input type="text" value={venueForm.street} onChange={(e) => setVenueForm({ ...venueForm, street: e.target.value })} placeholder="e.g. Hauptstraße" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </label>
                                <label style={{ display: 'block', flex: 1 }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>House No.</div>
                                    <input type="text" value={venueForm.housenumber} onChange={(e) => setVenueForm({ ...venueForm, housenumber: e.target.value })} placeholder="e.g. 12" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </label>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <label style={{ display: 'block', flex: 1 }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>Postal Code</div>
                                    <input type="text" value={venueForm.postal_code} onChange={(e) => setVenueForm({ ...venueForm, postal_code: e.target.value })} placeholder="e.g. 10115" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </label>
                                <label style={{ display: 'block', flex: 1 }}>
                                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>State</div>
                                    <input type="text" value={venueForm.state} onChange={(e) => setVenueForm({ ...venueForm, state: e.target.value })} placeholder="e.g. Berlin" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                                </label>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexShrink: 0 }}>
                            <button onClick={applyVenueDetails} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Apply</button>
                            <button onClick={() => setVenueDetailsOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {contextMenu && <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 999 }} onClick={() => setContextMenu(null)} />}

            {/* Selection menu (appears after marquee selection with multiple shapes) */}
            {selectionMenu && selectedIds.length > 1 && (
                <>
                    <div data-no-deselect style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9998 }} onClick={() => setSelectionMenu(null)} />
                    <div data-no-deselect style={{
                        position: 'fixed',
                        top: `${selectionMenu.y}px`,
                        left: `${selectionMenu.x}px`,
                        background: '#333',
                        border: '1px solid #555',
                        borderRadius: '8px',
                        padding: '10px',
                        zIndex: 9999,
                        minWidth: '160px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>{selectedIds.length} shapes selected</div>
                        <button onClick={() => { groupSelectedShapes(); }} style={{ ...popupBtnStyle, width: '100%', marginBottom: '5px' }}>📦 Group</button>
                        <hr style={{ border: 'none', borderTop: '1px solid #555', margin: '8px 0' }} />
                        <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                            <button onClick={() => { setZoomScale(z => Math.min(3, z * 1.2)); setSelectionMenu(null); }} style={{ ...popupBtnStyle, flex: 1 }}>🔍+ Zoom In</button>
                            <button onClick={() => { setZoomScale(z => Math.max(0.2, z / 1.2)); setSelectionMenu(null); }} style={{ ...popupBtnStyle, flex: 1 }}>🔍- Zoom Out</button>
                        </div>
                        <button onClick={() => { setSelectionMenu(null); setSelectedIds([]); setSelectedId(null); setSelectedType(null); }} style={{ ...popupBtnStyle, width: '100%', background: '#555' }}>✕ Cancel</button>
                    </div>
                </>
            )}

            {/* ── Auth gate (login required to save / import) ── */}
            {authGateOpen && (
                <div
                    data-no-deselect
                    style={{ ...modalOverlayStyle, zIndex: 2500 }}
                    onClick={() => { setAuthGateOpen(false); pendingAuthActionRef.current = null; }}
                >
                    <div style={{ width: '100%', maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
                        <LoginCard
                            title="Anmelden zum Speichern"
                            subtitle="Melde dich an, um deinen Saalplan zu speichern oder einen bestehenden zu importieren."
                            onClose={() => { setAuthGateOpen(false); pendingAuthActionRef.current = null; }}
                            onSuccess={() => {
                                setAuthGateOpen(false);
                                const action = pendingAuthActionRef.current;
                                pendingAuthActionRef.current = null;
                                // Defer so the resumed action sees the stored session.
                                setTimeout(() => action?.(), 0);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ── Import Venue Popup ── */}
            {importPopupOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => { setImportPopupOpen(false); setImportDropdownOpen(false); setImportSearchQuery(''); setImportSuggestions([]); }}>
                    <div style={{ ...modalStyle, maxWidth: '500px' }} onClick={(e) => { e.stopPropagation(); if (importDropdownOpen) setImportDropdownOpen(false); }}>
                        <h3 style={{ marginTop: 0, marginBottom: '4px' }}>Import Venue</h3>
                        <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#888', lineHeight: 1.4 }}>
                            🔍 Find your venue by searching its name or location below
                        </p>

                        {/* Search input with live suggestions */}
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <input
                                type="text"
                                value={importSearchQuery}
                                onChange={(e) => handleImportSearchChange(e.target.value)}
                                placeholder="Search name, location..."
                                autoFocus
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '14px' }}
                            />
                            {importLoading && (
                                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#999' }}>searching...</span>
                            )}

                            {/* Suggestions dropdown */}
                            {importDropdownOpen && (
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto',
                                        background: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px',
                                        zIndex: 10, boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                                    }}
                                >
                                    {importSuggestions.length > 0 ? importSuggestions.map((v) => {
                                        const loc = formatLocation(v.location);
                                        return (
                                            <div
                                                key={v.id}
                                                onClick={() => selectImportVenue(v.id)}
                                                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f7ff')}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                                            >
                                                <div style={{ fontSize: '13px', color: '#222', fontWeight: 500 }}>
                                                    {v.venue_name}
                                                    {v.configuration ? <span style={{ color: '#666', fontWeight: 400 }}> | {v.configuration}</span> : null}
                                                    {typeof v.capacity === 'number' && v.capacity > 0 ? <span style={{ color: '#999', fontWeight: 400 }}>, {v.capacity} cap.</span> : null}
                                                </div>
                                                {loc && (
                                                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                                                        {loc}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }) : (
                                        <div style={{ padding: '14px', color: '#999', fontSize: '13px', textAlign: 'center' }}>
                                            No venues found matching &ldquo;{importSearchQuery}&rdquo;
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setImportPopupOpen(false); setImportSearchQuery(''); setImportSuggestions([]); setImportDropdownOpen(false); }} style={{ padding: '8px 20px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Validation error popup ── */}
            {saveValidationError && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setSaveValidationError(null)}>
                    <div style={{ ...modalStyle, maxWidth: '380px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚠️</div>
                        <h3 style={{ marginTop: 0, color: '#e74c3c' }}>Missing Required Fields</h3>
                        <p style={{ color: '#555', lineHeight: 1.5 }}>{saveValidationError}</p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                            <button onClick={() => { setSaveValidationError(null); openVenueDetails(); }} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#3498db', color: 'white', border: 'none', cursor: 'pointer' }}>Fill Details</button>
                            <button onClick={() => setSaveValidationError(null)} style={{ flex: 1, padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Save Preview Popup ── */}
            {savePreviewOpen && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setSavePreviewOpen(false)}>
                    <div style={{ ...modalStyle, maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0, marginBottom: '12px', flexShrink: 0 }}>Save Venue</h3>

                        {/* Scrollable content area */}
                        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginBottom: '14px' }}>

                            {/* SVG Preview - pannable & zoomable */}
                            <div
                                ref={previewContainerRef}
                                style={{ background: '#ffffff', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden', cursor: previewDragRef.current.dragging ? 'grabbing' : 'grab', height: '250px', position: 'relative', userSelect: 'none' }}
                                onMouseDown={(e) => {
                                    if (e.button !== 0) return;
                                    previewDragRef.current = { dragging: true, lastX: e.clientX, lastY: e.clientY };
                                    e.preventDefault();
                                }}
                                onMouseMove={(e) => {
                                    if (!previewDragRef.current.dragging) return;
                                    const dx = e.clientX - previewDragRef.current.lastX;
                                    const dy = e.clientY - previewDragRef.current.lastY;
                                    previewDragRef.current.lastX = e.clientX;
                                    previewDragRef.current.lastY = e.clientY;
                                    setPreviewPan(prev => ({ x: prev.x + dx, y: prev.y + dy }));
                                }}
                                onMouseUp={() => { previewDragRef.current.dragging = false; }}
                                onMouseLeave={() => { previewDragRef.current.dragging = false; }}
                            >
                                <div
                                    dangerouslySetInnerHTML={{ __html: savePreviewSvg }}
                                    style={{
                                        width: '100%', height: '100%',
                                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                                        transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                                        transformOrigin: 'center center',
                                    }}
                                />
                            </div>

                            {/* Editable venue fields */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {/* Venue Name */}
                                <div>
                                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Venue Name <span style={{ color: '#e74c3c' }}>*</span></div>
                                    <input
                                        type="text"
                                        value={seatMap.venue_name}
                                        onChange={(e) => setSeatMap(prev => ({ ...prev, venue_name: e.target.value }))}
                                        placeholder="e.g. Madison Square Garden"
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                    />
                                </div>

                                {/* Capacity + Configuration on same row */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Capacity</div>
                                        <input
                                            type="number"
                                            min={0}
                                            value={seatMap.capacity === '' ? '' : seatMap.capacity}
                                            onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setSeatMap(prev => ({ ...prev, capacity: v === '' ? '' : parseInt(v, 10) })); }}
                                            placeholder="0"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Configuration <span style={{ color: '#e74c3c' }}>*</span></div>
                                        <input
                                            type="text"
                                            value={seatMap.configuration}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, configuration: e.target.value }))}
                                            placeholder="e.g. Concert, Theater"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                </div>

                                {/* Location heading */}
                                <div style={{ fontSize: '12px', color: '#666', fontWeight: 600, marginTop: '4px' }}>Location</div>

                                {/* Street + House number on same row */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 2 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Street</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.street || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], street: e.target.value }] }))}
                                            placeholder="Street name"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>House Nr.</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.housenumber || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], housenumber: e.target.value }] }))}
                                            placeholder="Nr."
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                </div>

                                {/* Postal code + City on same row */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Postal Code</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.postal_code || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], postal_code: e.target.value }] }))}
                                            placeholder="Postal code"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>City</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.city || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], city: e.target.value }] }))}
                                            placeholder="City"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                </div>

                                {/* State + Country on same row */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>State</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.state || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], state: e.target.value }] }))}
                                            placeholder="State"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Country</div>
                                        <input
                                            type="text"
                                            value={seatMap.location[0]?.country || ''}
                                            onChange={(e) => setSeatMap(prev => ({ ...prev, location: [{ ...prev.location[0], country: e.target.value }] }))}
                                            placeholder="Country"
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </div>
                                </div>

                                {/* Blocks (sections) with dropdown rows */}
                                {seatMap.sections.length > 0 && (
                                    <div style={{ marginTop: '6px' }}>
                                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', fontWeight: 600 }}>Blocks ({seatMap.sections.length})</div>
                                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                                            {seatMap.sections.map((sec, idx) => {
                                                const isExpanded = !!expandedBlocks[sec.id || `sec-${idx}`];
                                                const secKey = sec.id || `sec-${idx}`;
                                                return (
                                                    <div key={secKey} style={{ borderBottom: idx < seatMap.sections.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                                        <div
                                                            onClick={() => setExpandedBlocks(prev => ({ ...prev, [secKey]: !isExpanded }))}
                                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', cursor: 'pointer', background: isExpanded ? '#f7fbff' : '#fff', fontSize: '13px' }}
                                                        >
                                                            <span style={{ color: '#333' }}>
                                                                <strong>{sec.name || sec.id || `Block ${idx + 1}`}</strong>
                                                                {sec.gate ? <span style={{ color: '#999', marginLeft: '6px' }}>Gate: {sec.gate}</span> : null}
                                                                {typeof sec.capacity === 'number' && sec.capacity > 0 ? <span style={{ color: '#999', marginLeft: '6px' }}>Cap: {sec.capacity}</span> : null}
                                                            </span>
                                                            <span style={{ color: '#999', fontSize: '11px' }}>{sec.section_rows.length} row{sec.section_rows.length !== 1 ? 's' : ''} {isExpanded ? '▾' : '▸'}</span>
                                                        </div>
                                                        {isExpanded && sec.section_rows.length > 0 && (
                                                            <div style={{ padding: '4px 10px 8px 20px', background: '#fafcff' }}>
                                                                {sec.section_rows.map((row, ri) => (
                                                                    <div key={row.id || ri} style={{ fontSize: '12px', color: '#555', padding: '3px 0', borderBottom: ri < sec.section_rows.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                                                        <span style={{ fontWeight: 500 }}>{row.name || row.id || `Row ${ri + 1}`}</span>
                                                                        <span style={{ color: '#999', marginLeft: '8px' }}>{row.row_seats.length} seat{row.row_seats.length !== 1 ? 's' : ''}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {isExpanded && sec.section_rows.length === 0 && (
                                                            <div style={{ padding: '6px 10px 8px 20px', fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>No rows</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Static action buttons (always visible at bottom) */}
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', flexShrink: 0, borderTop: '1px solid #eee', paddingTop: '12px' }}>
                            <button
                                onClick={() => {
                                    if (!seatMap.venue_name.trim() || !seatMap.configuration.trim()) {
                                        alert('Venue Name and Configuration are required.');
                                        return;
                                    }
                                    handleSaveNew();
                                }}
                                style={{ flex: 1, minWidth: '100px', padding: '10px', borderRadius: '4px', background: '#27ae60', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                            >Save as New</button>
                            {importedFromId && (
                                <button
                                    onClick={() => {
                                        if (!seatMap.venue_name.trim() || !seatMap.configuration.trim()) {
                                            alert('Venue Name and Configuration are required.');
                                            return;
                                        }
                                        handleUpdateExisting();
                                    }}
                                    style={{ flex: 1, minWidth: '100px', padding: '10px', borderRadius: '4px', background: '#e67e22', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                >Update Existing</button>
                            )}
                            <button onClick={() => setSavePreviewOpen(false)} style={{ flex: 1, minWidth: '100px', padding: '10px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Venue Interior Preview Modal ── */}
            {venuePreviewModalOpen && venuePreviewUrl && (
                <div data-no-deselect style={modalOverlayStyle} onClick={() => setVenuePreviewModalOpen(false)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            ...modalStyle,
                            maxWidth: 700,
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 16 }}>Venue Interior Preview</h3>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                    onClick={() => venuePreviewInputRef.current?.click()}
                                    title="Change preview"
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 4,
                                        border: 'none',
                                        background: '#3498db',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}
                                >🔄 Change</button>
                                <button
                                    onClick={() => {
                                        setVenuePreviewFile(null);
                                        setVenuePreviewUrl('');
                                        setVenuePreviewModalOpen(false);
                                    }}
                                    title="Remove preview"
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 4,
                                        border: 'none',
                                        background: '#e74c3c',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}
                                >🗑️ Remove</button>
                                <button
                                    onClick={() => setVenuePreviewModalOpen(false)}
                                    title="Close"
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 4,
                                        border: 'none',
                                        background: '#555',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 12,
                                    }}
                                >✕</button>
                            </div>
                        </div>

                        {/* Media display */}
                        <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#111', borderRadius: 8, minHeight: 300 }}>
                            {(venuePreviewFile?.type.startsWith('video/') || venuePreviewUrl.match(/\.(mp4|webm)$/i)) ? (
                                <video
                                    src={venuePreviewUrl}
                                    controls
                                    style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 6 }}
                                />
                            ) : (
                                <img
                                    src={venuePreviewUrl}
                                    alt="Venue interior preview"
                                    style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 6, objectFit: 'contain' }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STYLES ---
const buttonStyle = (active: boolean) => ({
    padding: '8px 12px',
    cursor: 'pointer',
    background: active ? '#3498db' : '#555',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px'
});

const contextMenuBtnStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '10px 15px',
    background: 'transparent',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px',
    transition: 'background 0.2s',
};

const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000,
    padding: '20px'
};

const modalStyle: React.CSSProperties = {
    background: '#fff', padding: '20px', borderRadius: '12px', color: '#333',
    width: '100%', maxWidth: '400px', boxSizing: 'border-box'
};

const popupBtnStyle = { padding: '12px', cursor: 'pointer', borderRadius: '5px', border: '1px solid #ddd', background: '#f8f8f8' };

const zoomBtnStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: '1px solid #555',
    background: 'rgba(30,30,30,0.85)',
    color: 'white',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
};

export default SeatMapModel;